import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  UsersIcon,
  InformationCircleIcon,
  XMarkIcon,
  Cog6ToothIcon,
  TrashIcon,
  EyeIcon,
  FolderIcon,
  ArchiveBoxIcon,
  RocketLaunchIcon,
  PaperClipIcon,
  ArrowPathIcon,
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import {
  WorkflowContainer,
  WorkflowContainerMember,
  ActivityDefinition,
  ActivityReport,
  Workflow,
  User,
  UserRole,
  ProjectCollection,
  GoogleDriveAttachment,
  ProjectFormElement,
} from "../types";
import { useAuth } from "../contexts/AuthContext";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import { WorkflowStartModal } from "../components/workflow/WorkflowStartModal";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { TaskDetailModal } from "../components/reports/TaskDetailModal";
import { ActivityTableConfigurator } from "../components/reports/ActivityTableConfigurator";
import { ProjectFilesModal } from "../components/projects/ProjectFilesModal";
import AssignTaskModal from "../components/workflow/AssignTaskModal";
import { formatProjectDisplayName } from "../utils/projectCode";
import { isProjectManagerAllowedForTemplate } from "../utils/pmWorkflowAccess";
import { exportRowsToExcel } from "../utils/excel";
import {
  ActivityTableColumnPreference,
  ActivityTableScreenKey,
  collectFormColumnPreferences,
  dedupeActivityTableColumns,
  getFormColumnValue,
  normalizeActivityTableAsciiLabel,
  normalizeActivityTableLabel,
  parseFormFieldPairs,
  sanitizeActivityTableLabel,
} from "../utils/activityTableColumns";
import {
  calculateProjectFinanceSummary,
  getProjectTaxSettings,
  resolveProjectFinanceInputs,
} from "../utils/projectFinance";
import {
  allocateOvertimeCosts,
  getOvertimeSettings,
  OvertimeCostSegment,
} from "../utils/overtimeCost";
import { useVirtualizedRows } from "./manager-dashboard/hooks/useVirtualizedRows";
import {
  DailyExchangeRatesByDate,
  ExchangeRatesByBase,
  convertCurrencyAmountOnDate,
  extractExchangeRates,
  formatCurrencyByCode,
  getCurrencySuffixLabel,
  normalizeCurrencyCode,
} from "../utils/currency";
import { OverflowInfoCell } from "./manager-dashboard/OverflowInfoCell";

const NAV_REFRESH_EVENT = "nav:refresh";
const LEAVE_TYPE_FIELD_LABEL = "İzin Türü";
const WORKFLOW_DETAIL_ACTIVITY_TABLE_SCREEN_KEY: ActivityTableScreenKey =
  "workflow_instance_detail_activities";

const DEFAULT_PROJECT_DETAIL_BACK_ROUTE = "/project-instances";

type WorkflowInstanceDetailLocationState = {
  backTo?: string;
  backState?: unknown;
};

const parseHistoricalDateCandidate = (value: unknown): Date | null => {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
};

const toIsoDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toFiniteNumberOrFallback = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
};

const toNullableFiniteNumberOrFallback = (
  value: unknown,
  fallback: number | null,
): number | null => {
  if (value === null) {
    return null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return fallback;
};

const toBooleanOrFallback = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    if (value === "true") return true;
    if (value === "false") return false;
  }
  return fallback;
};

type ProjectCostSummaryValue = {
  plannedHours: number;
  spentHours: number;
  realizedCost: number;
  averageHourlyRate: number | null;
  remainingEstimatedCost: number | null;
  totalEstimatedCost: number | null;
};

type ProjectFinanceSummaryValue = ReturnType<typeof calculateProjectFinanceSummary>;

type CompletedActivityCostSummaryValue = {
  totalHours: number;
  totalCost: number;
};

type CompletedActivityInvoiceSummaryValue = {
  invoiced: CompletedActivityCostSummaryValue;
  notInvoiced: CompletedActivityCostSummaryValue;
  total: CompletedActivityCostSummaryValue;
};

type ArchivedProjectFinancialSnapshot = {
  snapshotTakenAt?: string;
  projectCostSummary?: Partial<ProjectCostSummaryValue> | null;
  projectFinanceSummary?: Partial<ProjectFinanceSummaryValue> | null;
  completedActivityInvoiceSummary?:
    | {
        invoiced?: Partial<CompletedActivityCostSummaryValue> | null;
        notInvoiced?: Partial<CompletedActivityCostSummaryValue> | null;
        total?: Partial<CompletedActivityCostSummaryValue> | null;
      }
    | null;
};

interface WorkflowInstance {
  id: number;
  name: string; // Workflow Template Name
  workflowId: number | null;
  workflowName: string | null;
  templateWorkflowId?: number | null;
  workflowContainerId: number;
  workflowContainerName: string;
  workflowContainerFormData?: Record<string, any>;
  projectMembers?: number[];
  projectCode?: string;
  totalSpentHours?: number;
  status: string;
  startedAt: string;
  completedAt: string | null;
  startedBy: number;
  starterName: string;
  createdAt?: string;
  archivedFinancialSnapshot?: ArchivedProjectFinancialSnapshot | null;
}

interface ActivityInstance {
  id: number;
  templateActivityId?: number | null;
  isLeave?: boolean;
  requiresApproval?: boolean;
  workflowInstanceId: number;
  activityTitle: string;
  name?: string;
  projectName?: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  assignedUsers: string;
  completedUserIds?: string | null;
  completedAt: string | null;
  createdAt?: string | null;
  estimatedDuration: number | null;
  actualDuration: number | null;
  isAdHoc?: boolean;
  isInvoiced?: boolean;
  userDurations?: string;
  submitDescription?: string;
  attachments?: string;
  userFormData?: string;
  formContentSummary?: string;
  formContentTooltip?: string;
  forms?: Array<{
    id?: number | string;
    title?: string;
    description?: string;
    formStructure?: any;
    submissionData?: any;
    userResponse?: any;
    isRequired?: boolean;
    displayOrder?: number;
  }>;
}

interface UserHourlyCostRateRow {
  id: number;
  userId: number;
  startDate: string;
  endDate?: string | null;
  hourlyCost: number;
  currency?: string | null;
}

interface CompanyHourlyCostRateRow {
  id: number;
  startDate: string;
  endDate?: string | null;
  hourlyCost: number;
  currency?: string | null;
}

type CollectionFormState = {
  collectionDate: string;
  amount: string;
  currency: string;
  description: string;
  attachments: GoogleDriveAttachment[];
  files: QueuedUploadFile[];
};

type FinanceShortcutMode = "invoice" | "offer";

type QueuedUploadFile = {
  id: string;
  file: File;
  description: string;
};

type FinanceShortcutFormState = {
  amount: string;
  currency: string;
  invoiceEnabled: boolean;
  files: QueuedUploadFile[];
};

type ExistingFinanceShortcutFilesState = {
  offer: GoogleDriveAttachment[];
  invoice: GoogleDriveAttachment[];
};

type CompletedActivityRowFilters = {
  date: string;
  assignee: string;
  startTime: string;
  endTime: string;
  minDurationHours: string;
  minCost: string;
  projectName: string;
  activityTitle: string;
  formContent: string;
  description: string;
  managerComment: string;
};

type CompletedActivitySortField = string;
type CompletedActivityInvoiceFilter = "all" | "invoiced" | "not_invoiced";

const INITIAL_COMPLETED_ACTIVITY_ROW_FILTERS: CompletedActivityRowFilters = {
  date: "",
  assignee: "",
  startTime: "",
  endTime: "",
  minDurationHours: "",
  minCost: "",
  projectName: "",
  activityTitle: "",
  formContent: "",
  description: "",
  managerComment: "",
};

const INVOICE_SHORTCUT_YES = "yes";
const INVOICE_SHORTCUT_NO = "no";
const INVOICE_SHORTCUT_SUBFOLDER = "Faturalar";
const INVOICE_SHORTCUT_CATEGORY = "invoice";
const OFFER_SHORTCUT_SUBFOLDER = "İş Tutarı - Teklifler";
const OFFER_SHORTCUT_CATEGORY = "offer_amount";
const FINANCE_SHORTCUT_EMPTY_FORM: FinanceShortcutFormState = {
  amount: "",
  currency: "TRY",
  invoiceEnabled: false,
  files: [],
};
const PROJECT_CURRENCY_OPTIONS = [
  { code: "TRY", symbol: "\u20BA" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "\u20AC" },
  { code: "GBP", symbol: "\u00A3" },
  { code: "AZN", symbol: "\u20BC" },
  { code: "CHF", symbol: "CHF" },
  { code: "JPY", symbol: "\u00A5" },
  { code: "CNY", symbol: "\u00A5" },
  { code: "RUB", symbol: "\u20BD" },
  { code: "SAR", symbol: "SAR" },
  { code: "AED", symbol: "AED" },
] as const;
const PROJECT_CURRENCY_SYMBOLS = PROJECT_CURRENCY_OPTIONS.reduce<
  Record<string, string>
>((acc, option) => {
  acc[option.code] = option.symbol;
  return acc;
}, {});

const parseShortcutDecimalInputValue = (rawValue: string): number | null => {
  if (rawValue.trim() === "") {
    return null;
  }

  const normalized = rawValue.trim().replace(/\s+/g, "").replace(",", ".");
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const createQueuedUploadFileId = (file: File): string =>
  `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const buildQueuedUploadFiles = (
  files: FileList | File[] | null | undefined,
): QueuedUploadFile[] =>
  Array.from(files || []).map((file) => ({
    id: createQueuedUploadFileId(file),
    file,
    description: "",
  }));

const normalizeFileLookupText = (value: string): string =>
  value
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u0131/g, "i")
    .trim();

const isDriveFolderAttachment = (file: GoogleDriveAttachment): boolean =>
  file.mimeType === "application/vnd.google-apps.folder";

const extractFinanceElementCurrencies = (
  formData: Record<string, any> | null | undefined,
): Record<string, string> => {
  if (!formData || typeof formData !== "object" || Array.isArray(formData)) {
    return {};
  }

  const raw =
    formData.elementCurrencies && typeof formData.elementCurrencies === "object"
      ? formData.elementCurrencies
      : formData.element_currencies &&
          typeof formData.element_currencies === "object"
        ? formData.element_currencies
        : null;

  if (!raw || Array.isArray(raw)) {
    return {};
  }

  return Object.entries(raw as Record<string, unknown>).reduce<
    Record<string, string>
  >((acc, [elementId, currencyCode]) => {
    if (!elementId) {
      return acc;
    }
    acc[elementId] = normalizeCurrencyCode(currencyCode);
    return acc;
  }, {});
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value?: string | null) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatCurrency = (amount?: number, currency = "TRY") => {
  if (!amount) return "-";
  return formatCurrencyByCode(amount, currency, { display: "suffix" });
};

type WorkCalendarSettings = {
  holidaysByYear?: Record<string, string[]>;
  workdayOverridesByYear?: Record<string, string[]>;
  halfDaysByYear?: Record<string, string[]>;
};

const parseIsoDateOnly = (value?: string | null): Date | null => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
};

const getWorkCalendarDateState = (
  date: Date,
  workCalendar?: WorkCalendarSettings | null,
) => {
  const dateKey = toIsoDateKey(date);
  const yearKey = String(date.getFullYear());
  const holidays = new Set(workCalendar?.holidaysByYear?.[yearKey] || []);
  const workdayOverrides = new Set(
    workCalendar?.workdayOverridesByYear?.[yearKey] || [],
  );
  const halfDays = new Set(workCalendar?.halfDaysByYear?.[yearKey] || []);
  const weekend = date.getDay() === 0 || date.getDay() === 6;
  const isHalfDay = halfDays.has(dateKey);
  const isWorkdayOverride = workdayOverrides.has(dateKey);
  const isHoliday =
    !isHalfDay &&
    (holidays.has(dateKey) || (weekend && !isWorkdayOverride));

  return {
    isHalfDay,
    isHoliday,
  };
};

const calculateBusinessDays = (
  startDate?: string | null,
  endDate?: string | null,
  workCalendar?: WorkCalendarSettings | null,
): number => {
  const start = parseIsoDateOnly(startDate);
  const end = parseIsoDateOnly(endDate);
  if (!start || !end || start > end) return 0;

  let businessDays = 0;
  const currentDate = new Date(start);

  while (currentDate <= end) {
    const { isHalfDay, isHoliday } = getWorkCalendarDateState(
      currentDate,
      workCalendar,
    );

    if (isHalfDay) {
      businessDays += 0.5;
    } else if (!isHoliday) {
      businessDays++;
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return businessDays;
};

const formatBusinessDays = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) {
    return "0";
  }

  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
    maximumFractionDigits: 1,
  }).format(value);
};

const getUserInitials = (firstName?: string, lastName?: string) => {
  const first = firstName?.charAt(0).toUpperCase() ?? "";
  const last = lastName?.charAt(0).toUpperCase() ?? "";
  return `${first}${last}` || "?";
};

const getUserInitialsFromName = (name?: string) => {
  if (!name) return "?";
  const parts = name.split(" ");
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${
      parts.at(-1)?.charAt(0) ?? ""
    }`.toUpperCase();
  }
  return name.charAt(0).toUpperCase();
};

const mapMembersFromIds = (
  memberIds: number[],
  users: User[],
  workflowContainerId: number,
): WorkflowContainerMember[] => {
  const userMap = new Map<number, User>(users.map((u) => [u.id, u]));
  const now = new Date().toISOString();
  return memberIds
    .map((userId) => {
      const user = userMap.get(userId);
      if (!user) {
        return null;
      }
      return {
        id: userId,
        workflowContainerId,
        userId,
        role: "team_member",
        canStartWorkflow: false,
        addedAt: now,
        user: {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          username: user.username,
          email: user.email,
          role: user.role,
        },
      } as WorkflowContainerMember;
    })
    .filter((member): member is WorkflowContainerMember => member !== null);
};

export const WorkflowInstanceDetail: React.FC = () => {
  const { t } = useTranslation([
    "workflows",
    "common",
    "errors",
    "tasks",
    "projects",
    "activities",
    "dashboard",
  ]);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const backNavigationState = (
    location.state as WorkflowInstanceDetailLocationState | null
  )?.backState;
  const backTo =
    (location.state as WorkflowInstanceDetailLocationState | null)?.backTo ||
    DEFAULT_PROJECT_DETAIL_BACK_ROUTE;
  const navigateBack = useCallback(() => {
    if (backNavigationState !== undefined) {
      navigate(backTo, { state: backNavigationState });
      return;
    }
    navigate(backTo);
  }, [backNavigationState, backTo, navigate]);
  const {
    isManager,
    isAdmin,
    isAYCCompany,
    user,
    companySettings,
    applyCompanySettings,
    getWorkflowLabel,
    getSubmitDescriptionLabel,
    hasWorkflowTerminologyOverride,
  } = useAuth();
  const canViewFinancials = isAdmin() || isManager();
  const isVisitorUser = user?.role === UserRole.VISITOR;
  const canVisitorViewFinancialDetails = useMemo(() => {
    if (user?.role !== UserRole.VISITOR || !user.settingsJson) {
      return false;
    }
    try {
      const settings = JSON.parse(user.settingsJson);
      return Boolean(settings?.workflowPermissions?.visitorFinancialAccess);
    } catch {
      return false;
    }
  }, [user?.role, user?.settingsJson]);
  const canViewProjectFiles = !isVisitorUser || canVisitorViewFinancialDetails;
  const canViewCollectionDetails =
    canViewFinancials || canVisitorViewFinancialDetails;
  const canManageCollections = canViewFinancials && !isVisitorUser;
  const canViewCompletedActivityCosts = canViewFinancials && !isVisitorUser;
  const canViewActivityInvoiceStatus = canViewFinancials && !isVisitorUser;
  const overtimeSettings = useMemo(
    () => getOvertimeSettings(companySettings),
    [companySettings],
  );

  const workflowText = getWorkflowLabel();
  const submitDescriptionLabel = getSubmitDescriptionLabel("Ödeme Açıklaması");
  const includeManagerCommentColumn = isManager() || isAdmin();
  const isYapilanIsFormColumn = (
    column: ActivityTableColumnPreference,
  ): boolean => {
    if (column.kind !== "form") {
      return false;
    }

    const normalizedLabel =
      normalizeActivityTableAsciiLabel(column.label) ||
      (column.id.startsWith("form:")
        ? normalizeActivityTableAsciiLabel(column.id.slice(5))
        : "");

    return normalizedLabel === "yapilan is";
  };

  const getCompletedActivityFormColumnValue = (
    tooltip: string | null | undefined,
    column: ActivityTableColumnPreference,
    fallbackActivityTitle = "",
  ): string => {
    const formValue = getFormColumnValue(tooltip, column);
    if (formValue) {
      return formValue;
    }

    if (!isAYCCompany() || !isYapilanIsFormColumn(column)) {
      return "";
    }

    return (fallbackActivityTitle || "").trim();
  };

  const getCompletedActivityFormColumnReference = (
    columnId: string,
  ): ActivityTableColumnPreference => {
    const existingColumn = completedActivityVisibleColumns.find(
      (column) => column.id === columnId,
    );

    return (
      existingColumn || {
        id: columnId,
        kind: "form",
        label: columnId.startsWith("form:") ? columnId.slice(5) : "",
      }
    );
  };

  const useWorkflowOverride = hasWorkflowTerminologyOverride();
  const workflowTemplateText = useWorkflowOverride
    ? workflowText
    : t("workflows:workflowTemplate");

  const [workflowInstance, setWorkflowInstance] =
    useState<WorkflowInstance | null>(null);
  const [projectContainer, setProjectContainer] =
    useState<WorkflowContainer | null>(null);
  const [projectMembers, setProjectMembers] = useState<
    WorkflowContainerMember[]
  >([]);
  const [activityInstances, setActivityInstances] = useState<
    ActivityInstance[]
  >([]);
  const [completedActivityItemsData, setCompletedActivityItemsData] = useState<
    ActivityInstance[]
  >([]);
  const [activityDefinitions, setActivityDefinitions] = useState<
    ActivityDefinition[]
  >([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] =
    useState<ActivityReport | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [showAssignTaskModal, setShowAssignTaskModal] = useState(false);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [showChangeTypeModal, setShowChangeTypeModal] = useState(false);
  const [showTemplateChangeModal, setShowTemplateChangeModal] = useState(false);
  const [availableWorkflows, setAvailableWorkflows] = useState<Workflow[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(
    null,
  );
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [managerCommentActivity, setManagerCommentActivity] =
    useState<ActivityInstance | null>(null);
  const [managerCommentText, setManagerCommentText] = useState("");
  const [savingManagerComment, setSavingManagerComment] = useState(false);
  const [
    latestManagerCommentsByActivityId,
    setLatestManagerCommentsByActivityId,
  ] = useState<
    Record<
      string,
      {
        comment?: string | null;
        authorName?: string | null;
        createdAt?: string | null;
      }
    >
  >({});
  const [completedActivityRowFilters, setCompletedActivityRowFilters] =
    useState<CompletedActivityRowFilters>(
      INITIAL_COMPLETED_ACTIVITY_ROW_FILTERS,
    );
  const [completedActivityInvoiceFilter, setCompletedActivityInvoiceFilter] =
    useState<CompletedActivityInvoiceFilter>("all");
  const [
    completedActivityFormColumnFilters,
    setCompletedActivityFormColumnFilters,
  ] = useState<Record<string, string>>({});
  const [completedActivitySortField, setCompletedActivitySortField] =
    useState<CompletedActivitySortField>("endDate");
  const [completedActivitySortOrder, setCompletedActivitySortOrder] = useState<
    "asc" | "desc"
  >("desc");
  const [
    isCompletedTableConfiguratorOpen,
    setIsCompletedTableConfiguratorOpen,
  ] = useState(false);
  const [isSavingCompletedTableLayout, setIsSavingCompletedTableLayout] =
    useState(false);
  const [invoiceStatusUpdatingId, setInvoiceStatusUpdatingId] = useState<
    number | null
  >(null);
  const [completedDurationSummaryMode, setCompletedDurationSummaryMode] =
    useState<"total" | "average">("total");
  const [userHourlyCostRates, setUserHourlyCostRates] = useState<
    UserHourlyCostRateRow[]
  >([]);
  const [companyAverageHourlyCostRates, setCompanyAverageHourlyCostRates] =
    useState<CompanyHourlyCostRateRow[]>([]);
  const [isCostRatesLoading, setIsCostRatesLoading] = useState(false);
  const [exchangeRatesByBase, setExchangeRatesByBase] =
    useState<ExchangeRatesByBase>({});
  const [dailyExchangeRatesByDate, setDailyExchangeRatesByDate] =
    useState<DailyExchangeRatesByDate>({});
  const [isExchangeRatesLoading, setIsExchangeRatesLoading] = useState(false);
  const [projectCollections, setProjectCollections] = useState<ProjectCollection[]>(
    [],
  );
  const [isCollectionsLoading, setIsCollectionsLoading] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [isSavingCollection, setIsSavingCollection] = useState(false);
  const [deletingCollectionId, setDeletingCollectionId] = useState<number | null>(
    null,
  );
  const [financeShortcutMode, setFinanceShortcutMode] =
    useState<FinanceShortcutMode | null>(null);
  const [financeShortcutForm, setFinanceShortcutForm] =
    useState<FinanceShortcutFormState>(FINANCE_SHORTCUT_EMPTY_FORM);
  const [isSavingFinanceShortcut, setIsSavingFinanceShortcut] = useState(false);
  const [isFinanceShortcutDragOver, setIsFinanceShortcutDragOver] =
    useState(false);
  const [existingFinanceShortcutFiles, setExistingFinanceShortcutFiles] =
    useState<ExistingFinanceShortcutFilesState>({ offer: [], invoice: [] });
  const [isExistingFinanceShortcutFilesLoading, setIsExistingFinanceShortcutFilesLoading] =
    useState(false);
  const [previewingDriveFileId, setPreviewingDriveFileId] = useState<string | null>(
    null,
  );
  const [isCollectionDragOver, setIsCollectionDragOver] = useState(false);
  const [collectionForm, setCollectionForm] = useState<CollectionFormState>({
    collectionDate: "",
    amount: "",
    currency: "TRY",
    description: "",
    attachments: [],
    files: [],
  });

  const canManageProject =
    isManager() ||
    isAdmin() ||
    isProjectManagerAllowedForTemplate(
      user,
      workflowInstance?.templateWorkflowId,
    );
  const canAssignTasks = isAdmin() || isManager();
  const canArchiveProject = isAdmin() || isManager();
  const isArchivedProject = workflowInstance?.status === "ARCHIVED";
  const archivedFinancialSnapshot = useMemo(() => {
    const rawSnapshot = workflowInstance?.archivedFinancialSnapshot;
    return rawSnapshot &&
      typeof rawSnapshot === "object" &&
      !Array.isArray(rawSnapshot)
      ? (rawSnapshot as ArchivedProjectFinancialSnapshot)
      : null;
  }, [workflowInstance?.archivedFinancialSnapshot]);

  const parseDateTimeString = (value?: string): Date | null => {
    if (!value) return null;
    const normalized = value.includes("T") ? value : value.replace(" ", "T");
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDateOnlyTr = (value: Date | null): string => {
    if (!value) return "-";
    return value.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTimeOnlyTr = (value: Date | null): string => {
    if (!value) return "-";
    return value.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateForInput = (value: Date | null): string => {
    if (!value) return "";
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatTimeForInput = (value: Date | null): string => {
    if (!value) return "";
    const hours = String(value.getHours()).padStart(2, "0");
    const minutes = String(value.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const normalizeFilterText = (value: unknown): string =>
    String(value ?? "").toLocaleLowerCase("tr-TR");

  const isLeaveActivityByDefinitions = (
    activity: ActivityInstance,
    definitions: ActivityDefinition[],
  ): boolean => {
    if (activity.isLeave === true) {
      return true;
    }

    const id = activity.templateActivityId;
    if (typeof id === "number") {
      const def = definitions.find((a) => a.id === id);
      return def?.isLeave === true;
    }

    // Fallback: match by title for legacy/missing templateActivityId.
    const title = (activity.activityTitle || activity.name || "").trim();
    if (!title) return false;
    const defByTitle = definitions.find(
      (a) => (a.title || "").trim() === title && a.isLeave === true,
    );
    return Boolean(defByTitle);
  };

  const isLeaveActivityInstance = (activity: ActivityInstance): boolean =>
    isLeaveActivityByDefinitions(activity, activityDefinitions);

  const mapActivityReportToCompletedItem = (
    report: ActivityReport,
    workflowInstanceId: number,
  ): ActivityInstance => ({
    id: report.activityId,
    templateActivityId: report.templateActivityId ?? null,
    workflowInstanceId,
    activityTitle:
      report.activityTitle || report.activityInstanceName || "Untitled Activity",
    name: report.activityInstanceName || report.activityTitle,
    projectName: report.projectName ?? null,
    isLeave: report.isLeave === true,
    requiresApproval: report.requiresApproval,
    status: report.activityStatus,
    startDate: report.activityCreatedAt || null,
    endDate: report.activityCompletedAt || null,
    assignedUsers:
      report.assignedUsers ||
      (typeof report.assigneeId === "number"
        ? JSON.stringify([report.assigneeId])
        : "[]"),
    completedUserIds: report.completedUserIds ?? null,
    completedAt: report.activityCompletedAt || null,
    createdAt: report.activityCreatedAt || null,
    estimatedDuration: null,
    actualDuration: report.duration ?? null,
    userDurations: report.userDurations,
    submitDescription: report.submitDescription,
    attachments: report.attachments,
    userFormData: report.userFormData,
    formContentSummary: report.formContentSummary,
    formContentTooltip: report.formContentTooltip,
    isInvoiced: Boolean(report.isInvoiced),
  });

  const formatDurationHours = (hours: number): number => {
    return Math.round(hours * 100) / 100;
  };

  const calculateBreakHoursFromRanges = (
    breaks: Array<{ start: string; end: string }>,
    rangeStart?: string,
    rangeEnd?: string,
  ): number => {
    if (!breaks || breaks.length === 0) return 0;
    const activityStart = parseDateTimeString(rangeStart);
    const activityEnd = parseDateTimeString(rangeEnd);

    const intervals = breaks
      .map((entry) => {
        const start = parseDateTimeString(entry.start);
        const end = parseDateTimeString(entry.end);
        if (!start || !end || end <= start) return null;
        let adjustedStart = start;
        let adjustedEnd = end;
        if (activityStart && adjustedStart < activityStart) {
          adjustedStart = activityStart;
        }
        if (activityEnd && adjustedEnd > activityEnd) {
          adjustedEnd = activityEnd;
        }
        if (adjustedEnd <= adjustedStart) return null;
        return { start: adjustedStart, end: adjustedEnd };
      })
      .filter(Boolean) as Array<{ start: Date; end: Date }>;

    if (intervals.length === 0) return 0;
    intervals.sort((a, b) => a.start.getTime() - b.start.getTime());
    let totalMs = 0;
    let current = intervals[0];
    for (let i = 1; i < intervals.length; i += 1) {
      const next = intervals[i];
      if (next.start <= current.end) {
        if (next.end > current.end) {
          current.end = next.end;
        }
      } else {
        totalMs += current.end.getTime() - current.start.getTime();
        current = next;
      }
    }
    totalMs += current.end.getTime() - current.start.getTime();
    return totalMs / (1000 * 60 * 60);
  };

  // Parse user durations to get TOTAL time data (sum of all users)
  const getUserTimeInfo = (
    userDurations: string | undefined,
  ): {
    startTime: string;
    endTime: string;
    duration: number;
    userCount: number;
  } | null => {
    if (!userDurations) return null;
    try {
      const parsed = JSON.parse(userDurations);
      const userIds = Object.keys(parsed);
      if (userIds.length === 0) return null;

      // Calculate total duration from all users
      let totalDuration = 0;
      let earliestStart = "";
      let latestEnd = "";

      userIds.forEach((userId) => {
        const userData = parsed[userId];

        // New format: { startTime, endTime, duration }
        if (
          userData &&
          typeof userData === "object" &&
          "duration" in userData
        ) {
          // Recalculate duration from startTime and endTime for accuracy
          const breakHours = Array.isArray(userData.breaks)
            ? calculateBreakHoursFromRanges(
                userData.breaks,
                userData.startTime,
                userData.endTime,
              )
            : 0;
          let userDuration = userData.duration || 0;
          if (userData.startTime && userData.endTime) {
            const start = parseDateTimeString(userData.startTime);
            const end = parseDateTimeString(userData.endTime);
            if (start && end) {
              const diffMs = end.getTime() - start.getTime();
              if (diffMs > 0) {
                const rawHours = diffMs / (1000 * 60 * 60);
                userDuration = formatDurationHours(
                  Math.max(rawHours - breakHours, 0),
                );
              }
            }
          } else if (breakHours > 0) {
            userDuration = formatDurationHours(
              Math.max(userDuration - breakHours, 0),
            );
          }
          totalDuration += userDuration;

          // Track earliest start and latest end (only for single user activities)
          if (userData.startTime) {
            if (!earliestStart || userData.startTime < earliestStart) {
              earliestStart = userData.startTime;
            }
          }
          if (userData.endTime) {
            if (!latestEnd || userData.endTime > latestEnd) {
              latestEnd = userData.endTime;
            }
          }
        }
      });

      return {
        startTime: earliestStart,
        endTime: latestEnd,
        duration: totalDuration,
        userCount: userIds.length,
      };
    } catch (error) {
      return null;
    }
  };

  const handleViewDetails = async (activity: any) => {
    const activityId =
      typeof activity?.activityId === "number" ? activity.activityId : activity?.id;
    if (!activityId) {
      return;
    }

    const previewReport: ActivityReport = {
      activityId,
      activityTitle: activity.activityTitle || activity.name || "",
      activityDescription: activity.description || activity.activityDescription,
      activityStatus: activity.status || activity.activityStatus,
      activityCreatedAt:
        activity.createdAt || activity.startDate || activity.activityCreatedAt,
      activityCompletedAt:
        activity.completedAt || activity.endDate || activity.activityCompletedAt,
      submitDescription: activity.submitDescription,
      assigneeName: getAssignedUserNames(activity),
      workflowContainerId: workflowInstance?.workflowContainerId,
      projectName:
        activity.projectName || workflowInstance?.workflowContainerName,
      workflowId: workflowInstance?.id || undefined,
      workflowName: workflowInstance?.name || undefined,
      userDurations: activity.userDurations,
      userFormData: activity.userFormData,
      attachments: activity.attachments,
      duration: activity.actualDuration ?? activity.duration,
      assignedUsers: activity.assignedUsers,
      completedUserIds: activity.completedUserIds,
      formContentSummary: activity.formContentSummary,
      formContentTooltip: activity.formContentTooltip,
    };

    setSelectedActivity(previewReport);
    setIsDetailModalOpen(true);

    try {
      const detailedReport = await apiService.getActivityReportDetail(activityId);
      setSelectedActivity(detailedReport);
    } catch (error) {
    }
  };

  const handleApproveDetailActivity = async (activityInstanceId: number) => {
    try {
      await apiService.approveActivityInstance(activityInstanceId);
      toast.success(t("common:approve"));
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("errors:generic"));
    }
  };

  const handleRejectDetailActivity = async (activityInstanceId: number) => {
    try {
      await apiService.rejectActivityInstance(activityInstanceId);
      toast.success(t("common:reject"));
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("errors:generic"));
    }
  };

  const getManagerCommentUserOptions = (activity: ActivityInstance) => {
    const ids = new Set<number>();
    parseJsonArray(activity.assignedUsers).forEach((id) => ids.add(Number(id)));
    parseJsonArray(activity.completedUserIds).forEach((id) =>
      ids.add(Number(id)),
    );
    const addedBy = (activity as any).addedByUserId;
    if (typeof addedBy === "number") {
      ids.add(addedBy);
    }
    const durations = parseJsonObject(activity.userDurations);
    if (durations && typeof durations === "object") {
      Object.keys(durations).forEach((key) => {
        const n = Number(key);
        if (Number.isFinite(n)) ids.add(n);
      });
    }

    const ordered = Array.from(ids).filter((n) => Number.isFinite(n));
    const labelFor = (userId: number): string => {
      const member = projectMembers.find(
        (m) => m.userId === userId || m.user?.id === userId,
      );
      if (member?.user) {
        const full =
          `${member.user.firstName ?? ""} ${member.user.lastName ?? ""}`.trim();
        return full || member.user.username || `User ${userId}`;
      }
      const user = allUsers.find((u) => u.id === userId);
      if (user) {
        const full = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
        return full || user.username || `User ${userId}`;
      }
      return `User ${userId}`;
    };

    return ordered.map((id) => ({ id, label: labelFor(id) }));
  };

  const openManagerCommentModal = (activity: ActivityInstance) => {
    if (!(isAdmin() || isManager())) return;
    const existing = latestManagerCommentsByActivityId[String(activity.id)];
    setManagerCommentActivity(activity);
    setManagerCommentText((existing?.comment ?? "").toString());
  };

  const closeManagerCommentModal = () => {
    if (savingManagerComment) return;
    setManagerCommentActivity(null);
    setManagerCommentText("");
  };

  const saveManagerComment = async () => {
    if (!managerCommentActivity) return;
    if (!(isAdmin() || isManager())) return;

    const comment = managerCommentText.trim();
    if (!comment) {
      toast.error(t("dashboard:managerActivities.commentEmpty"));
      return;
    }

    try {
      setSavingManagerComment(true);
      const resp = await apiService.createActivityManagerComment({
        activityInstanceId: managerCommentActivity.id,
        comment,
        isGlobal: true,
      });

      const createdAt =
        resp?.data?.createdAt ||
        resp?.data?.data?.createdAt ||
        new Date().toISOString();
      const authorName =
        resp?.data?.authorName ||
        resp?.data?.data?.authorName ||
        (() => {
          const full =
            `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
          return full || user?.username || null;
        })();

      setLatestManagerCommentsByActivityId((prev) => ({
        ...prev,
        [String(managerCommentActivity.id)]: {
          comment: comment,
          authorName: authorName,
          createdAt: createdAt,
        },
      }));
      toast.success(t("projects:workflowInstanceDetail.managerCommentSaved"));
      closeManagerCommentModal();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("errors:generic"));
    } finally {
      setSavingManagerComment(false);
    }
  };

  const handleDelete = async () => {
    if (!instanceId) return;
    try {
      await apiService.deleteWorkflowInstance(instanceId);
      toast.success(
        t("workflows:deleteSuccess", "Workflow deleted successfully"),
      );
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(NAV_REFRESH_EVENT, {
            detail: { projectInstances: true },
          }),
        );
      }
      navigateBack();
    } catch (error) {
      toast.error(t("errors:deleteFailed", "Failed to delete workflow"));
    }
  };

  const handleStartTemplateChange = () => {
    if (!selectedWorkflowId) {
      toast.error(t("workflows:templateIdNotFound"));
      return;
    }
    setShowChangeTypeModal(false);
    setShowTemplateChangeModal(true);
  };

  const handleCloseTemplateChange = () => {
    setShowTemplateChangeModal(false);
    setSelectedWorkflowId(null);
  };

  const handleTemplateChangeSuccess = () => {
    setShowTemplateChangeModal(false);
    setSelectedWorkflowId(null);
    setRefreshKey((prev) => prev + 1);
  };

  const instanceId = useMemo(() => (id ? Number.parseInt(id, 10) : null), [id]);

  const reloadWorkflowInstanceSummary = useCallback(
    async (targetInstanceId: number): Promise<WorkflowInstance | null> => {
      const instance = await apiService.getWorkflowInstanceById(targetInstanceId);
      if (instance) {
        setWorkflowInstance(instance);
        return instance as WorkflowInstance;
      }
      return null;
    },
    [],
  );

  const loadCompletedActivityReports = useCallback(
    async (targetInstanceId: number): Promise<ActivityInstance[]> => {
      const detailItems: ActivityInstance[] = [];
      const detailStatuses: Array<"COMPLETED" | "PENDING_APPROVAL"> = [
        "COMPLETED",
        "PENDING_APPROVAL",
      ];

      for (const status of detailStatuses) {
        let page = 0;
        let hasNext = true;

        while (hasNext) {
          const response = await apiService.getActivityReportsList({
            workflowInstanceId: targetInstanceId,
            status,
            page,
            size: 1000,
          });

          const pageItems = Array.isArray(response?.items)
            ? response.items.map((report) =>
                mapActivityReportToCompletedItem(report, targetInstanceId),
              )
            : [];
          detailItems.push(...pageItems);

          hasNext = Boolean(response?.hasNext);
          page += 1;
        }
      }

      return Array.from(
        new Map(detailItems.map((item) => [item.id, item])).values(),
      );
    },
    [],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!instanceId) return;
      setLoading(true);

      try {
        const instance = await reloadWorkflowInstanceSummary(instanceId);
        if (!instance) {
          toast.error(t("errors:loadFailed"));
          return;
        }

        if (isVisitorUser) {
          setActivityInstances([]);
          setCompletedActivityItemsData([]);
          setActivityDefinitions([]);
          try {
            const [containerResult, peopleResult] = await Promise.allSettled([
              apiService.getWorkflowContainerById(instance.workflowContainerId),
              apiService.getWorkflowInstancePeople(instance.id),
            ]);

            if (containerResult.status === "fulfilled") {
              setProjectContainer(containerResult.value);
            } else {
              setProjectContainer(null);
            }

            if (peopleResult.status === "fulfilled") {
              const resolvedPeople = Array.isArray(peopleResult.value)
                ? peopleResult.value
                : [];
              setAllUsers(resolvedPeople);
              setProjectMembers(
                mapMembersFromIds(
                  Array.isArray(instance.projectMembers)
                    ? instance.projectMembers
                    : [],
                  resolvedPeople,
                  instance.workflowContainerId,
                ),
              );
            } else {
              setAllUsers([]);
              setProjectMembers([]);
            }
          } catch (error) {
            setProjectContainer(null);
            setAllUsers([]);
            setProjectMembers([]);
          }
        } else {
          let instanceMemberIds: number[] = [];
          const companyId = (user?.company?.id ??
            (user as any)?.companyId) as number | undefined;
          const canLoadCompanyUsers =
            isAdmin() ||
            isManager() ||
            user?.role === UserRole.PROJECT_MANAGER;

          const [
            instanceDataResult,
            containerResult,
            workflowResult,
            activitiesResult,
            definitionsResult,
            completedReportsResult,
            usersResult,
            peopleResult,
          ] = await Promise.allSettled([
            apiService.getWorkflowInstanceFormData(instance.id),
            apiService.getWorkflowContainerById(instance.workflowContainerId),
            !instance.workflowName && instance.workflowId
              ? apiService.getWorkflowById(instance.workflowId)
              : Promise.resolve(null),
            apiService.getActivityInstancesByWorkflowInstance(instanceId),
            companyId
              ? apiService.getActivitiesByCompany(companyId)
              : Promise.resolve([]),
            loadCompletedActivityReports(instanceId),
            canLoadCompanyUsers
              ? apiService.getUsers(true)
              : Promise.resolve([]),
            apiService.getWorkflowInstancePeople(instance.id),
          ]);

          if (instanceDataResult.status === "fulfilled") {
            instanceMemberIds = instanceDataResult.value.projectMembers || [];
          } else {
          }

          if (containerResult.status === "fulfilled") {
            setProjectContainer(containerResult.value);
          } else {
            setProjectContainer(null);
          }

          if (
            workflowResult.status === "fulfilled" &&
            workflowResult.value?.name &&
            !instance.workflowName
          ) {
            const loadedWorkflow = workflowResult.value;
            setWorkflowInstance((prev) =>
              prev ? { ...prev, workflowName: loadedWorkflow.name } : null,
            );
          } else if (workflowResult.status === "rejected") {
          }

          const workflowActivities =
            activitiesResult.status === "fulfilled" ? activitiesResult.value : [];
          if (activitiesResult.status === "rejected") {
          }
          setActivityInstances(workflowActivities);

          const loadedDefinitions =
            definitionsResult.status === "fulfilled" &&
            Array.isArray(definitionsResult.value)
              ? definitionsResult.value
              : [];
          if (definitionsResult.status === "rejected") {
          }
          setActivityDefinitions(loadedDefinitions);

          if (completedReportsResult.status === "fulfilled") {
            setCompletedActivityItemsData(completedReportsResult.value);
          } else {
            const fallbackCompletedItems = workflowActivities.filter(
              (activity) =>
                activity.status === "COMPLETED" ||
                activity.status === "PENDING_APPROVAL",
            );
            setCompletedActivityItemsData(fallbackCompletedItems);
          }

          const referencedUsers =
            peopleResult.status === "fulfilled" &&
            Array.isArray(peopleResult.value)
              ? peopleResult.value
              : [];
          if (peopleResult.status === "rejected") {
          }

          const companyUsers =
            usersResult.status === "fulfilled" && Array.isArray(usersResult.value)
              ? usersResult.value
              : [];
          if (usersResult.status === "rejected") {
          }

          const mergedUsers = Array.from(
            new Map<number, User>(
              [...companyUsers, ...referencedUsers].map((resolvedUser) => [
                resolvedUser.id,
                resolvedUser,
              ]),
            ).values(),
          );

          if (mergedUsers.length > 0) {
            setAllUsers(mergedUsers);

            const userMap = new Map<number, User>(
              mergedUsers.map((resolvedUser) => [resolvedUser.id, resolvedUser]),
            );
            const now = new Date().toISOString();
            const mappedMembers = instanceMemberIds
              .map((userId) => {
                const user = userMap.get(userId);
                if (!user) {
                  return null;
                }

                const isWorkflowProjectManager = (() => {
                  if (
                    user.role !== UserRole.PROJECT_MANAGER ||
                    !instance.templateWorkflowId ||
                    !user.settingsJson
                  ) {
                    return false;
                  }
                  try {
                    const settings = JSON.parse(user.settingsJson);
                    const rawIds =
                      settings?.workflowPermissions?.allowedWorkflowIds;
                    if (!Array.isArray(rawIds)) {
                      return false;
                    }
                    const allowed = rawIds
                      .map((id: any) => Number.parseInt(String(id), 10))
                      .filter((id: number) => !Number.isNaN(id));
                    return allowed.includes(instance.templateWorkflowId);
                  } catch (error) {
                    return false;
                  }
                })();

                return {
                  id: userId,
                  workflowContainerId: instance.workflowContainerId,
                  userId,
                  role: isWorkflowProjectManager
                    ? "project_manager"
                    : "team_member",
                  canStartWorkflow: false,
                  addedAt: now,
                  user: {
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    username: user.username,
                    email: user.email,
                    role: user.role,
                  },
                } as WorkflowContainerMember;
              })
              .filter(
                (member): member is WorkflowContainerMember => member !== null,
              );

            setProjectMembers(mappedMembers);
          } else {
            setAllUsers([]);
            setProjectMembers([]);
          }
        }
      } catch (error) {
        toast.error(t("errors:loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [
    instanceId,
    isVisitorUser,
    loadCompletedActivityReports,
    refreshKey,
    reloadWorkflowInstanceSummary,
    t,
    user,
  ]);

  useEffect(() => {
    const fetchCostRates = async () => {
      if (!canViewFinancials || isVisitorUser) {
        setUserHourlyCostRates([]);
        setCompanyAverageHourlyCostRates([]);
        return;
      }

      try {
        setIsCostRatesLoading(true);
        const [userRatesResponse, averageRatesResponse] = await Promise.all([
          apiService.getHourlyCostRates(),
          apiService.getAverageHourlyCostRates(),
        ]);

        const userRates = (userRatesResponse?.data || userRatesResponse) as
          | UserHourlyCostRateRow[]
          | null;
        const averageRates = (averageRatesResponse?.data ||
          averageRatesResponse) as CompanyHourlyCostRateRow[] | null;

        setUserHourlyCostRates(Array.isArray(userRates) ? userRates : []);
        setCompanyAverageHourlyCostRates(
          Array.isArray(averageRates) ? averageRates : [],
        );
      } catch (error) {
        setUserHourlyCostRates([]);
        setCompanyAverageHourlyCostRates([]);
      } finally {
        setIsCostRatesLoading(false);
      }
    };

    void fetchCostRates();
  }, [canViewFinancials, isVisitorUser, refreshKey]);

  useEffect(() => {
    if (!showChangeTypeModal) {
      return;
    }

    const loadWorkflows = async () => {
      setIsLoadingWorkflows(true);
      try {
        const workflows = await apiService.getWorkflows();
        const activeWorkflows = workflows.filter(
          (workflow) =>
            workflow.isActive !== false &&
            workflow.id !== workflowInstance?.workflowId,
        );
        setAvailableWorkflows(activeWorkflows);
        setSelectedWorkflowId(activeWorkflows[0]?.id ?? null);
      } catch (error) {
        toast.error(t("errors:loadFailed"));
      } finally {
        setIsLoadingWorkflows(false);
      }
    };

    void loadWorkflows();
  }, [showChangeTypeModal, workflowInstance?.workflowId, t]);

  const confirmReactivateProject = async () => {
    if (!instanceId) return;
    try {
      await apiService.reactivateWorkflowInstance(instanceId);
      toast.success(t("workflows:projectReactivated"));
      setShowReactivateDialog(false);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(NAV_REFRESH_EVENT, {
            detail: { projectInstances: true },
          }),
        );
      }

      await reloadWorkflowInstanceSummary(instanceId);
    } catch (error) {
      toast.error(t("workflows:projectReactivateFailed"));
    }
  };

  const handleCompleteWorkflow = async () => {
    if (!instanceId) return;

    setIsCompleting(true);
    try {
      const archivedFinancialSnapshotPayload =
        buildArchivedFinancialSnapshotPayload();
      await apiService.completeWorkflowInstance(
        instanceId,
        archivedFinancialSnapshotPayload
          ? { archivedFinancialSnapshot: archivedFinancialSnapshotPayload }
          : undefined,
      );
      toast.success(t("workflows:projectArchived"));
      setShowCompleteDialog(false);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(NAV_REFRESH_EVENT, {
            detail: { projectInstances: true },
          }),
        );
      }

      await reloadWorkflowInstanceSummary(instanceId);
    } catch (error) {
      toast.error(t("workflows:projectArchiveFailed"));
    } finally {
      setIsCompleting(false);
    }
  };

  const getStatusDisplayName = (status: string): string => {
    switch (status) {
      case "ACTIVE":
        return t("workflows:statuses.active");
      case "IN_PROGRESS":
        return t("workflows:statuses.inProgress");
      case "COMPLETED":
        return t("workflows:statuses.completed");
      case "ARCHIVED":
        return t("workflows:statuses.archived");
      case "PENDING":
        return t("workflows:statuses.pending");
      case "PENDING_APPROVAL":
        return t("dashboard:pendingApproval");
      case "REJECTED":
        return t("tasks:status.rejected");
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
      case "IN_PROGRESS":
        return "from-blue-100 to-cyan-100 text-blue-800 border-blue-200";
      case "COMPLETED":
        return "from-emerald-100 to-green-100 text-emerald-800 border-emerald-200";
      case "ARCHIVED":
        return "from-gray-100 to-slate-100 text-gray-800 border-gray-200";
      case "PENDING":
        return "from-amber-100 to-yellow-100 text-amber-800 border-amber-200";
      case "PENDING_APPROVAL":
        return "from-amber-100 to-orange-100 text-amber-800 border-amber-200";
      case "REJECTED":
        return "from-rose-100 to-red-100 text-rose-800 border-rose-200";
      default:
        return "from-slate-100 to-gray-100 text-slate-800 border-slate-200";
    }
  };

  const detailTemplateActivities = useMemo(
    () => activityInstances.filter((activity) => !activity.isAdHoc),
    [activityInstances],
  );

  const showLeaveActivitiesInDetail = useMemo(() => {
    const sourceActivities =
      detailTemplateActivities.length > 0
        ? detailTemplateActivities
        : completedActivityItemsData;

    if (sourceActivities.length === 0) {
      return false;
    }

    return sourceActivities.every((activity) =>
      isLeaveActivityByDefinitions(activity, activityDefinitions),
    );
  }, [activityDefinitions, completedActivityItemsData, detailTemplateActivities]);

  const completedActivityItems = useMemo(
    () =>
      completedActivityItemsData.filter((activity) => {
        const isLeave = isLeaveActivityByDefinitions(
          activity,
          activityDefinitions,
        );
        return showLeaveActivitiesInDetail ? isLeave : !isLeave;
      }),
    [activityDefinitions, completedActivityItemsData, showLeaveActivitiesInDetail],
  );

  const fullyCompletedActivityItems = useMemo(
    () =>
      completedActivityItems.filter((activity) => activity.status === "COMPLETED"),
    [completedActivityItems],
  );

  const hasPendingApprovalDetailActivities = useMemo(
    () =>
      completedActivityItems.some(
        (activity) => activity.status === "PENDING_APPROVAL",
      ),
    [completedActivityItems],
  );

  const completedActivities = fullyCompletedActivityItems.length;
  const totalActivities = useMemo(() => {
    const scopedTemplateActivities = detailTemplateActivities.filter((activity) => {
      const isLeave = isLeaveActivityByDefinitions(activity, activityDefinitions);
      return showLeaveActivitiesInDetail ? isLeave : !isLeave;
    });

    if (scopedTemplateActivities.length > 0) {
      return scopedTemplateActivities.length;
    }

    return fullyCompletedActivityItems.length;
  }, [
    activityDefinitions,
    detailTemplateActivities,
    fullyCompletedActivityItems.length,
    showLeaveActivitiesInDetail,
  ]);
  const progress =
    totalActivities > 0
      ? Math.round((completedActivities / totalActivities) * 100)
      : 0;

  const completedActivityIdsKey = completedActivityItems
    .map((a) => a.id)
    .filter((id): id is number => typeof id === "number")
    .sort((a, b) => a - b)
    .join(",");
  const canLoadLatestManagerComments = isAdmin() || isManager();

  useEffect(() => {
    const loadLatestManagerComments = async () => {
      if (!canLoadLatestManagerComments) {
        setLatestManagerCommentsByActivityId({});
        return;
      }
      if (!completedActivityIdsKey) {
        setLatestManagerCommentsByActivityId({});
        return;
      }

      try {
        const ids = completedActivityIdsKey
          .split(",")
          .map((s) => Number(s))
          .filter((n) => Number.isFinite(n));
        const resp = await apiService.getLatestActivityManagerComments({
          activityInstanceIds: ids,
        });
        const data = resp?.data || resp;
        const map = (data?.activities || {}) as Record<
          string,
          {
            comment?: string | null;
            authorName?: string | null;
            createdAt?: string | null;
          }
        >;
        setLatestManagerCommentsByActivityId(map || {});
      } catch (error) {
        setLatestManagerCommentsByActivityId({});
      }
    };

    void loadLatestManagerComments();
  }, [completedActivityIdsKey, canLoadLatestManagerComments]);

  const normalizeFieldToken = (value: unknown): string =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/ı/g, "i")
      .replace(/ö/g, "o")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ğ/g, "g")
      .replace(/ç/g, "c")
      .replace(/[^a-z0-9]/g, "");

  const fieldTranslationKeyById: Record<string, string> = {
    name: "projects:projectName",
    projectname: "projects:projectName",
    description: "common:description",
    projectdescription: "common:description",
    clientname: "projects:clientName",
    client: "projects:clientName",
    type: "projects:projectType",
    projecttype: "projects:projectType",
    projectmanager: "projects:projectManager",
    projectresponsible: "projects:projectManager",
    projesorumlusu: "projects:projectManager",
    startdate: "projects:startDate",
    enddate: "projects:endDate",
    budget: "projects:budget",
    projectbudget: "projects:budget",
    offeramount: "projects:offerAmount",
    istutariteklif: "projects:offerAmount",
    invoice: "projects:invoice",
    fatura: "projects:invoice",
    estimateddurationdays: "projects:projectDurationDays",
    projectduration: "projects:projectDurationDays",
    projectdurationhours: "projects:projectDurationDays",
    ulke: "projects:country",
    country: "projects:country",
    il: "projects:city",
    sehir: "projects:city",
    city: "projects:city",
    ilce: "projects:district",
    district: "projects:district",
    mahalle: "projects:neighborhood",
    neighborhood: "projects:neighborhood",
    currency: "projects:currency",
    parabirimi: "projects:currency",
  };

  const fieldTranslationKeyByType: Record<string, string> = {
    budget_input: "projects:budget",
    offer_amount: "projects:offerAmount",
    invoice: "projects:invoice",
    project_duration: "projects:projectDurationDays",
    client_select: "projects:clientName",
    project_type_select: "projects:projectType",
    country_select: "projects:country",
    city_select: "projects:city",
    province_select: "projects:city",
    district_select: "projects:district",
    neighborhood_select: "projects:neighborhood",
    currency_input: "projects:currency",
  };

  const fieldTranslationKeyByLabel: Record<string, string> = {
    projesorumlusu: "projects:projectManager",
    fatura: "projects:invoice",
    istutariteklif: "projects:offerAmount",
    projesuresisaat: "projects:projectDurationDays",
    projeturu: "projects:projectType",
    ulke: "projects:country",
    il: "projects:city",
    sehir: "projects:city",
    ilce: "projects:district",
    mahalle: "projects:neighborhood",
    parabirimi: "projects:currency",
    butce: "projects:budget",
  };

  const getFieldLabel = (
    key: string,
    element?: { id?: string; type?: string; label?: string } | null,
  ): string => {
    const keyToken = normalizeFieldToken(key);
    const typeToken = normalizeFieldToken(element?.type);
    const labelToken = normalizeFieldToken(element?.label);

    const translationKey =
      fieldTranslationKeyById[keyToken] ||
      fieldTranslationKeyByType[typeToken] ||
      fieldTranslationKeyByLabel[labelToken];

    if (translationKey) {
      return t(translationKey as any, {
        defaultValue: element?.label || key,
      });
    }

    if (element?.label) {
      return element.label;
    }

    return key;
  };

  const parseJsonArray = (raw?: string | null): any[] => {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const parseJsonObject = (raw?: string | null): any => {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const parseRateDateBoundary = (
    value: string | null | undefined,
    endOfDay = false,
  ): Date | null => {
    if (!value || typeof value !== "string") return null;
    return parseDateTimeString(
      `${value}${endOfDay ? "T23:59:59" : "T00:00:00"}`,
    );
  };

  const isDateWithinRate = (
    targetDate: Date,
    startDate: string,
    endDate?: string | null,
  ): boolean => {
    const start = parseRateDateBoundary(startDate, false);
    const end = endDate
      ? parseRateDateBoundary(endDate, true)
      : parseRateDateBoundary("9999-12-31", true);
    if (!start || !end) return false;
    return targetDate >= start && targetDate <= end;
  };

  const projectFinanceInputs = useMemo(
    () =>
      resolveProjectFinanceInputs(
        workflowInstance?.workflowContainerFormData,
        projectContainer?.formElements as any,
      ),
    [projectContainer?.formElements, workflowInstance?.workflowContainerFormData],
  );

  const projectFormElements = useMemo<ProjectFormElement[]>(
    () =>
      Array.isArray(projectContainer?.formElements)
        ? ((projectContainer?.formElements as ProjectFormElement[]) || [])
        : [],
    [projectContainer?.formElements],
  );

  const offerAmountElement = useMemo(
    () =>
      projectFormElements.find((element) => element.type === "offer_amount") ||
      null,
    [projectFormElements],
  );

  const invoiceElement = useMemo(
    () =>
      projectFormElements.find((element) => element.type === "invoice") || null,
    [projectFormElements],
  );

  const financeElementCurrencies = useMemo(
    () =>
      extractFinanceElementCurrencies(
        workflowInstance?.workflowContainerFormData,
      ),
    [workflowInstance?.workflowContainerFormData],
  );

  const currentOfferShortcutCurrency = useMemo(() => {
    const storedCurrency = offerAmountElement
      ? financeElementCurrencies[offerAmountElement.id]
      : undefined;

    return normalizeCurrencyCode(
      storedCurrency ||
        financeElementCurrencies.offer_amount ||
        financeElementCurrencies.offerAmount ||
        offerAmountElement?.currency ||
        projectFinanceInputs.projectCurrency ||
        "TRY",
    );
  }, [
    financeElementCurrencies,
    offerAmountElement,
    projectFinanceInputs.projectCurrency,
  ]);

  const activeExistingFinanceShortcutFiles = useMemo(
    () =>
      financeShortcutMode === "offer"
        ? existingFinanceShortcutFiles.offer
        : financeShortcutMode === "invoice"
          ? existingFinanceShortcutFiles.invoice
          : [],
    [existingFinanceShortcutFiles, financeShortcutMode],
  );

  const handlePreviewWorkflowFile = useCallback(
    async (file: GoogleDriveAttachment) => {
      if (!instanceId || !file?.id) return;

      const previewWindow = apiService.shouldOpenPreviewWindow(
        file.name,
        file.mimeType,
      )
        ? window.open("", "_blank")
        : null;
      if (previewWindow) {
        previewWindow.opener = null;
        apiService.preparePreviewWindow(previewWindow, file.name);
      }

      try {
        setPreviewingDriveFileId(file.id);
        await apiService.previewWorkflowInstanceFile(
          instanceId,
          file.id,
          file.name || "preview",
          previewWindow,
        );
      } catch (error) {
        if (previewWindow && !previewWindow.closed) {
          previewWindow.close();
        }
        toast.error("Dosya görüntülenemedi.");
      } finally {
        setPreviewingDriveFileId(null);
      }
    },
    [instanceId],
  );

  const formatProjectDetailValue = useCallback(
    (value: unknown): string => {
      if (value === null || value === undefined) {
        return t("common:notProvided");
      }

      if (Array.isArray(value)) {
        const items = value
          .map((item) => String(item ?? "").trim())
          .filter(Boolean);
        return items.length > 0 ? items.join(", ") : t("common:notProvided");
      }

      if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed || trimmed === "[]" || trimmed === "{}") {
          return t("common:notProvided");
        }
        return trimmed;
      }

      return String(value);
    },
    [t],
  );

  useEffect(() => {
    if (!canViewCollectionDetails) {
      setExchangeRatesByBase({});
      return;
    }

    const currencies = new Set<string>();
    currencies.add(projectFinanceInputs.projectCurrency);

    userHourlyCostRates.forEach((rate) => {
      currencies.add(normalizeCurrencyCode(rate.currency));
    });
    companyAverageHourlyCostRates.forEach((rate) => {
      currencies.add(normalizeCurrencyCode(rate.currency));
    });

    const requestedBases = Array.from(currencies).filter(Boolean);
    if (!requestedBases.length) {
      setExchangeRatesByBase({});
      return;
    }

    let isCancelled = false;

    const loadExchangeRates = async () => {
      try {
        setIsExchangeRatesLoading(true);
        const responses = await Promise.all(
          requestedBases.map(async (baseCurrency) => {
            try {
              const payload = await apiService.getLatestExchangeRates(baseCurrency);
              const extracted = extractExchangeRates(payload);
              return extracted
                ? ([extracted.baseCurrency, extracted.rates] as const)
                : null;
            } catch (error) {
              return null;
            }
          }),
        );

        if (isCancelled) {
          return;
        }

        const nextRates = responses.reduce<ExchangeRatesByBase>((acc, entry) => {
          if (entry) {
            acc[entry[0]] = entry[1];
          }
          return acc;
        }, {});
        setExchangeRatesByBase(nextRates);
      } finally {
        if (!isCancelled) {
          setIsExchangeRatesLoading(false);
        }
      }
    };

    void loadExchangeRates();

    return () => {
      isCancelled = true;
    };
  }, [
    canViewCollectionDetails,
    companyAverageHourlyCostRates,
    projectFinanceInputs.projectCurrency,
    userHourlyCostRates,
  ]);

  useEffect(() => {
    if (!canViewCollectionDetails) {
      setDailyExchangeRatesByDate({});
      return;
    }

    let minDate: Date | null = null;
    let maxDate: Date | null = null;
    const trackDate = (candidate: Date | null) => {
      if (!candidate) {
        return;
      }
      if (!minDate || candidate < minDate) {
        minDate = candidate;
      }
      if (!maxDate || candidate > maxDate) {
        maxDate = candidate;
      }
    };

    activityInstances.forEach((activity) => {
      trackDate(parseHistoricalDateCandidate(activity.completedAt));
      trackDate(parseHistoricalDateCandidate(activity.endDate));
      trackDate(parseHistoricalDateCandidate(activity.startDate));
      trackDate(parseHistoricalDateCandidate(activity.createdAt));

      if (!activity.userDurations) {
        return;
      }

      try {
        const parsed = JSON.parse(activity.userDurations);
        if (!parsed || typeof parsed !== "object") {
          return;
        }

        Object.values(parsed).forEach((entry: any) => {
          trackDate(parseHistoricalDateCandidate(entry?.startTime));
          trackDate(parseHistoricalDateCandidate(entry?.endTime));
          if (Array.isArray(entry?.breaks)) {
            entry.breaks.forEach((breakEntry: any) => {
              trackDate(parseHistoricalDateCandidate(breakEntry?.start));
              trackDate(parseHistoricalDateCandidate(breakEntry?.end));
            });
          }
        });
      } catch {
        // ignore malformed payload
      }
    });

    projectCollections.forEach((collection) => {
      trackDate(parseHistoricalDateCandidate(collection.collectionDate));
    });

    if (!minDate || !maxDate) {
      setDailyExchangeRatesByDate({});
      return;
    }

    let isCancelled = false;

    const loadDailyExchangeRates = async () => {
      try {
        const payload = await apiService.getDailyExchangeRates(
          toIsoDateKey(minDate as Date),
          toIsoDateKey(maxDate as Date),
          "TRY"
        );
        if (isCancelled) {
          return;
        }

        const rows: any[] = Array.isArray(payload?.data) ? payload.data : [];
        const nextDailyRates = rows.reduce(
          (acc: DailyExchangeRatesByDate, row: any) => {
            const dateKey =
              typeof row?.rateDate === "string" ? row.rateDate.slice(0, 10) : "";
            const extracted = extractExchangeRates({
              data: {
                baseCurrency: row?.baseCurrency,
                rates: row?.rates,
              },
            });
            if (dateKey && extracted) {
              acc[dateKey] = {
                ...(acc[dateKey] || {}),
                [extracted.baseCurrency]: extracted.rates,
              };
            }
            return acc;
          },
          {} as DailyExchangeRatesByDate
        );
        setDailyExchangeRatesByDate(nextDailyRates);
      } catch (error) {
        if (!isCancelled) {
          setDailyExchangeRatesByDate({});
        }
      }
    };

    void loadDailyExchangeRates();

    return () => {
      isCancelled = true;
    };
  }, [activityInstances, canViewCollectionDetails, projectCollections]);

  useEffect(() => {
    if (!id || !canViewCollectionDetails) {
      setProjectCollections([]);
      return;
    }

    let isCancelled = false;

    const loadCollections = async () => {
      try {
        setIsCollectionsLoading(true);
        const rows = await apiService.getProjectCollections(Number(id));
        if (!isCancelled) {
          setProjectCollections(
            Array.isArray(rows) ? (rows as ProjectCollection[]) : [],
          );
        }
      } catch (error) {
        if (!isCancelled) {
          setProjectCollections([]);
        }
      } finally {
        if (!isCancelled) {
          setIsCollectionsLoading(false);
        }
      }
    };

    void loadCollections();

    return () => {
      isCancelled = true;
    };
  }, [id, canViewCollectionDetails, refreshKey]);

  const userRatesByUserId = useMemo(() => {
    const map = new Map<number, UserHourlyCostRateRow[]>();
    userHourlyCostRates.forEach((rate) => {
      if (
        typeof rate.userId !== "number" ||
        typeof rate.hourlyCost !== "number" ||
        !Number.isFinite(rate.hourlyCost) ||
        rate.hourlyCost < 0
      ) {
        return;
      }
      const existing = map.get(rate.userId) || [];
      existing.push(rate);
      map.set(rate.userId, existing);
    });

    map.forEach((rows, userId) => {
      const sorted = [...rows].sort((a, b) => {
        const aTime = parseRateDateBoundary(a.startDate)?.getTime() || 0;
        const bTime = parseRateDateBoundary(b.startDate)?.getTime() || 0;
        return bTime - aTime;
      });
      map.set(userId, sorted);
    });

    return map;
  }, [userHourlyCostRates]);

  const sortedAverageRates = useMemo(() => {
    return [...companyAverageHourlyCostRates]
      .filter(
        (rate) =>
          typeof rate.hourlyCost === "number" &&
          Number.isFinite(rate.hourlyCost) &&
          rate.hourlyCost >= 0,
      )
      .sort((a, b) => {
        const aTime = parseRateDateBoundary(a.startDate)?.getTime() || 0;
        const bTime = parseRateDateBoundary(b.startDate)?.getTime() || 0;
        return bTime - aTime;
      });
  }, [companyAverageHourlyCostRates]);

  const convertRateAmountToCurrency = useCallback(
    (
      amount: number | null | undefined,
      fromCurrency: string,
      toCurrency: string,
      date?: Date | null,
    ) =>
      convertCurrencyAmountOnDate(
        amount,
        fromCurrency,
        toCurrency,
        date,
        dailyExchangeRatesByDate,
        exchangeRatesByBase,
      ),
    [dailyExchangeRatesByDate, exchangeRatesByBase],
  );

  const getApplicableAverageRate = (
    date: Date | null,
    targetCurrency = "TRY",
  ): number | null => {
    if (!date) return null;
    for (const rate of sortedAverageRates) {
      if (!rate.startDate || typeof rate.hourlyCost !== "number") {
        continue;
      }
      if (isDateWithinRate(date, rate.startDate, rate.endDate)) {
        return convertRateAmountToCurrency(
          rate.hourlyCost,
          normalizeCurrencyCode(rate.currency),
          targetCurrency,
          date,
        );
      }
    }
    return null;
  };

  const resolveApplicableHourlyRate = (
    userId: number | null | undefined,
    date: Date | null,
    targetCurrency = "TRY",
  ): number | null => {
    if (!date) return null;

    if (typeof userId === "number") {
      const userRates = userRatesByUserId.get(userId) || [];
      for (const rate of userRates) {
        if (
          !rate.startDate ||
          typeof rate.hourlyCost !== "number" ||
          !Number.isFinite(rate.hourlyCost)
        ) {
          continue;
        }
        if (isDateWithinRate(date, rate.startDate, rate.endDate)) {
          return convertRateAmountToCurrency(
            rate.hourlyCost,
            normalizeCurrencyCode(rate.currency),
            targetCurrency,
            date,
          );
        }
      }
    }

    return getApplicableAverageRate(date, targetCurrency);
  };

  const getDurationFromUserDurationEntry = (entry: any): number => {
    if (typeof entry === "number" && Number.isFinite(entry) && entry > 0) {
      return entry;
    }

    if (!entry || typeof entry !== "object") {
      return 0;
    }

    const breakHours = Array.isArray(entry.breaks)
      ? calculateBreakHoursFromRanges(
          entry.breaks,
          entry.startTime,
          entry.endTime,
        )
      : 0;

    let duration =
      typeof entry.duration === "number" && Number.isFinite(entry.duration)
        ? entry.duration
        : 0;

    if (entry.startTime && entry.endTime) {
      const start = parseDateTimeString(entry.startTime);
      const end = parseDateTimeString(entry.endTime);
      if (start && end) {
        const diffMs = end.getTime() - start.getTime();
        if (diffMs > 0) {
          const rawHours = diffMs / (1000 * 60 * 60);
          duration = formatDurationHours(Math.max(rawHours - breakHours, 0));
        }
      }
    } else if (breakHours > 0) {
      duration = formatDurationHours(Math.max(duration - breakHours, 0));
    }

    return duration > 0 ? duration : 0;
  };

  const getWorkedSlicesFromUserDurationEntry = (
    entry: any,
  ): Array<{ at: Date; hours: number }> => {
    if (!entry || typeof entry !== "object") {
      return [];
    }

    const rawStart = parseDateTimeString(entry.startTime);
    const rawEnd = parseDateTimeString(entry.endTime);
    if (!rawStart || !rawEnd) {
      return [];
    }

    const workStart = rawStart;
    const workEnd =
      rawEnd <= rawStart
        ? new Date(rawEnd.getTime() + 24 * 60 * 60 * 1000)
        : rawEnd;
    if (workEnd <= workStart) {
      return [];
    }

    type Interval = { start: Date; end: Date };
    let intervals: Interval[] = [{ start: workStart, end: workEnd }];

    if (Array.isArray(entry.breaks) && entry.breaks.length > 0) {
      entry.breaks.forEach((breakEntry: any) => {
        const breakRawStart = parseDateTimeString(breakEntry?.start);
        const breakRawEnd = parseDateTimeString(breakEntry?.end);
        if (!breakRawStart || !breakRawEnd) {
          return;
        }

        const breakStart = breakRawStart;
        const breakEnd =
          breakRawEnd <= breakRawStart
            ? new Date(breakRawEnd.getTime() + 24 * 60 * 60 * 1000)
            : breakRawEnd;
        if (breakEnd <= breakStart) {
          return;
        }

        const nextIntervals: Interval[] = [];
        intervals.forEach((interval) => {
          if (breakEnd <= interval.start || breakStart >= interval.end) {
            nextIntervals.push(interval);
            return;
          }

          const cutStart =
            breakStart > interval.start ? breakStart : interval.start;
          const cutEnd = breakEnd < interval.end ? breakEnd : interval.end;

          if (cutStart > interval.start) {
            nextIntervals.push({ start: interval.start, end: cutStart });
          }
          if (cutEnd < interval.end) {
            nextIntervals.push({ start: cutEnd, end: interval.end });
          }
        });

        intervals = nextIntervals;
      });
    }

    const slices: Array<{ at: Date; hours: number }> = [];
    intervals.forEach((interval) => {
      let cursor = interval.start;
      while (cursor < interval.end) {
        const nextDay = new Date(
          cursor.getFullYear(),
          cursor.getMonth(),
          cursor.getDate() + 1,
          0,
          0,
          0,
          0,
        );
        const sliceEnd = nextDay < interval.end ? nextDay : interval.end;
        const hours =
          (sliceEnd.getTime() - cursor.getTime()) / (1000 * 60 * 60);
        if (hours > 0) {
          slices.push({
            at: cursor,
            hours: formatDurationHours(hours),
          });
        }
        cursor = sliceEnd;
      }
    });

    return slices;
  };

  const buildCompletedActivityCostAllocation = useCallback(
    (activities: ActivityInstance[]) => {
      const segments: OvertimeCostSegment[] = [];
      let totalHours = 0;

      activities.forEach((activity) => {
        if (activity.status !== "COMPLETED" || isLeaveActivityInstance(activity)) {
          return;
        }

        const fallbackDate =
          parseDateTimeString(activity.completedAt || undefined) ||
          parseDateTimeString(activity.endDate || undefined) ||
          parseDateTimeString(activity.startDate || undefined) ||
          parseDateTimeString(activity.createdAt || undefined) ||
          null;

        let hasUserDuration = false;
        const durationMap = parseJsonObject(activity.userDurations);

        if (durationMap && typeof durationMap === "object") {
          Object.entries(durationMap).forEach(([rawUserId, entry]) => {
            const userId = Number(rawUserId);
            if (!Number.isFinite(userId)) {
              return;
            }

            const workedSlices = getWorkedSlicesFromUserDurationEntry(entry);
            if (workedSlices.length > 0) {
              hasUserDuration = true;
              workedSlices.forEach((slice) => {
                if (!Number.isFinite(slice.hours) || slice.hours <= 0) {
                  return;
                }
                totalHours += slice.hours;
                segments.push({
                  activityId: activity.id,
                  userId,
                  bucketKey: `user:${userId}`,
                  at: slice.at,
                  hours: slice.hours,
                  rate: resolveApplicableHourlyRate(
                    userId,
                    slice.at,
                    projectFinanceInputs.projectCurrency,
                  ),
                });
              });
              return;
            }

            const duration = getDurationFromUserDurationEntry(entry);
            if (duration <= 0) {
              return;
            }
            hasUserDuration = true;
            totalHours += duration;

            const entryDate =
              typeof entry === "object" && entry
                ? parseDateTimeString((entry as any).endTime) ||
                  parseDateTimeString((entry as any).startTime) ||
                  fallbackDate
                : fallbackDate;

            segments.push({
              activityId: activity.id,
              userId,
              bucketKey: `user:${userId}`,
              at: entryDate,
              hours: duration,
              rate: resolveApplicableHourlyRate(
                userId,
                entryDate,
                projectFinanceInputs.projectCurrency,
              ),
            });
          });
        }

        if (!hasUserDuration) {
          const duration =
            typeof activity.actualDuration === "number" &&
            Number.isFinite(activity.actualDuration) &&
            activity.actualDuration > 0
              ? activity.actualDuration
              : 0;

          if (duration > 0) {
            totalHours += duration;
            const assigneeIds = parseJsonArray(activity.assignedUsers)
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value));
            const singleAssigneeId =
              assigneeIds.length === 1 ? assigneeIds[0] : null;

            segments.push({
              activityId: activity.id,
              userId: singleAssigneeId,
              bucketKey:
                typeof singleAssigneeId === "number"
                  ? `user:${singleAssigneeId}`
                  : `activity:${activity.id}`,
              at: fallbackDate,
              hours: duration,
              rate: resolveApplicableHourlyRate(
                singleAssigneeId,
                fallbackDate,
                projectFinanceInputs.projectCurrency,
              ),
            });
          }
        }
      });

      const allocation = allocateOvertimeCosts({
        segments,
        overtimeSettings,
      });

      const costByActivityId = new Map<number, number>();
      allocation.costByActivityId.forEach((value, activityId) => {
        costByActivityId.set(activityId, Number.isFinite(value) ? value : 0);
      });

      return {
        totalHours: formatDurationHours(totalHours),
        totalCost: Number.isFinite(allocation.totalCost)
          ? allocation.totalCost
          : 0,
        costByActivityId,
      };
    },
    [
      getDurationFromUserDurationEntry,
      getWorkedSlicesFromUserDurationEntry,
      isLeaveActivityInstance,
      overtimeSettings,
      projectFinanceInputs.projectCurrency,
      resolveApplicableHourlyRate,
    ],
  );

  const buildCompletedActivityCostMetrics = useCallback(
    (activities: ActivityInstance[]) => {
      const { totalHours, totalCost } =
        buildCompletedActivityCostAllocation(activities);
      return {
        totalHours,
        totalCost,
      };
    },
    [buildCompletedActivityCostAllocation],
  );

  const completedActivityCostByActivityId = useMemo(() => {
    if (!canViewCompletedActivityCosts) {
      return new Map<number, number>();
    }

    return buildCompletedActivityCostAllocation(fullyCompletedActivityItems)
      .costByActivityId;
  }, [
    buildCompletedActivityCostAllocation,
    canViewCompletedActivityCosts,
    fullyCompletedActivityItems,
  ]);

  const calculatedProjectCostSummary = useMemo<ProjectCostSummaryValue>(() => {
    const plannedHoursRaw =
      workflowInstance?.workflowContainerFormData?.estimatedDurationHours ??
      workflowInstance?.workflowContainerFormData?.estimatedDurationDays ??
      0;
    const plannedHours =
      typeof plannedHoursRaw === "string"
        ? Number.parseFloat(plannedHoursRaw)
        : Number(plannedHoursRaw ?? 0);
    const spentHours = Number(workflowInstance?.totalSpentHours ?? 0);

    if (!canViewFinancials || isVisitorUser) {
      return {
        plannedHours: Number.isFinite(plannedHours) ? plannedHours : 0,
        spentHours: Number.isFinite(spentHours) ? spentHours : 0,
        realizedCost: 0,
        averageHourlyRate: null as number | null,
        remainingEstimatedCost: null as number | null,
        totalEstimatedCost: null as number | null,
      };
    }

    const projectCurrency = projectFinanceInputs.projectCurrency;
    const completedCostMetrics = buildCompletedActivityCostMetrics(
      activityInstances,
    );
    const realizedCost = completedCostMetrics.totalCost;

    const averageHourlyRate = getApplicableAverageRate(new Date(), projectCurrency);
    const safePlannedHours = Number.isFinite(plannedHours) ? plannedHours : 0;
    const safeSpentHours = Number.isFinite(spentHours) ? spentHours : 0;
    const remainingHours =
      safePlannedHours > 0 ? Math.max(safePlannedHours - safeSpentHours, 0) : 0;
    const remainingEstimatedCost =
      averageHourlyRate !== null ? remainingHours * averageHourlyRate : null;
    const totalEstimatedCost =
      remainingEstimatedCost !== null
        ? realizedCost + remainingEstimatedCost
        : null;

    return {
      plannedHours: safePlannedHours,
      spentHours: safeSpentHours,
      realizedCost: Number.isFinite(realizedCost) ? realizedCost : 0,
      averageHourlyRate,
      remainingEstimatedCost,
      totalEstimatedCost,
    };
  }, [
    activityInstances,
    buildCompletedActivityCostMetrics,
    canViewFinancials,
    isVisitorUser,
    projectFinanceInputs.projectCurrency,
    sortedAverageRates,
    userRatesByUserId,
    workflowInstance,
  ]);

  const calculatedCompletedActivityInvoiceSummary =
    useMemo<CompletedActivityInvoiceSummaryValue>(() => {
    if (!canViewActivityInvoiceStatus) {
      return {
        invoiced: { totalHours: 0, totalCost: 0 },
        notInvoiced: { totalHours: 0, totalCost: 0 },
        total: { totalHours: 0, totalCost: 0 },
      };
    }

    const invoicedActivities = completedActivityItems.filter((activity) =>
      Boolean(activity.isInvoiced),
    );
    const notInvoicedActivities = completedActivityItems.filter(
      (activity) => !activity.isInvoiced,
    );

    return {
      invoiced: buildCompletedActivityCostMetrics(invoicedActivities),
      notInvoiced: buildCompletedActivityCostMetrics(notInvoicedActivities),
      total: buildCompletedActivityCostMetrics(completedActivityItems),
    };
    }, [
      buildCompletedActivityCostMetrics,
      canViewActivityInvoiceStatus,
      completedActivityItems,
    ]);

  const projectCollectionsSummary = useMemo(() => {
    if (!canViewCollectionDetails) {
      return {
        totalCollected: null as number | null,
      };
    }

    const projectCurrency = projectFinanceInputs.projectCurrency;
    let totalCollected = 0;
    let hasValue = false;

    projectCollections.forEach((collection) => {
      const converted = convertRateAmountToCurrency(
        collection.amount,
        normalizeCurrencyCode(collection.currency),
        projectCurrency,
        parseHistoricalDateCandidate(collection.collectionDate),
      );

      if (converted !== null && Number.isFinite(converted)) {
        totalCollected += converted;
        hasValue = true;
      }
    });

    return {
      totalCollected: hasValue ? totalCollected : 0,
    };
  }, [
    canViewCollectionDetails,
    convertRateAmountToCurrency,
    projectCollections,
    projectFinanceInputs.projectCurrency,
  ]);

  const calculatedProjectFinanceSummary = useMemo<ProjectFinanceSummaryValue>(() => {
    const { vatRate, corporateTaxRate } = getProjectTaxSettings(companySettings);

    return calculateProjectFinanceSummary({
      ...projectFinanceInputs,
      vatRate,
      corporateTaxRate,
      realizedCost: calculatedProjectCostSummary.realizedCost,
      totalEstimatedCost: calculatedProjectCostSummary.totalEstimatedCost,
      totalCollected: projectCollectionsSummary.totalCollected,
    });
  }, [
    companySettings,
    projectFinanceInputs,
    calculatedProjectCostSummary.realizedCost,
    calculatedProjectCostSummary.totalEstimatedCost,
    projectCollectionsSummary.totalCollected,
  ]);

  const projectCostSummary = useMemo<ProjectCostSummaryValue>(() => {
    const snapshotCostSummary = archivedFinancialSnapshot?.projectCostSummary;
    if (!isArchivedProject || !snapshotCostSummary) {
      return calculatedProjectCostSummary;
    }

    return {
      plannedHours: toFiniteNumberOrFallback(
        snapshotCostSummary.plannedHours,
        calculatedProjectCostSummary.plannedHours,
      ),
      spentHours: toFiniteNumberOrFallback(
        snapshotCostSummary.spentHours,
        calculatedProjectCostSummary.spentHours,
      ),
      realizedCost: toFiniteNumberOrFallback(
        snapshotCostSummary.realizedCost,
        calculatedProjectCostSummary.realizedCost,
      ),
      averageHourlyRate: toNullableFiniteNumberOrFallback(
        snapshotCostSummary.averageHourlyRate,
        calculatedProjectCostSummary.averageHourlyRate,
      ),
      remainingEstimatedCost: toNullableFiniteNumberOrFallback(
        snapshotCostSummary.remainingEstimatedCost,
        calculatedProjectCostSummary.remainingEstimatedCost,
      ),
      totalEstimatedCost: toNullableFiniteNumberOrFallback(
        snapshotCostSummary.totalEstimatedCost,
        calculatedProjectCostSummary.totalEstimatedCost,
      ),
    };
  }, [
    archivedFinancialSnapshot,
    calculatedProjectCostSummary,
    isArchivedProject,
  ]);

  const projectFinanceSummary = useMemo<ProjectFinanceSummaryValue>(() => {
    const snapshotFinanceSummary = archivedFinancialSnapshot?.projectFinanceSummary;
    if (!isArchivedProject || !snapshotFinanceSummary) {
      return calculatedProjectFinanceSummary;
    }

    return {
      ...calculatedProjectFinanceSummary,
      projectAmount: toNullableFiniteNumberOrFallback(
        snapshotFinanceSummary.projectAmount,
        calculatedProjectFinanceSummary.projectAmount,
      ),
      projectCurrency:
        typeof snapshotFinanceSummary.projectCurrency === "string" &&
        snapshotFinanceSummary.projectCurrency.trim()
          ? snapshotFinanceSummary.projectCurrency.trim().toUpperCase()
          : calculatedProjectFinanceSummary.projectCurrency,
      invoiceEnabled: toBooleanOrFallback(
        snapshotFinanceSummary.invoiceEnabled,
        calculatedProjectFinanceSummary.invoiceEnabled,
      ),
      vatRate: toFiniteNumberOrFallback(
        snapshotFinanceSummary.vatRate,
        calculatedProjectFinanceSummary.vatRate,
      ),
      corporateTaxRate: toFiniteNumberOrFallback(
        snapshotFinanceSummary.corporateTaxRate,
        calculatedProjectFinanceSummary.corporateTaxRate,
      ),
      invoiceTotal: toNullableFiniteNumberOrFallback(
        snapshotFinanceSummary.invoiceTotal,
        calculatedProjectFinanceSummary.invoiceTotal,
      ),
      totalCollected: toNullableFiniteNumberOrFallback(
        snapshotFinanceSummary.totalCollected,
        calculatedProjectFinanceSummary.totalCollected,
      ),
      remainingCollection: toNullableFiniteNumberOrFallback(
        snapshotFinanceSummary.remainingCollection,
        calculatedProjectFinanceSummary.remainingCollection,
      ),
      isCurrencySupported: toBooleanOrFallback(
        snapshotFinanceSummary.isCurrencySupported,
        calculatedProjectFinanceSummary.isCurrencySupported,
      ),
      grossProfit: toNullableFiniteNumberOrFallback(
        snapshotFinanceSummary.grossProfit,
        calculatedProjectFinanceSummary.grossProfit,
      ),
      netProfit: toNullableFiniteNumberOrFallback(
        snapshotFinanceSummary.netProfit,
        calculatedProjectFinanceSummary.netProfit,
      ),
      estimatedGrossProfit: toNullableFiniteNumberOrFallback(
        snapshotFinanceSummary.estimatedGrossProfit,
        calculatedProjectFinanceSummary.estimatedGrossProfit,
      ),
      estimatedNetProfit: toNullableFiniteNumberOrFallback(
        snapshotFinanceSummary.estimatedNetProfit,
        calculatedProjectFinanceSummary.estimatedNetProfit,
      ),
    };
  }, [
    archivedFinancialSnapshot,
    calculatedProjectFinanceSummary,
    isArchivedProject,
  ]);

  const completedActivityInvoiceSummary =
    useMemo<CompletedActivityInvoiceSummaryValue>(() => {
      const snapshotSummary =
        archivedFinancialSnapshot?.completedActivityInvoiceSummary;
      if (!isArchivedProject || !snapshotSummary) {
        return calculatedCompletedActivityInvoiceSummary;
      }

      const resolveBucket = (
        bucket: Partial<CompletedActivityCostSummaryValue> | null | undefined,
        fallback: CompletedActivityCostSummaryValue,
      ): CompletedActivityCostSummaryValue => ({
        totalHours: toFiniteNumberOrFallback(bucket?.totalHours, fallback.totalHours),
        totalCost: toFiniteNumberOrFallback(bucket?.totalCost, fallback.totalCost),
      });

      return {
        invoiced: resolveBucket(
          snapshotSummary.invoiced,
          calculatedCompletedActivityInvoiceSummary.invoiced,
        ),
        notInvoiced: resolveBucket(
          snapshotSummary.notInvoiced,
          calculatedCompletedActivityInvoiceSummary.notInvoiced,
        ),
        total: resolveBucket(
          snapshotSummary.total,
          calculatedCompletedActivityInvoiceSummary.total,
        ),
      };
    }, [
      archivedFinancialSnapshot,
      calculatedCompletedActivityInvoiceSummary,
      isArchivedProject,
    ]);

  function buildArchivedFinancialSnapshotPayload(): ArchivedProjectFinancialSnapshot | null {
    if (!canViewFinancials || isVisitorUser) {
      return null;
    }

    return {
      snapshotTakenAt: new Date().toISOString(),
      projectCostSummary: {
        plannedHours: calculatedProjectCostSummary.plannedHours,
        spentHours: calculatedProjectCostSummary.spentHours,
        realizedCost: calculatedProjectCostSummary.realizedCost,
        averageHourlyRate: calculatedProjectCostSummary.averageHourlyRate,
        remainingEstimatedCost:
          calculatedProjectCostSummary.remainingEstimatedCost,
        totalEstimatedCost: calculatedProjectCostSummary.totalEstimatedCost,
      },
      projectFinanceSummary: {
        projectAmount: calculatedProjectFinanceSummary.projectAmount,
        projectCurrency: calculatedProjectFinanceSummary.projectCurrency,
        invoiceEnabled: calculatedProjectFinanceSummary.invoiceEnabled,
        vatRate: calculatedProjectFinanceSummary.vatRate,
        corporateTaxRate: calculatedProjectFinanceSummary.corporateTaxRate,
        invoiceTotal: calculatedProjectFinanceSummary.invoiceTotal,
        totalCollected: calculatedProjectFinanceSummary.totalCollected,
        remainingCollection: calculatedProjectFinanceSummary.remainingCollection,
        isCurrencySupported:
          calculatedProjectFinanceSummary.isCurrencySupported,
        grossProfit: calculatedProjectFinanceSummary.grossProfit,
        netProfit: calculatedProjectFinanceSummary.netProfit,
        estimatedGrossProfit:
          calculatedProjectFinanceSummary.estimatedGrossProfit,
        estimatedNetProfit:
          calculatedProjectFinanceSummary.estimatedNetProfit,
      },
      completedActivityInvoiceSummary: {
        invoiced: {
          totalHours: calculatedCompletedActivityInvoiceSummary.invoiced.totalHours,
          totalCost: calculatedCompletedActivityInvoiceSummary.invoiced.totalCost,
        },
        notInvoiced: {
          totalHours:
            calculatedCompletedActivityInvoiceSummary.notInvoiced.totalHours,
          totalCost:
            calculatedCompletedActivityInvoiceSummary.notInvoiced.totalCost,
        },
        total: {
          totalHours: calculatedCompletedActivityInvoiceSummary.total.totalHours,
          totalCost: calculatedCompletedActivityInvoiceSummary.total.totalCost,
        },
      },
    };
  }

  const closeFinanceShortcutModal = useCallback(() => {
    setFinanceShortcutMode(null);
    setFinanceShortcutForm(FINANCE_SHORTCUT_EMPTY_FORM);
    setIsFinanceShortcutDragOver(false);
  }, []);

  const openFinanceShortcutModal = useCallback(
    (mode: FinanceShortcutMode) => {
      if (isArchivedProject) {
        return;
      }

      setFinanceShortcutMode(mode);
      setFinanceShortcutForm({
        amount:
          projectFinanceInputs.projectAmount !== null
            ? String(projectFinanceInputs.projectAmount)
            : "",
        currency: currentOfferShortcutCurrency || "TRY",
        invoiceEnabled: projectFinanceInputs.invoiceEnabled,
        files: [],
      });
    },
    [
      currentOfferShortcutCurrency,
      isArchivedProject,
      projectFinanceInputs.invoiceEnabled,
      projectFinanceInputs.projectAmount,
    ],
  );

  const handleFinanceShortcutFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextFiles = buildQueuedUploadFiles(event.target.files);

      if (nextFiles.length > 0) {
        setFinanceShortcutForm((prev) => ({
          ...prev,
          files: [...prev.files, ...nextFiles],
        }));
      }

      event.target.value = "";
    },
    [],
  );

  const handleFinanceShortcutDragOver = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isSavingFinanceShortcut) {
        setIsFinanceShortcutDragOver(true);
      }
    },
    [isSavingFinanceShortcut],
  );

  const handleFinanceShortcutDragLeave = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsFinanceShortcutDragOver(false);
    },
    [],
  );

  const handleFinanceShortcutDrop = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsFinanceShortcutDragOver(false);
      if (isSavingFinanceShortcut) {
        return;
      }

      const nextFiles = buildQueuedUploadFiles(event.dataTransfer.files);
      if (nextFiles.length > 0) {
        setFinanceShortcutForm((prev) => ({
          ...prev,
          files: [...prev.files, ...nextFiles],
        }));
      }
      event.dataTransfer.clearData();
    },
    [isSavingFinanceShortcut],
  );

  const removeFinanceShortcutFile = useCallback((fileId: string) => {
    setFinanceShortcutForm((prev) => ({
      ...prev,
      files: prev.files.filter((file) => file.id !== fileId),
    }));
  }, []);

  const updateFinanceShortcutFileDescription = useCallback(
    (fileId: string, description: string) => {
      setFinanceShortcutForm((prev) => ({
        ...prev,
        files: prev.files.map((file) =>
          file.id === fileId ? { ...file, description } : file,
        ),
      }));
    },
    [],
  );

  const handleCollectionFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextFiles = buildQueuedUploadFiles(event.target.files);
      if (nextFiles.length > 0) {
        setCollectionForm((prev) => ({
          ...prev,
          files: [...prev.files, ...nextFiles],
        }));
      }

      event.target.value = "";
    },
    [],
  );

  const handleCollectionDragOver = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isSavingCollection) {
        setIsCollectionDragOver(true);
      }
    },
    [isSavingCollection],
  );

  const handleCollectionDragLeave = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsCollectionDragOver(false);
    },
    [],
  );

  const handleCollectionDrop = useCallback(
    (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsCollectionDragOver(false);
      if (isSavingCollection) {
        return;
      }

      const nextFiles = buildQueuedUploadFiles(event.dataTransfer.files);
      if (nextFiles.length > 0) {
        setCollectionForm((prev) => ({
          ...prev,
          files: [...prev.files, ...nextFiles],
        }));
      }
      event.dataTransfer.clearData();
    },
    [isSavingCollection],
  );

  const removeCollectionFile = useCallback((fileId: string) => {
    setCollectionForm((prev) => ({
      ...prev,
      files: prev.files.filter((file) => file.id !== fileId),
    }));
  }, []);

  const updateCollectionFileDescription = useCallback(
    (fileId: string, description: string) => {
      setCollectionForm((prev) => ({
        ...prev,
        files: prev.files.map((file) =>
          file.id === fileId ? { ...file, description } : file,
        ),
      }));
    },
    [],
  );

  const renderQueuedUploadSection = ({
    files,
    isDragActive,
    disabled,
    label,
    hint,
    onFileChange,
    onDragOver,
    onDragLeave,
    onDrop,
    onRemoveFile,
    onUpdateFileDescription,
  }: {
    files: QueuedUploadFile[];
    isDragActive: boolean;
    disabled: boolean;
    label: string;
    hint: string;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDragOver: (event: React.DragEvent<HTMLElement>) => void;
    onDragLeave: (event: React.DragEvent<HTMLElement>) => void;
    onDrop: (event: React.DragEvent<HTMLElement>) => void;
    onRemoveFile: (fileId: string) => void;
    onUpdateFileDescription: (fileId: string, description: string) => void;
  }) => (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-slate-700">{label}</label>
        <p className="text-xs text-slate-500 mt-1">{hint}</p>
      </div>

      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed transition-colors ${
          isDragActive
            ? "border-blue-400 bg-blue-50"
            : "border-slate-300 bg-slate-50/70"
        } ${disabled ? "opacity-60" : ""}`}
      >
        <label className="flex cursor-pointer flex-col items-center justify-center px-6 py-8 text-center">
          <CloudArrowUpIcon className="mb-3 h-9 w-9 text-blue-500" />
          <span className="text-sm font-semibold text-slate-700">
            {t("dashboard:clickToUpload")}
          </span>
          <span className="mt-1 text-xs text-slate-500">
            {t(
              "projects:workflowInstanceDetail.financeShortcut.dropHint",
              "Dosyaları sürükleyip bırakabilir veya seçebilirsiniz.",
            )}
          </span>
          <input
            type="file"
            multiple
            onChange={onFileChange}
            disabled={disabled}
            className="hidden"
          />
        </label>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-700">
            {t("dashboard:selectedFiles")} ({files.length})
          </h4>
          <div className="max-h-52 space-y-2 overflow-y-auto">
            {files.map((queuedFile) => (
              <div
                key={queuedFile.id}
                className="rounded-lg border border-slate-200 bg-white p-3 space-y-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <DocumentTextIcon className="h-5 w-5 flex-shrink-0 text-blue-500" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800">
                        {queuedFile.file.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {(queuedFile.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemoveFile(queuedFile.id)}
                    disabled={disabled}
                    className="rounded-md p-1 text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-700 disabled:opacity-60"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
                <input
                  type="text"
                  value={queuedFile.description}
                  onChange={(event) =>
                    onUpdateFileDescription(queuedFile.id, event.target.value)
                  }
                  placeholder={t(
                    "dashboard:uploadProjectFiles.optionalDescription",
                  )}
                  disabled={disabled}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const loadExistingFinanceShortcutFiles = useCallback(
    async (targetInstanceId: number) => {
      const rootFiles = await apiService.getWorkflowInstanceFiles(targetInstanceId);
      const configs: Array<{
        mode: FinanceShortcutMode;
        category: string;
        folderName: string;
      }> = [
        {
          mode: "offer",
          category: OFFER_SHORTCUT_CATEGORY,
          folderName: OFFER_SHORTCUT_SUBFOLDER,
        },
        {
          mode: "invoice",
          category: INVOICE_SHORTCUT_CATEGORY,
          folderName: INVOICE_SHORTCUT_SUBFOLDER,
        },
      ];

      const nextState: ExistingFinanceShortcutFilesState = {
        offer: [],
        invoice: [],
      };

      for (const config of configs) {
        const normalizedCategory = normalizeFileLookupText(config.category);
        const normalizedFolderName = normalizeFileLookupText(config.folderName);

        const rootCategoryFiles = rootFiles.filter(
          (file) =>
            !isDriveFolderAttachment(file) &&
            normalizeFileLookupText(file.category || "") === normalizedCategory,
        );

        const matchingFolder = rootFiles.find(
          (file) =>
            isDriveFolderAttachment(file) &&
            normalizeFileLookupText(file.name || "") === normalizedFolderName,
        );

        let folderFiles: GoogleDriveAttachment[] = [];
        if (matchingFolder?.id) {
          const children = await apiService.getWorkflowInstanceFiles(
            targetInstanceId,
            matchingFolder.id,
          );
          folderFiles = children.filter(
            (file) =>
              !isDriveFolderAttachment(file) &&
              (!file.category ||
                normalizeFileLookupText(file.category) === normalizedCategory),
          );
        }

        nextState[config.mode] = Array.from(
          new Map(
            [...rootCategoryFiles, ...folderFiles].map((file) => [file.id, file]),
          ).values(),
        ).sort((a, b) => {
          const aTime = new Date(a.uploadedAt || a.addedAt || 0).getTime();
          const bTime = new Date(b.uploadedAt || b.addedAt || 0).getTime();
          return bTime - aTime;
        });
      }

      return nextState;
    },
    [],
  );

  const handleSaveFinanceShortcut = useCallback(async () => {
    if (
      !instanceId ||
      !workflowInstance ||
      !financeShortcutMode ||
      isArchivedProject
    ) {
      return;
    }

    const safeFormData =
      workflowInstance.workflowContainerFormData &&
      typeof workflowInstance.workflowContainerFormData === "object" &&
      !Array.isArray(workflowInstance.workflowContainerFormData)
        ? { ...workflowInstance.workflowContainerFormData }
        : {};

    const normalizedCurrency = normalizeCurrencyCode(
      financeShortcutForm.currency || currentOfferShortcutCurrency || "TRY",
    );

    if (financeShortcutMode === "offer") {
      const parsedAmount = parseShortcutDecimalInputValue(
        financeShortcutForm.amount,
      );

      if (parsedAmount === null || parsedAmount < 0) {
        toast.error(
          t(
            "projects:workflowInstanceDetail.financeShortcut.offerRequiredError",
            "İş teklifi tutarı zorunludur.",
          ),
        );
        return;
      }

      if (offerAmountElement) {
        safeFormData[offerAmountElement.id] = parsedAmount;
      } else {
        safeFormData.offer_amount = parsedAmount;
      }

      if (safeFormData.offer_amount !== undefined) {
        safeFormData.offer_amount = parsedAmount;
      }
      if (safeFormData.offerAmount !== undefined) {
        safeFormData.offerAmount = parsedAmount;
      }

      const nextElementCurrencies = {
        ...extractFinanceElementCurrencies(safeFormData),
      };
      if (offerAmountElement) {
        nextElementCurrencies[offerAmountElement.id] = normalizedCurrency;
      } else {
        nextElementCurrencies.offer_amount = normalizedCurrency;
      }
      if (nextElementCurrencies.offer_amount !== undefined) {
        nextElementCurrencies.offer_amount = normalizedCurrency;
      }
      if (nextElementCurrencies.offerAmount !== undefined) {
        nextElementCurrencies.offerAmount = normalizedCurrency;
      }

      safeFormData.elementCurrencies = nextElementCurrencies;
      if (
        safeFormData.element_currencies &&
        typeof safeFormData.element_currencies === "object" &&
        !Array.isArray(safeFormData.element_currencies)
      ) {
        safeFormData.element_currencies = nextElementCurrencies;
      }
      safeFormData.projectCurrency = normalizedCurrency;
    } else {
      const nextInvoiceValue = financeShortcutForm.invoiceEnabled
        ? INVOICE_SHORTCUT_YES
        : INVOICE_SHORTCUT_NO;

      if (invoiceElement) {
        safeFormData[invoiceElement.id] = nextInvoiceValue;
      } else {
        safeFormData.invoice = nextInvoiceValue;
      }

      if (safeFormData.invoice !== undefined) {
        safeFormData.invoice = nextInvoiceValue;
      }
      if (safeFormData.fatura !== undefined) {
        safeFormData.fatura = nextInvoiceValue;
      }
    }

    let uploadFailed = false;

    try {
      setIsSavingFinanceShortcut(true);

      await apiService.updateWorkflowInstanceFormData(
        instanceId,
        safeFormData,
        workflowInstance.projectMembers,
      );

      const shouldUploadFiles =
        financeShortcutMode === "offer" || financeShortcutForm.invoiceEnabled;

      if (shouldUploadFiles && financeShortcutForm.files.length > 0) {
        for (const queuedFile of financeShortcutForm.files) {
          try {
            await apiService.uploadFileToProject(
              instanceId,
              queuedFile.file,
              queuedFile.description.trim() || undefined,
              financeShortcutMode === "offer"
                ? OFFER_SHORTCUT_SUBFOLDER
                : INVOICE_SHORTCUT_SUBFOLDER,
              financeShortcutMode === "offer"
                ? OFFER_SHORTCUT_CATEGORY
                : INVOICE_SHORTCUT_CATEGORY,
            );
          } catch (error) {
            uploadFailed = true;
          }
        }
      }

      await reloadWorkflowInstanceSummary(instanceId);

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(NAV_REFRESH_EVENT, {
            detail: { projectInstances: true },
          }),
        );
      }

      closeFinanceShortcutModal();

      if (uploadFailed) {
        toast.error(
          t(
            "projects:workflowInstanceDetail.financeShortcut.partialUploadWarning",
            "Bilgi güncellendi ancak bazı dosyalar yüklenemedi.",
          ),
        );
      } else {
        toast.success(
          t(
            "projects:workflowInstanceDetail.financeShortcut.saved",
            "Finansal bilgi güncellendi.",
          ),
        );
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          t(
            "projects:workflowInstanceDetail.financeShortcut.saveFailed",
            "Finansal bilgi güncellenirken hata oluştu.",
          ),
      );
    } finally {
      setIsSavingFinanceShortcut(false);
    }
  }, [
    closeFinanceShortcutModal,
    currentOfferShortcutCurrency,
    financeShortcutForm,
    financeShortcutMode,
    instanceId,
    invoiceElement,
    isArchivedProject,
    offerAmountElement,
    reloadWorkflowInstanceSummary,
    t,
    workflowInstance,
  ]);

  useEffect(() => {
    if (!financeShortcutMode || !instanceId) {
      setIsExistingFinanceShortcutFilesLoading(false);
      return;
    }

    let isCancelled = false;

    const loadFiles = async () => {
      try {
        setIsExistingFinanceShortcutFilesLoading(true);
        const nextFiles = await loadExistingFinanceShortcutFiles(instanceId);
        if (!isCancelled) {
          setExistingFinanceShortcutFiles(nextFiles);
        }
      } catch (error) {
        if (!isCancelled) {
          setExistingFinanceShortcutFiles({ offer: [], invoice: [] });
        }
      } finally {
        if (!isCancelled) {
          setIsExistingFinanceShortcutFilesLoading(false);
        }
      }
    };

    void loadFiles();

    return () => {
      isCancelled = true;
    };
  }, [financeShortcutMode, instanceId, loadExistingFinanceShortcutFiles]);

  const openCollectionModal = useCallback(() => {
    if (isArchivedProject) {
      return;
    }

    setIsCollectionDragOver(false);
    setCollectionForm({
      collectionDate: formatDateForInput(new Date()),
      amount: "",
      currency: projectFinanceSummary.projectCurrency || "TRY",
      description: "",
      attachments: [],
      files: [],
    });
    setShowCollectionModal(true);
  }, [isArchivedProject, projectFinanceSummary.projectCurrency]);

  const handleSaveCollection = useCallback(async () => {
    if (!id || isArchivedProject) {
      return;
    }

    const amount = Number.parseFloat(collectionForm.amount.replace(",", "."));
    if (
      !collectionForm.collectionDate ||
      !Number.isFinite(amount) ||
      amount < 0
    ) {
      toast.error(t("projects:workflowInstanceDetail.collections.requiredError"));
      return;
    }

    try {
      setIsSavingCollection(true);

      const createdCollection = await apiService.createProjectCollection(Number(id), {
        collectionDate: collectionForm.collectionDate,
        amount,
        currency: collectionForm.currency || "TRY",
        description: collectionForm.description.trim(),
        attachments: [],
      });

      let uploadedAttachments: GoogleDriveAttachment[] = [];
      if (collectionForm.files.length > 0) {
        uploadedAttachments = await Promise.all(
          collectionForm.files.map((queuedFile) =>
            apiService.uploadFileToProject(
              Number(id),
              queuedFile.file,
              queuedFile.description.trim() ||
                collectionForm.description.trim() ||
                undefined,
              "Tahsilatlar",
              "collection",
            ),
          ),
        );

        await apiService.updateProjectCollection(Number(id), createdCollection.id, {
          collectionDate: collectionForm.collectionDate,
          amount,
          currency: collectionForm.currency || "TRY",
          description: collectionForm.description.trim(),
          attachments: uploadedAttachments,
        });
      }

      setProjectCollections((prev) => [
        {
          ...createdCollection,
          attachments: uploadedAttachments,
        } as ProjectCollection,
        ...prev,
      ]);
      setShowCollectionModal(false);
      setIsCollectionDragOver(false);
      setCollectionForm({
        collectionDate: "",
        amount: "",
        currency: projectFinanceSummary.projectCurrency || "TRY",
        description: "",
        attachments: [],
        files: [],
      });
      toast.success(t("projects:workflowInstanceDetail.collections.saved"));
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          t("projects:workflowInstanceDetail.collections.saveFailed"),
      );
    } finally {
      setIsSavingCollection(false);
    }
  }, [collectionForm, id, isArchivedProject, projectFinanceSummary.projectCurrency, t]);

  const handleDeleteCollection = useCallback(
    async (collectionId: number) => {
      if (!id || isArchivedProject) {
        return;
      }

      try {
        setDeletingCollectionId(collectionId);
        await apiService.deleteProjectCollection(Number(id), collectionId);
        setProjectCollections((prev) =>
          prev.filter((collection) => collection.id !== collectionId),
        );
        toast.success(t("projects:workflowInstanceDetail.collections.deleted"));
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            t("projects:workflowInstanceDetail.collections.deleteFailed"),
        );
      } finally {
        setDeletingCollectionId(null);
      }
    },
    [id, isArchivedProject, t],
  );

  const getAssignedUserNames = (activity: ActivityInstance): string => {
    const ids = parseJsonArray(activity.assignedUsers);
    if (ids.length === 0) return "-";
    const names = ids.map((userId: number) => {
      const member = projectMembers.find(
        (m) => m.userId === userId || m.user?.id === userId,
      );
      if (member?.user) {
        const firstName = member.user.firstName ?? "";
        const lastName = member.user.lastName ?? "";
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || member.user.username || `User ${userId}`;
      }
      const user = allUsers.find((u) => u.id === userId);
      if (user) {
        const fullName =
          `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
        return fullName || user.username || `User ${userId}`;
      }
      return `User ${userId}`;
    });
    return names.join(", ");
  };

  const getDurationLabel = (activity: ActivityInstance): string => {
    const timeInfo = getUserTimeInfo(activity.userDurations);
    if (timeInfo && timeInfo.duration > 0) {
      return `${timeInfo.duration.toFixed(2)} ${t("dashboard:hours")}`;
    }
    if (activity.actualDuration) {
      return `${activity.actualDuration} ${t("dashboard:hours")}`;
    }
    if (activity.estimatedDuration) {
      return `${activity.estimatedDuration} ${t("dashboard:hours")}`;
    }
    return "-";
  };

  const getAttachments = (activity: ActivityInstance): any[] =>
    parseJsonArray(activity.attachments).filter(Boolean);

  const getSubmitDescriptionPreview = (activity: ActivityInstance): string => {
    const raw = activity.submitDescription;
    if (!raw || !raw.trim()) return "-";
    const trimmed = raw.trim();
    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        const extractDescriptions = (value: any): string[] => {
          if (!value) return [];
          if (Array.isArray(value)) {
            return value.flatMap(extractDescriptions);
          }
          if (typeof value === "string") {
            return value.trim() ? [value.trim()] : [];
          }
          if (typeof value === "object") {
            const candidates = [
              value.description,
              value.submitDescription,
              value.comment,
              value.note,
            ];
            const found = candidates.find(
              (item) => typeof item === "string" && item.trim(),
            );
            return found ? [found.trim()] : [];
          }
          return [];
        };

        const descriptions = extractDescriptions(parsed);
        if (descriptions.length > 0) {
          const preview = descriptions.slice(0, 2).join(" / ");
          return descriptions.length > 2
            ? `${preview} +${descriptions.length - 2}`
            : preview;
        }
        return "-";
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  };

  const getFormContent = (
    activity: ActivityInstance,
  ): { summary: string; tooltip: string } => {
    if (activity.formContentSummary || activity.formContentTooltip) {
      return {
        summary: activity.formContentSummary || "-",
        tooltip: activity.formContentTooltip || "",
      };
    }

    const allUserFormData = parseJsonObject(activity.userFormData);
    if (!allUserFormData || typeof allUserFormData !== "object") {
      return { summary: "-", tooltip: "" };
    }

    const completedIds = parseJsonArray(activity.completedUserIds);
    const preferredUserId =
      completedIds.length > 0 ? String(completedIds[0]) : null;
    const userIds = Object.keys(allUserFormData);
    if (userIds.length === 0) return { summary: "-", tooltip: "" };

    const userId =
      preferredUserId && userIds.includes(preferredUserId)
        ? preferredUserId
        : userIds[0];

    const userBlock = allUserFormData[userId];
    if (!userBlock || typeof userBlock !== "object") {
      return { summary: "-", tooltip: "" };
    }

    const formIds = Object.keys(userBlock);
    if (formIds.length === 0) return { summary: "-", tooltip: "" };

    // Pick the first form response with at least 1 filled field.
    let chosenFormId: string | null = null;
    for (const fid of formIds) {
      const resp = userBlock[fid];
      if (resp && typeof resp === "object" && Object.keys(resp).length > 0) {
        chosenFormId = fid;
        break;
      }
    }
    if (!chosenFormId) return { summary: "-", tooltip: "" };

    const response = userBlock[chosenFormId];
    if (!response || typeof response !== "object") {
      return { summary: "-", tooltip: "" };
    }

    const forms = Array.isArray(activity.forms) ? activity.forms : [];
    const formMeta =
      forms.length > 0
        ? forms.find((f) => String(f?.id ?? "") === String(chosenFormId)) ||
          (forms.length === 1 ? forms[0] : null) ||
          forms[0]
        : null;

    const parseFormStructure = (data?: string | object) => {
      if (!data) return null;
      if (typeof data === "object") return data as any;
      if (typeof data === "string") {
        try {
          return JSON.parse(data) as any;
        } catch {
          return null;
        }
      }
      return null;
    };

    const isInternalElementLabel = (label: string) => {
      const trimmed = String(label || "").trim();
      // Hide auto-generated/internal labels that leak implementation details into UI.
      return /^Element\b/i.test(trimmed) && /\d{6,}/.test(trimmed);
    };

    const normalizeId = (id: any) => {
      if (id === null || id === undefined) return "";
      return String(id).trim();
    };

    const candidateFieldIds = (rawKey: string): string[] => {
      const key = String(rawKey || "").trim();
      if (!key) return [];

      const ids: string[] = [key];

      // Keys sometimes look like: "Element 1770239265518 luhvod3kt"
      const numeric = key.match(/\d{8,}/)?.[0];
      if (numeric) ids.push(numeric);

      const tokenAfter = key.match(/\d{8,}\s+([A-Za-z0-9_-]+)/)?.[1];
      if (tokenAfter) ids.push(tokenAfter);

      const afterColon = key.split(":").pop();
      if (afterColon && afterColon.trim() && afterColon.trim() !== key) {
        ids.push(afterColon.trim());
      }

      return Array.from(new Set(ids.filter(Boolean)));
    };

    const collectIdsFromStructure = (structure: any, out: Set<string>) => {
      if (!structure) return;
      if (Array.isArray(structure)) {
        structure.forEach((v) => collectIdsFromStructure(v, out));
        return;
      }
      if (typeof structure !== "object") return;

      const id = normalizeId((structure as any).id);
      if (id) out.add(id);

      Object.values(structure).forEach((v) => collectIdsFromStructure(v, out));
    };

    const findLabelInStructure = (
      fieldId: string,
      structure: any,
    ): string | null => {
      if (!structure || typeof structure !== "object") return null;

      // Common shapes (same as ManagerDashboard).
      if (
        (structure as any).fields &&
        Array.isArray((structure as any).fields)
      ) {
        const field = (structure as any).fields.find(
          (f: any) => normalizeId(f?.id) === fieldId,
        );
        const label = field?.label ?? field?.title ?? field?.name;
        if (label) return String(label);
      }
      if (
        (structure as any).elements &&
        Array.isArray((structure as any).elements)
      ) {
        const el = (structure as any).elements.find(
          (e: any) => normalizeId(e?.id) === fieldId,
        );
        const label = el?.label ?? el?.title ?? el?.name;
        if (label) return String(label);
      }

      // Deep scan for other schema layouts (pages/sections/nodes).
      const stack: any[] = [structure];
      while (stack.length > 0) {
        const node = stack.pop();
        if (!node) continue;
        if (Array.isArray(node)) {
          for (let i = 0; i < node.length; i += 1) stack.push(node[i]);
          continue;
        }
        if (typeof node !== "object") continue;

        const nodeId = normalizeId((node as any).id);
        if (nodeId === fieldId) {
          const label =
            (node as any).label ?? (node as any).title ?? (node as any).name;
          if (label) return String(label);
        }

        Object.values(node).forEach((v) => stack.push(v));
      }

      return null;
    };

    let formStructure = parseFormStructure(formMeta?.formStructure);

    // Prefer the structure that best matches the response keys (helps when chosenFormId doesn't match forms[].id).
    if (forms.length > 0) {
      const parsedStructures = forms
        .map((f) => parseFormStructure((f as any)?.formStructure))
        .filter(Boolean) as any[];

      if (parsedStructures.length > 0) {
        const responseKeys = Object.keys(response);
        let bestScore = -1;
        let best = parsedStructures[0];

        for (const st of parsedStructures) {
          const ids = new Set<string>();
          collectIdsFromStructure(st, ids);

          let score = 0;
          for (const key of responseKeys) {
            const cands = candidateFieldIds(key);
            if (cands.some((c) => ids.has(c))) score += 1;
          }

          if (score > bestScore) {
            bestScore = score;
            best = st;
          }
        }

        formStructure = best;
      }
    }

    const getFieldLabel = (fieldId: string): string => {
      const fallback = fieldId
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());

      // Prefer project definition labels when available (matches ManagerDashboard behavior).
      // Prevents leaking internal labels like "Element 123..." in the UI.
      if (
        projectContainer?.formElements &&
        Array.isArray(projectContainer.formElements)
      ) {
        const candidates = candidateFieldIds(fieldId);
        for (const id of candidates) {
          const found = projectContainer.formElements.find((el: any) => {
            const ids = [
              normalizeId(el?.id),
              normalizeId(el?.elementId),
              normalizeId(el?.key),
            ].filter(Boolean);
            return ids.includes(id);
          });
          const label = (found as any)?.label ?? (found as any)?.name;
          if (label && !isInternalElementLabel(String(label))) {
            return String(label);
          }
        }
      }

      if (!formStructure || typeof formStructure !== "object") {
        // Hide internal-looking field ids (e.g. "Element 123...") when we can't resolve a real label.
        return /^Element\s+\d+/i.test(fieldId) ? "" : fallback;
      }

      const candidates = candidateFieldIds(fieldId);
      for (const id of candidates) {
        const label = findLabelInStructure(id, formStructure);
        if (label && !isInternalElementLabel(label)) return String(label);
      }

      // If we still couldn't resolve a label, don't expose internal ids in UI.
      const numericId = fieldId.match(/\d{8,}/)?.[0];
      if (
        /^Element\s+\d+/i.test(fieldId) ||
        (numericId && numericId.length >= 10)
      ) {
        return "";
      }
      return fallback;
    };

    const formatValue = (value: any): string => {
      if (value === null || value === undefined) return "";
      if (typeof value === "string") return value.trim();
      if (typeof value === "number" || typeof value === "boolean")
        return String(value);
      if (Array.isArray(value)) {
        const parts = value
          .map((v) => {
            if (v === null || v === undefined) return "";
            if (typeof v === "string") return v.trim();
            if (typeof v === "number" || typeof v === "boolean")
              return String(v);
            if (typeof v === "object") {
              if ("label" in (v as any)) return String((v as any).label);
              if ("name" in (v as any)) return String((v as any).name);
            }
            try {
              return JSON.stringify(v);
            } catch {
              return "";
            }
          })
          .filter((s) => s);
        return parts.join(", ");
      }
      if (typeof value === "object") {
        if ("label" in value) return String((value as any).label);
        if ("name" in value) return String((value as any).name);
        try {
          const str = JSON.stringify(value);
          return str.length > 120 ? `${str.slice(0, 117)}...` : str;
        } catch {
          return "";
        }
      }
      return String(value);
    };

    const pairs = Object.entries(response)
      .map(([key, rawValue]) => {
        const label = getFieldLabel(key);
        const v = formatValue(rawValue);
        if (!v) return null;
        // Never show internal "Element ..." labels in UI even if they slipped through.
        const safeLabel = isInternalElementLabel(label) ? "" : label;
        return { label: safeLabel, value: v };
      })
      .filter(Boolean) as Array<{ label: string; value: string }>;

    if (pairs.length === 0) {
      return { summary: "-", tooltip: "" };
    }

    const full = pairs.map((p) =>
      p.label ? `${p.label}: ${p.value}` : p.value,
    );
    const summaryParts = full.slice(0, 3);
    const summary =
      full.length > 3
        ? `${summaryParts.join(" | ")}  +${full.length - 3}`
        : summaryParts.join(" | ");
    return { summary, tooltip: full.join("\n") };
  };

  const completedActivityCostColumnLabel = useMemo(() => {
    const translated = t("dashboard:managerActivities.costTl");
    const suffix = getCurrencySuffixLabel(projectFinanceInputs.projectCurrency);
    if (/\([^)]*\)\s*$/.test(translated)) {
      return translated.replace(/\([^)]*\)\s*$/, `(${suffix})`);
    }
    return `${translated} (${suffix})`;
  }, [projectFinanceInputs.projectCurrency, t]);

  const completedActivityMinCostPlaceholder = useMemo(() => {
    const translated = t("dashboard:managerActivities.minCostPlaceholder");
    const suffix = getCurrencySuffixLabel(projectFinanceInputs.projectCurrency);
    const parts = translated.trim().split(/\s+/);
    if (parts.length > 1) {
      return `${parts.slice(0, -1).join(" ")} ${suffix}`;
    }
    return `${translated} ${suffix}`;
  }, [projectFinanceInputs.projectCurrency, t]);

  const completedActivityDefaultColumns = useMemo<
    ActivityTableColumnPreference[]
  >(() => {
    const columns: ActivityTableColumnPreference[] = [
      { id: "rowNo", kind: "default", label: t("dashboard:managerActivities.rowNo") },
      { id: "endDate", kind: "default", label: t("dashboard:managerActivities.endDate") },
      { id: "assignee", kind: "default", label: t("dashboard:managerActivities.employeeName") },
      { id: "startTime", kind: "default", label: t("dashboard:managerActivities.startTime") },
      { id: "endTimeOnly", kind: "default", label: t("dashboard:managerActivities.endTime") },
      { id: "duration", kind: "default", label: `${t("dashboard:managerActivities.duration")} (${t("dashboard:hours")})` },
      { id: "projectName", kind: "default", label: t("dashboard:managerActivities.projectName") },
      { id: "activityTitle", kind: "default", label: t("dashboard:managerActivities.activityTitle") },
      { id: "formContent", kind: "default", label: t("dashboard:managerActivities.formContent") },
      {
        id: "submitDescription",
        kind: "default",
        label: submitDescriptionLabel,
      },
    ];

    if (canViewCompletedActivityCosts) {
      columns.splice(6, 0, {
        id: "cost",
        kind: "default",
        label: completedActivityCostColumnLabel,
      });
    }

    if (includeManagerCommentColumn) {
      columns.push({
        id: "managerComment",
        kind: "default",
        label: t("dashboard:managerActivities.managerComment"),
      });
    }

    if (canViewActivityInvoiceStatus) {
      columns.push({
        id: "invoiceStatus",
        kind: "default",
        label: t("projects:workflowInstanceDetail.invoiceTracking.columnLabel"),
      });
    }

    columns.push({
      id: "details",
      kind: "default",
      label: t("common:details"),
    });

    return columns;
  }, [
    canViewCompletedActivityCosts,
    canViewActivityInvoiceStatus,
    completedActivityCostColumnLabel,
    includeManagerCommentColumn,
    submitDescriptionLabel,
    t,
  ]);

  const storedCompletedActivityTableLayout = useMemo<
    ActivityTableColumnPreference[]
  >(() => {
    const raw =
      companySettings?.activityTableLayouts?.[
        WORKFLOW_DETAIL_ACTIVITY_TABLE_SCREEN_KEY
      ];
    if (!Array.isArray(raw)) {
      return [];
    }

    return dedupeActivityTableColumns(
      raw
        .map((item) => {
          if (!item || typeof item !== "object") {
            return null;
          }

          const id =
            typeof (item as any).id === "string" ? (item as any).id.trim() : "";
          const kind =
            (item as any).kind === "form"
              ? "form"
              : (item as any).kind === "default"
                ? "default"
                : null;
          const label =
            typeof (item as any).label === "string"
              ? sanitizeActivityTableLabel((item as any).label.trim())
              : "";

          if (!id || !kind || !label) {
            return null;
          }

          return {
            id,
            kind,
            label,
          } satisfies ActivityTableColumnPreference;
        })
        .filter((item): item is ActivityTableColumnPreference => item !== null),
    );
  }, [companySettings]);

  const completedActivityAvailableFormColumns = useMemo(
    () =>
      collectFormColumnPreferences(
        activityInstances
          .filter((activity) => activity.status === "COMPLETED")
          .map((activity) => getFormContent(activity).tooltip),
      ),
    [activityInstances],
  );

  const completedActivityVisibleColumns = useMemo<
    ActivityTableColumnPreference[]
  >(() => {
    if (!storedCompletedActivityTableLayout.length) {
      return completedActivityDefaultColumns;
    }

    const defaultMap = new Map(
      completedActivityDefaultColumns.map((column) => [column.id, column]),
    );
    const formMap = new Map(
      completedActivityAvailableFormColumns.map((column) => [
        column.id,
        column,
      ]),
    );

    const resolved = storedCompletedActivityTableLayout
      .map((column) => {
        if (column.kind === "default") {
          return defaultMap.get(column.id) || null;
        }
        return formMap.get(column.id) || null;
      })
      .filter(
        (column): column is ActivityTableColumnPreference => column !== null,
      );

    let nextColumns = resolved.length
      ? dedupeActivityTableColumns(resolved)
      : completedActivityDefaultColumns;

    if (
      canViewCompletedActivityCosts &&
      !nextColumns.some(
        (column) => column.kind === "default" && column.id === "cost",
      )
    ) {
      const projectIndex = nextColumns.findIndex(
        (column) => column.kind === "default" && column.id === "projectName",
      );
      const detailsIndex = nextColumns.findIndex(
        (column) => column.kind === "default" && column.id === "details",
      );
      const costColumn = completedActivityDefaultColumns.find(
        (column) => column.kind === "default" && column.id === "cost",
      );

      if (costColumn) {
        if (projectIndex >= 0) {
          nextColumns = [
            ...nextColumns.slice(0, projectIndex),
            costColumn,
            ...nextColumns.slice(projectIndex),
          ];
        } else if (detailsIndex >= 0) {
          nextColumns = [
            ...nextColumns.slice(0, detailsIndex),
            costColumn,
            ...nextColumns.slice(detailsIndex),
          ];
        } else {
          nextColumns = [...nextColumns, costColumn];
        }
      }
    }

    if (
      canViewActivityInvoiceStatus &&
      !nextColumns.some(
        (column) => column.kind === "default" && column.id === "invoiceStatus",
      )
    ) {
      const detailsIndex = nextColumns.findIndex(
        (column) => column.kind === "default" && column.id === "details",
      );
      const invoiceColumn = completedActivityDefaultColumns.find(
        (column) => column.kind === "default" && column.id === "invoiceStatus",
      );

      if (invoiceColumn) {
        if (detailsIndex >= 0) {
          return [
            ...nextColumns.slice(0, detailsIndex),
            invoiceColumn,
            ...nextColumns.slice(detailsIndex),
          ];
        }

        return [...nextColumns, invoiceColumn];
      }
    }

    return nextColumns;
  }, [
    canViewCompletedActivityCosts,
    canViewActivityInvoiceStatus,
    completedActivityAvailableFormColumns,
    completedActivityDefaultColumns,
    storedCompletedActivityTableLayout,
  ]);

  const completedActivityColumnSortFieldById: Record<
    string,
    CompletedActivitySortField | null
  > = {
    rowNo: null,
    endDate: "endDate",
    assignee: "assignee",
    startTime: "startTime",
    endTimeOnly: "endTime",
    duration: "duration",
    cost: "costAmount",
    projectName: "projectName",
    activityTitle: "activityTitle",
    formContent: null,
    submitDescription: null,
    managerComment: "managerComment",
    invoiceStatus: null,
    details: null,
  };

  const getActivityStartEnd = (
    activity: ActivityInstance,
  ): { start: Date | null; end: Date | null } => {
    const base =
      parseDateTimeString(
        activity.completedAt || activity.createdAt || undefined,
      ) ||
      (activity.completedAt
        ? new Date(activity.completedAt)
        : activity.createdAt
          ? new Date(activity.createdAt)
          : null);

    const toDateTime = (value: any): Date | null => {
      if (!value || typeof value !== "string") return null;
      const trimmed = value.trim();
      const parsed = parseDateTimeString(trimmed);
      if (parsed) return parsed;
      const timeOnlyMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
      if (!timeOnlyMatch || !base) return null;
      const hours = Number(timeOnlyMatch[1]);
      const minutes = Number(timeOnlyMatch[2]);
      if (
        Number.isNaN(hours) ||
        Number.isNaN(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return null;
      }
      return new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate(),
        hours,
        minutes,
        0,
        0,
      );
    };

    const timeInfo = getUserTimeInfo(activity.userDurations);
    const startRaw = timeInfo?.startTime
      ? toDateTime(timeInfo.startTime)
      : null;
    const endRaw = timeInfo?.endTime ? toDateTime(timeInfo.endTime) : null;
    if (startRaw && endRaw) {
      const end =
        endRaw <= startRaw
          ? new Date(endRaw.getTime() + 24 * 60 * 60 * 1000)
          : endRaw;
      return { start: startRaw, end };
    }

    const startFallback = parseDateTimeString(
      activity.createdAt || activity.completedAt || undefined,
    );
    const endFallback = parseDateTimeString(
      activity.completedAt || activity.createdAt || undefined,
    );
    return { start: startFallback, end: endFallback };
  };

  const sourceLeaveActivities =
    activityInstances.length > 0 ? activityInstances : completedActivityItemsData;
  const leaveDetailItems = sourceLeaveActivities
    .filter((activity) =>
      isLeaveActivityByDefinitions(activity, activityDefinitions),
    )
    .filter((activity) => activity.status !== "DELETED")
    .sort((a, b) => {
      const aStart = getActivityStartEnd(a).start?.getTime() ?? 0;
      const bStart = getActivityStartEnd(b).start?.getTime() ?? 0;
      return bStart - aStart;
    });

  const leaveDetailTotalDurationHours = leaveDetailItems.reduce(
    (total, activity) =>
      total + (getUserTimeInfo(activity.userDurations)?.duration ?? 0),
    0,
  );

  const getActivityLeaveType = (
    activity: ActivityInstance,
  ): { summary: string; tooltip: string } => {
    const content = getFormContent(activity);
    const targetLabel = normalizeActivityTableAsciiLabel(
      LEAVE_TYPE_FIELD_LABEL,
    );
    const values = parseFormFieldPairs(content.tooltip)
      .filter(
        (pair) => normalizeActivityTableAsciiLabel(pair.label) === targetLabel,
      )
      .map((pair) => pair.value.trim())
      .filter(Boolean);

    const combined = Array.from(new Set(values)).join(" | ");
    if (!combined) {
      return {
        summary: content.summary || "-",
        tooltip: content.tooltip || content.summary || "",
      };
    }

    return {
      summary: combined,
      tooltip: combined,
    };
  };

  const formatTimeValue = (value?: string | null): string => {
    if (!value) return "-";
    const raw = String(value);
    const hasDate = raw.includes("-") || raw.includes("T");
    if (hasDate) {
      const parsed = new Date(raw);
      if (!isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString("tr-TR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    }
    return raw;
  };

  const getActivityStartLabel = (activity: ActivityInstance): string => {
    const timeInfo = getUserTimeInfo(activity.userDurations);
    if (timeInfo?.startTime) {
      return formatTimeValue(timeInfo.startTime);
    }
    return formatTimeValue(activity.startDate);
  };

  const getActivityEndLabel = (activity: ActivityInstance): string => {
    const timeInfo = getUserTimeInfo(activity.userDurations);
    if (timeInfo?.endTime) {
      return formatTimeValue(timeInfo.endTime);
    }
    return formatTimeValue(activity.completedAt || activity.endDate);
  };

  const getCompletedActivityCostValue = useCallback(
    (activity: ActivityInstance): number | null => {
      if (!canViewCompletedActivityCosts || activity.status !== "COMPLETED") {
        return null;
      }

      const cost = completedActivityCostByActivityId.get(activity.id) ?? 0;
      return Number.isFinite(cost) ? cost : 0;
    },
    [canViewCompletedActivityCosts, completedActivityCostByActivityId],
  );

  // Helper function to translate role names (snake_case to camelCase to translation)
  const getRoleLabel = (role: string): string => {
    // Convert snake_case to camelCase (e.g., "team_member" → "teamMember")
    const camelCaseRole = role.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );

    // Try to get translation, fallback to original if not found
    const translationKey = `projects:roles.${camelCaseRole}`;
    const translated = t(translationKey);

    // If translation key is returned as-is, it means no translation exists
    return translated !== translationKey ? translated : role;
  };

  const getUserRoleLabel = (role?: UserRole | string | null): string => {
    if (!role) {
      return t("projects:roles.teamMember");
    }

    const camelCaseRole = String(role)
      .toLocaleLowerCase("en-US")
      .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    const translationKey = `common:roles.${camelCaseRole}`;
    const translated = t(translationKey);
    return translated !== translationKey ? translated : String(role);
  };

  const getProjectMemberDisplayRole = (
    member: WorkflowContainerMember,
  ): string => {
    if (member.user?.role) {
      return getUserRoleLabel(member.user.role);
    }
    return getRoleLabel(member.role);
  };

  const clearCompletedActivityRowFilters = () => {
    setCompletedActivityRowFilters(INITIAL_COMPLETED_ACTIVITY_ROW_FILTERS);
    setCompletedActivityInvoiceFilter("all");
    setCompletedActivityFormColumnFilters({});
  };

  const handleToggleCompletedActivityInvoiceStatus = async (
    activity: ActivityInstance,
  ) => {
    if (isArchivedProject) {
      return;
    }

    const nextIsInvoiced = !Boolean(activity.isInvoiced);

    try {
      setInvoiceStatusUpdatingId(activity.id);
      await apiService.updateActivityInvoiceStatus(activity.id, nextIsInvoiced);

      setCompletedActivityItemsData((prev) =>
        prev.map((item) =>
          item.id === activity.id ? { ...item, isInvoiced: nextIsInvoiced } : item,
        ),
      );
      setActivityInstances((prev) =>
        prev.map((item) =>
          item.id === activity.id ? { ...item, isInvoiced: nextIsInvoiced } : item,
        ),
      );

      toast.success(
        nextIsInvoiced
          ? t("projects:workflowInstanceDetail.invoiceTracking.saved")
          : t("projects:workflowInstanceDetail.invoiceTracking.removed"),
      );
    } catch (error) {
      toast.error(
        t("projects:workflowInstanceDetail.invoiceTracking.saveFailed"),
      );
    } finally {
      setInvoiceStatusUpdatingId(null);
    }
  };

  const persistCompletedActivityTableLayout = async (
    columns: ActivityTableColumnPreference[],
  ) => {
    try {
      setIsSavingCompletedTableLayout(true);
      const response = await apiService.updateActivityTableLayout(
        WORKFLOW_DETAIL_ACTIVITY_TABLE_SCREEN_KEY,
        columns,
      );
      const settings = (response?.data || response) as Record<string, any>;
      applyCompanySettings(settings);
      setIsCompletedTableConfiguratorOpen(false);
      toast.success(t("dashboard:managerActivities.layoutSaved"));
    } catch (error) {
      toast.error(t("errors:generic"));
    } finally {
      setIsSavingCompletedTableLayout(false);
    }
  };

  const renderCompletedActivityFilterCell = (
    column: ActivityTableColumnPreference,
  ) => {
    const isNumericDefaultColumn =
      column.kind === "default" &&
      (column.id === "duration" || column.id === "cost");

    if (column.kind === "form") {
      return (
        <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
          <input
            type="text"
            value={completedActivityFormColumnFilters[column.id] || ""}
            onChange={(e) =>
              setCompletedActivityFormColumnFilters((prev) => ({
                ...prev,
                [column.id]: e.target.value,
              }))
            }
            placeholder={column.label}
            className="w-full min-w-[140px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        </th>
      );
    }

    switch (column.id) {
      case "rowNo":
        return (
          <th
            key={`completed-filter-${column.id}`}
            className="w-[88px] min-w-[88px] max-w-[88px] px-2 py-1.5 sticky left-0 z-40 bg-purple-50/95 border-r border-purple-200"
          >
            <button
              type="button"
              onClick={clearCompletedActivityRowFilters}
              className="w-full px-2 py-1 text-[10px] font-semibold rounded border border-purple-300 text-purple-700 hover:bg-purple-100"
            >
              {t("common:clear")}
            </button>
          </th>
        );
      case "endDate":
        return (
          <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="date"
              value={completedActivityRowFilters.date}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  date: e.target.value,
                }))
              }
              className="w-full min-w-[130px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "assignee":
        return (
          <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={completedActivityRowFilters.assignee}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  assignee: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.employee")}
              className="w-full min-w-[130px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "startTime":
        return (
          <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="time"
              value={completedActivityRowFilters.startTime}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  startTime: e.target.value,
                }))
              }
              className="w-full min-w-[95px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "endTimeOnly":
        return (
          <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="time"
              value={completedActivityRowFilters.endTime}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  endTime: e.target.value,
                }))
              }
              className="w-full min-w-[95px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "duration":
        return (
          <th
            key={`completed-filter-${column.id}`}
            className="px-3 py-1.5 text-right"
          >
            <input
              type="number"
              min="0"
              step="0.25"
              value={completedActivityRowFilters.minDurationHours}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  minDurationHours: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.minHoursPlaceholder")}
              className="w-full min-w-[90px] rounded border border-purple-200 px-2 py-1 text-[11px] text-right focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "cost":
        return (
          <th
            key={`completed-filter-${column.id}`}
            className="px-3 py-1.5 text-right"
          >
            <input
              type="number"
              min="0"
              step="0.01"
              value={completedActivityRowFilters.minCost}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  minCost: e.target.value,
                }))
              }
              placeholder={completedActivityMinCostPlaceholder}
              className="w-full min-w-[90px] rounded border border-purple-200 px-2 py-1 text-[11px] text-right focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "projectName":
        return (
          <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={completedActivityRowFilters.projectName}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  projectName: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.projectName")}
              className="w-full min-w-[130px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "activityTitle":
        return (
          <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={completedActivityRowFilters.activityTitle}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  activityTitle: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.activity")}
              className="w-full min-w-[130px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "formContent":
        return (
          <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={completedActivityRowFilters.formContent}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  formContent: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.formContent")}
              className="w-full min-w-[160px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "submitDescription":
        return (
          <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={completedActivityRowFilters.description}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder={submitDescriptionLabel}
              className="w-full min-w-[130px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      case "managerComment":
        return (
          <th key={`completed-filter-${column.id}`} className="px-3 py-1.5">
            <input
              type="text"
              value={completedActivityRowFilters.managerComment}
              onChange={(e) =>
                setCompletedActivityRowFilters((prev) => ({
                  ...prev,
                  managerComment: e.target.value,
                }))
              }
              placeholder={t("dashboard:managerActivities.managerComment")}
              className="w-full min-w-[160px] rounded border border-purple-200 px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </th>
        );
      default:
        return (
          <th
            key={`completed-filter-${column.id}`}
            className={`px-3 py-1.5 text-[11px] text-purple-700 font-semibold ${
              isNumericDefaultColumn ? "text-right" : "text-center"
            }`}
          >
            -
          </th>
        );
    }
  };

  const handleCompletedActivitySort = (field: CompletedActivitySortField) => {
    if (completedActivitySortField === field) {
      setCompletedActivitySortOrder((prev) =>
        prev === "asc" ? "desc" : "asc",
      );
      return;
    }

    setCompletedActivitySortField(field);
    setCompletedActivitySortOrder("asc");
  };

  const CompletedActivitySortIcon = ({ field }: { field: string }) => {
    if (completedActivitySortField !== field) {
      return <ChevronUpIcon className="w-3 h-3 text-gray-300" />;
    }
    return completedActivitySortOrder === "asc" ? (
      <ChevronUpIcon className="w-3 h-3 text-blue-600" />
    ) : (
      <ChevronDownIcon className="w-3 h-3 text-blue-600" />
    );
  };

  const filteredCompletedActivityItems = completedActivityItems.filter(
    (activity) => {
      const assignedNames = getAssignedUserNames(activity);
      const { start, end } = getActivityStartEnd(activity);
      const duration = getUserTimeInfo(activity.userDurations)?.duration ?? 0;
      const activityCost = getCompletedActivityCostValue(activity) ?? 0;
      const content = getFormContent(activity);
      const description = getSubmitDescriptionPreview(activity);
      const projectName =
        activity.projectName ||
        workflowInstance?.workflowContainerName ||
        workflowInstance?.name ||
        "-";
      const latestCommentRow =
        latestManagerCommentsByActivityId[String(activity.id)];
      const latestComment = (latestCommentRow?.comment ?? "").toString().trim();

      const endDateValue = formatDateForInput(end);
      const startTimeValue = formatTimeForInput(start);
      const endTimeValue = formatTimeForInput(end);
      const assigneeValue = normalizeFilterText(assignedNames);
      const projectValue = normalizeFilterText(projectName);
      const activityValue = normalizeFilterText(
        activity.activityTitle || activity.name || "",
      );
      const formContentValue = normalizeFilterText(content.summary || "");
      const descriptionValue = normalizeFilterText(description || "");
      const managerCommentValue = normalizeFilterText(latestComment);
      const formColumnValues = Object.entries(
        completedActivityFormColumnFilters,
      );

      if (canViewActivityInvoiceStatus) {
        if (
          completedActivityInvoiceFilter === "invoiced" &&
          !Boolean(activity.isInvoiced)
        ) {
          return false;
        }

        if (
          completedActivityInvoiceFilter === "not_invoiced" &&
          Boolean(activity.isInvoiced)
        ) {
          return false;
        }
      }

      if (completedActivityRowFilters.date) {
        if (
          !endDateValue ||
          endDateValue !== completedActivityRowFilters.date
        ) {
          return false;
        }
      }

      if (completedActivityRowFilters.startTime) {
        if (
          !startTimeValue ||
          !startTimeValue.startsWith(completedActivityRowFilters.startTime)
        ) {
          return false;
        }
      }

      if (completedActivityRowFilters.endTime) {
        if (
          !endTimeValue ||
          !endTimeValue.startsWith(completedActivityRowFilters.endTime)
        ) {
          return false;
        }
      }

      if (completedActivityRowFilters.minDurationHours) {
        const minDuration = Number.parseFloat(
          completedActivityRowFilters.minDurationHours,
        );
        if (Number.isFinite(minDuration) && duration < minDuration) {
          return false;
        }
      }

      if (completedActivityRowFilters.minCost) {
        const minCost = Number.parseFloat(
          completedActivityRowFilters.minCost.replace(",", "."),
        );
        if (Number.isFinite(minCost) && activityCost < minCost) {
          return false;
        }
      }

      if (completedActivityRowFilters.assignee.trim()) {
        const query = normalizeFilterText(
          completedActivityRowFilters.assignee.trim(),
        );
        if (!assigneeValue.includes(query)) {
          return false;
        }
      }

      if (completedActivityRowFilters.projectName.trim()) {
        const query = normalizeFilterText(
          completedActivityRowFilters.projectName.trim(),
        );
        if (!projectValue.includes(query)) {
          return false;
        }
      }

      if (completedActivityRowFilters.activityTitle.trim()) {
        const query = normalizeFilterText(
          completedActivityRowFilters.activityTitle.trim(),
        );
        if (!activityValue.includes(query)) {
          return false;
        }
      }

      if (completedActivityRowFilters.formContent.trim()) {
        const query = normalizeFilterText(
          completedActivityRowFilters.formContent.trim(),
        );
        if (!formContentValue.includes(query)) {
          return false;
        }
      }

      if (completedActivityRowFilters.description.trim()) {
        const query = normalizeFilterText(
          completedActivityRowFilters.description.trim(),
        );
        if (!descriptionValue.includes(query)) {
          return false;
        }
      }

      if (completedActivityRowFilters.managerComment.trim()) {
        const query = normalizeFilterText(
          completedActivityRowFilters.managerComment.trim(),
        );
        if (!managerCommentValue.includes(query)) {
          return false;
        }
      }

      for (const [columnId, rawFilterValue] of formColumnValues) {
        const filterValue = rawFilterValue.trim();
        if (!filterValue) {
          continue;
        }

        const columnValue = normalizeFilterText(
          getCompletedActivityFormColumnValue(
            content.tooltip,
            getCompletedActivityFormColumnReference(columnId),
            activity.name || activity.activityTitle || "",
          ) || "",
        );
        const query = normalizeFilterText(filterValue);
        if (!columnValue.includes(query)) {
          return false;
        }
      }

      return true;
    },
  );
  const filteredAndSortedCompletedActivityItems = (() => {
    const rows = [...filteredCompletedActivityItems];
    rows.sort((a, b) => {
      const aStartEnd = getActivityStartEnd(a);
      const bStartEnd = getActivityStartEnd(b);

      const aAssignee = getAssignedUserNames(a).toLocaleLowerCase("tr-TR");
      const bAssignee = getAssignedUserNames(b).toLocaleLowerCase("tr-TR");

      const aDuration = getUserTimeInfo(a.userDurations)?.duration ?? 0;
      const bDuration = getUserTimeInfo(b.userDurations)?.duration ?? 0;
      const aCost = getCompletedActivityCostValue(a) ?? 0;
      const bCost = getCompletedActivityCostValue(b) ?? 0;

      const aProject = (
        a.projectName ||
        workflowInstance?.workflowContainerName ||
        workflowInstance?.name ||
        "-"
      ).toLocaleLowerCase("tr-TR");
      const bProject = (
        b.projectName ||
        workflowInstance?.workflowContainerName ||
        workflowInstance?.name ||
        "-"
      ).toLocaleLowerCase("tr-TR");

      const aActivity = (a.activityTitle || a.name || "-").toLocaleLowerCase(
        "tr-TR",
      );
      const bActivity = (b.activityTitle || b.name || "-").toLocaleLowerCase(
        "tr-TR",
      );

      const aComment = (
        latestManagerCommentsByActivityId[String(a.id)]?.comment || ""
      )
        .toString()
        .toLocaleLowerCase("tr-TR");
      const bComment = (
        latestManagerCommentsByActivityId[String(b.id)]?.comment || ""
      )
        .toString()
        .toLocaleLowerCase("tr-TR");

      let aValue: number | string = "";
      let bValue: number | string = "";

      switch (completedActivitySortField) {
        case "endDate":
          aValue = aStartEnd.end ? aStartEnd.end.getTime() : 0;
          bValue = bStartEnd.end ? bStartEnd.end.getTime() : 0;
          break;
        case "assignee":
          aValue = aAssignee;
          bValue = bAssignee;
          break;
        case "startTime":
          aValue = aStartEnd.start ? aStartEnd.start.getTime() : 0;
          bValue = bStartEnd.start ? bStartEnd.start.getTime() : 0;
          break;
        case "endTime":
          aValue = aStartEnd.end ? aStartEnd.end.getTime() : 0;
          bValue = bStartEnd.end ? bStartEnd.end.getTime() : 0;
          break;
        case "duration":
          aValue = aDuration;
          bValue = bDuration;
          break;
        case "costAmount":
          aValue = aCost;
          bValue = bCost;
          break;
        case "projectName":
          aValue = aProject;
          bValue = bProject;
          break;
        case "activityTitle":
          aValue = aActivity;
          bValue = bActivity;
          break;
        case "managerComment":
          aValue = aComment;
          bValue = bComment;
          break;
        default:
          if (completedActivitySortField.startsWith("form:")) {
            const columnReference = getCompletedActivityFormColumnReference(
              completedActivitySortField,
            );
            aValue = normalizeFilterText(
              getCompletedActivityFormColumnValue(
                getFormContent(a).tooltip,
                columnReference,
                a.name || a.activityTitle || "",
              ) || "",
            );
            bValue = normalizeFilterText(
              getCompletedActivityFormColumnValue(
                getFormContent(b).tooltip,
                columnReference,
                b.name || b.activityTitle || "",
              ) || "",
            );
            break;
          }
          break;
      }

      if (aValue < bValue) return completedActivitySortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return completedActivitySortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return rows;
  })();
  const filteredCompletedActivitiesTotalCost = useMemo(() => {
    if (!canViewCompletedActivityCosts) {
      return 0;
    }

    return filteredAndSortedCompletedActivityItems.reduce(
      (total, activity) => total + (getCompletedActivityCostValue(activity) ?? 0),
      0,
    );
  }, [
    canViewCompletedActivityCosts,
    filteredAndSortedCompletedActivityItems,
    getCompletedActivityCostValue,
  ]);

  const filteredCompletedActivitiesAverageCost = useMemo(() => {
    if (filteredAndSortedCompletedActivityItems.length === 0) {
      return 0;
    }

    return (
      filteredCompletedActivitiesTotalCost /
      filteredAndSortedCompletedActivityItems.length
    );
  }, [
    filteredAndSortedCompletedActivityItems.length,
    filteredCompletedActivitiesTotalCost,
  ]);

  const completedDurationSummary = (() => {
    const durations = filteredAndSortedCompletedActivityItems
      .map((item) => getUserTimeInfo(item.userDurations)?.duration ?? 0)
      .filter((value) => Number.isFinite(value) && value >= 0);
    const totalHours = durations.reduce((sum, value) => sum + value, 0);
    const recordCount = durations.length;
    const averageHours = recordCount > 0 ? totalHours / recordCount : 0;
    const displayHours =
      completedDurationSummaryMode === "average" ? averageHours : totalHours;

    return {
      totalHours,
      averageHours,
      recordCount,
      displayHours,
    };
  })();
  const {
    scrollContainerRef: completedActivitiesScrollContainerRef,
    setScrollTop: setCompletedActivitiesScrollTop,
    virtualizationEnabled: completedActivitiesVirtualizationEnabled,
    startIndex: completedActivitiesVirtualStartIndex,
    endIndex: completedActivitiesVirtualEndIndex,
    topSpacerHeight: completedActivitiesTopSpacerHeight,
    bottomSpacerHeight: completedActivitiesBottomSpacerHeight,
  } = useVirtualizedRows({
    itemCount: filteredAndSortedCompletedActivityItems.length,
    rowHeight: 40,
    overscan: 10,
    enabled: filteredAndSortedCompletedActivityItems.length > 80,
    resetKey: `${completedActivityIdsKey}-${completedActivitySortField}-${completedActivitySortOrder}-${filteredAndSortedCompletedActivityItems.length}`,
  });
  const visibleCompletedActivityItems = useMemo(
    () =>
      completedActivitiesVirtualizationEnabled
        ? filteredAndSortedCompletedActivityItems.slice(
            completedActivitiesVirtualStartIndex,
            completedActivitiesVirtualEndIndex,
          )
        : filteredAndSortedCompletedActivityItems,
    [
      completedActivitiesVirtualEndIndex,
      completedActivitiesVirtualStartIndex,
      completedActivitiesVirtualizationEnabled,
      filteredAndSortedCompletedActivityItems,
    ],
  );

  const projectDisplayName = (() => {
    const baseName =
      workflowInstance?.workflowContainerFormData?.name ||
      workflowInstance?.workflowContainerName ||
      "";
    if (workflowInstance?.projectCode) {
      return baseName
        ? `${workflowInstance.projectCode}-${baseName}`
        : workflowInstance.projectCode;
    }
    return formatProjectDisplayName({
      name: baseName,
      projectTypeName:
        workflowInstance?.workflowName || workflowInstance?.name || "",
      startDate: workflowInstance?.createdAt,
      country: workflowInstance?.workflowContainerFormData?.ulke,
    });
  })();

  const exportCompletedActivitiesToExcel = () => {
    if (showLeaveActivitiesInDetail) {
      if (leaveDetailItems.length === 0) return;

      const headers = [
        t("dashboard:managerLeaves.rowNo"),
        t("dashboard:managerLeaves.leaveStartDate"),
        t("dashboard:managerLeaves.leaveEndDate"),
        t("dashboard:managerLeaves.leaveAssignee"),
        t("dashboard:managerLeaves.leaveStartTime"),
        t("dashboard:managerLeaves.leaveEndTime"),
        t("dashboard:managerLeaves.durationHours"),
        LEAVE_TYPE_FIELD_LABEL,
        t("dashboard:managerLeaves.leaveDescription"),
        t("common:status"),
      ];

      const rows: Array<Array<string | number>> = [headers];
      leaveDetailItems.forEach((activity, idx) => {
        const { start, end } = getActivityStartEnd(activity);
        const duration = getUserTimeInfo(activity.userDurations)?.duration ?? 0;
        const leaveType = getActivityLeaveType(activity);
        const description = getSubmitDescriptionPreview(activity);
        rows.push([
          idx + 1,
          formatDateOnlyTr(start),
          formatDateOnlyTr(end),
          getAssignedUserNames(activity) === "-" ? "" : getAssignedUserNames(activity),
          formatTimeOnlyTr(start),
          formatTimeOnlyTr(end),
          duration > 0 ? Number(duration.toFixed(2)) : "",
          leaveType.tooltip || leaveType.summary || "",
          description === "-" ? "" : description,
          getStatusDisplayName(activity.status || ""),
        ]);
      });

      const safeBaseName = (projectDisplayName || `workflow_${instanceId ?? ""}`)
        .replace(/[<>:"/\\|?*]+/g, "_")
        .trim();
      const dateStamp = new Date().toISOString().slice(0, 10);
      exportRowsToExcel({
        fileName: `${safeBaseName || "workflow"}_${t("dashboard:managerLeaves.excelFilePrefix")}_${dateStamp}.xlsx`,
        sheetName: t("dashboard:managerLeaves.sheetName"),
        rows,
      });
      return;
    }

    if (filteredAndSortedCompletedActivityItems.length === 0) return;

    const headers = completedActivityVisibleColumns.map((column) => column.label);
    const rows: Array<
      | Array<string | number>
      | {
          values: Array<string | number>;
          style?: {
            fillColor?: string;
            fontColor?: string;
          };
        }
    > = [headers];

    filteredAndSortedCompletedActivityItems.forEach((activity, idx) => {
      const assignedNames = getAssignedUserNames(activity);
      const description = getSubmitDescriptionPreview(activity);
      const { start, end } = getActivityStartEnd(activity);
      const duration = getUserTimeInfo(activity.userDurations)?.duration ?? 0;
      const activityCost = getCompletedActivityCostValue(activity);
      const content = getFormContent(activity);
      const projectName =
        activity.projectName ||
        workflowInstance?.workflowContainerName ||
        workflowInstance?.name ||
        "-";
      const row = completedActivityVisibleColumns.map((column) => {
        if (column.kind === "form") {
          return (
            getCompletedActivityFormColumnValue(
              content.tooltip,
              column,
              activity.name || activity.activityTitle || "",
            ) || ""
          );
        }

        switch (column.id) {
          case "rowNo":
            return idx + 1;
          case "endDate":
            return formatDateOnlyTr(end);
          case "assignee":
            return assignedNames === "-" ? "" : assignedNames;
          case "startTime":
            return formatTimeOnlyTr(start);
          case "endTimeOnly":
            return formatTimeOnlyTr(end);
          case "duration":
            return duration > 0 ? Number(duration.toFixed(2)) : "";
          case "cost":
            return activityCost !== null
              ? Number(activityCost.toFixed(2))
              : "";
          case "projectName":
            return projectName === "-" ? "" : projectName;
          case "activityTitle":
            return activity.activityTitle || activity.name || "";
          case "formContent":
            return content.tooltip || content.summary || "";
          case "submitDescription":
            return description === "-" ? "" : description;
          case "managerComment": {
            const latest =
              latestManagerCommentsByActivityId[String(activity.id)];
            return (latest?.comment ?? "").toString().trim();
          }
          case "invoiceStatus":
            return activity.isInvoiced
              ? t("projects:workflowInstanceDetail.invoiceTracking.invoiced")
              : t("projects:workflowInstanceDetail.invoiceTracking.notInvoiced");
          case "details":
            return "";
          default:
            return "";
        }
      });
      rows.push(
        activity.isInvoiced
          ? {
              values: row,
              style: {
                fillColor: "#ECFDF5",
                fontColor: "#065F46",
              },
            }
          : row,
      );
    });

    const summaryLabel =
      completedDurationSummaryMode === "average"
        ? t("dashboard:managerActivities.filteredAverage")
        : t("dashboard:managerActivities.filteredTotal");
    const summaryRow = new Array(headers.length).fill("");
    summaryRow[0] = summaryLabel;
    const durationIndex = completedActivityVisibleColumns.findIndex(
      (column) => column.kind === "default" && column.id === "duration",
    );
    if (durationIndex >= 0) {
      summaryRow[durationIndex] = Number(
        completedDurationSummary.displayHours.toFixed(2),
      );
    }
    if (canViewCompletedActivityCosts) {
      const summaryCost =
        completedDurationSummaryMode === "average"
          ? filteredCompletedActivitiesAverageCost
          : filteredCompletedActivitiesTotalCost;
      const costIndex = completedActivityVisibleColumns.findIndex(
        (column) => column.kind === "default" && column.id === "cost",
      );
      if (costIndex >= 0) {
        summaryRow[costIndex] =
          summaryCost > 0 ? Number(summaryCost.toFixed(2)) : "-";
      }
    }
    const recordCountIndex = headers.length - 1;
    if (recordCountIndex >= 0) {
      summaryRow[recordCountIndex] = t(
        "dashboard:managerActivities.recordCount",
        {
          count: completedDurationSummary.recordCount,
        },
      );
    }
    rows.push(summaryRow);

    const safeBaseName = (projectDisplayName || `workflow_${instanceId ?? ""}`)
      .replace(/[<>:"/\\|?*]+/g, "_")
      .trim();
    const dateStamp = new Date().toISOString().slice(0, 10);
    exportRowsToExcel({
      fileName: `${safeBaseName || "workflow"}_${t("dashboard:managerActivities.excelFilePrefix")}_${dateStamp}.xlsx`,
      sheetName: t("dashboard:managerActivities.sheetName"),
      rows,
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
      </div>
    );
  }

  if (!workflowInstance) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t("workflows:workflowNotFound")}
          </h2>
          <button
            onClick={navigateBack}
            className="text-blue-600 hover:text-blue-800"
          >
            {t("common:back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-page bg-slate-50 py-6">
      <div className="w-full px-4 lg:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Back button */}
          <div className="flex items-center space-x-4 mb-5">
            <button
              onClick={navigateBack}
              className="flex items-center text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              {t("common:back")}
            </button>
          </div>

          {/* Header Card */}
          <div className="bg-white/80 backdrop-blur rounded-2xl shadow-xl border border-slate-100 p-4 sm:p-5 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-stretch lg:justify-between gap-5">
              <div className="space-y-3 flex-1">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 p-2 rounded-xl bg-blue-50 text-blue-600 text-2xl">
                    🚀
                  </div>
                  <div className="min-w-0 flex-1">
                    <h1 className="break-words text-xl font-bold text-slate-900 sm:text-2xl lg:text-3xl">
                      {projectDisplayName ||
                        workflowInstance.workflowContainerFormData?.name ||
                        workflowInstance.workflowContainerName}
                    </h1>
                    {!isVisitorUser &&
                      workflowInstance.workflowContainerFormData
                        ?.description && (
                        <div className="relative group mt-1.5 max-w-2xl">
                          <p
                            className="text-sm text-slate-600 break-words"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              wordBreak: "break-all",
                              overflowWrap: "anywhere",
                              maxWidth: "100%",
                            }}
                          >
                            {
                              workflowInstance.workflowContainerFormData
                                .description
                            }
                          </p>
                          <div className="pointer-events-none absolute left-0 top-full z-20 mt-2 max-w-[420px] rounded-md bg-slate-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100 break-words whitespace-pre-wrap">
                            {
                              workflowInstance.workflowContainerFormData
                                .description
                            }
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                {!isVisitorUser &&
                  (workflowInstance.workflowName || workflowInstance.name) && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <DocumentTextIcon className="w-4 h-4 text-slate-400" />
                      <span>
                        {workflowTemplateText}:{" "}
                        <strong>
                          {workflowInstance.workflowName ||
                            workflowInstance.name}
                        </strong>
                      </span>
                    </div>
                  )}

                {!isVisitorUser && (
                  <div className="flex flex-wrap items-center gap-2">
                    {workflowInstance.status !== "IN_PROGRESS" && (
                      <span
                        className={`inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-full border bg-gradient-to-r ${getStatusColor(
                          workflowInstance.status,
                        )}`}
                      >
                        {getStatusDisplayName(workflowInstance.status)}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Right Side Actions */}
              <div className="flex w-full flex-shrink-0 flex-col items-stretch justify-between gap-3 lg:w-auto lg:items-end">
                {!isVisitorUser && (
                  <div className="inline-flex w-full items-center justify-center px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 lg:w-auto">
                    <span className="text-sm text-slate-600">
                      {t("workflows:startedBy")}:{" "}
                      <span className="font-semibold text-slate-900">
                        {workflowInstance.starterName}
                      </span>
                    </span>
                  </div>
                )}

                <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end lg:w-auto">
                  {canViewProjectFiles && (
                    <button
                      onClick={() => setShowFilesModal(true)}
                      className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-blue-600 px-2 py-2 text-center text-xs font-semibold text-white shadow-md transition-all hover:bg-blue-700 hover:shadow-lg sm:px-3 sm:text-sm"
                    >
                      <FolderIcon className="w-4 h-4" />
                      {t("projects:workflowInstanceDetail.filesButton")}
                    </button>
                  )}
                  {canAssignTasks && (
                    <button
                      onClick={() => setShowAssignTaskModal(true)}
                      className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-indigo-600 px-2 py-2 text-center text-xs font-semibold text-white shadow-md transition-all hover:bg-indigo-700 hover:shadow-lg sm:px-3 sm:text-sm"
                    >
                      <PlusIcon className="w-4 h-4" />
                      {t("tasks:assignTask")}
                    </button>
                  )}

                  {canArchiveProject &&
                    workflowInstance.status === "ARCHIVED" && (
                      <button
                        onClick={() => setShowReactivateDialog(true)}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 px-2 py-2 text-center text-xs font-semibold text-white shadow-md transition-all hover:from-green-700 hover:to-emerald-700 hover:shadow-lg sm:px-3 sm:text-sm"
                      >
                        <RocketLaunchIcon className="w-4 h-4" />
                        {t("workflows:reactivateProject")}
                      </button>
                    )}

                  {canArchiveProject &&
                    workflowInstance.status !== "ARCHIVED" && (
                      <button
                        onClick={() => setShowCompleteDialog(true)}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-gray-600 to-slate-600 px-2 py-2 text-center text-xs font-semibold text-white shadow-md transition-all hover:from-gray-700 hover:to-slate-700 hover:shadow-lg sm:px-3 sm:text-sm"
                      >
                        <ArchiveBoxIcon className="w-4 h-4" />
                        {t("workflows:archive")}
                      </button>
                    )}

                  {canManageProject && (
                    <>
                      <button
                        onClick={() => setShowChangeTypeModal(true)}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-indigo-600 px-2 py-2 text-center text-xs font-semibold text-white shadow-md transition-all hover:bg-indigo-700 hover:shadow-lg sm:px-3 sm:text-sm"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        {t("workflows:changeProjectType")}
                      </button>
                      <button
                        onClick={() => setShowEditDetailsModal(true)}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-amber-600 px-2 py-2 text-center text-xs font-semibold text-white shadow-md transition-all hover:bg-amber-700 hover:shadow-lg sm:px-3 sm:text-sm"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        {t("workflows:editDetails")}
                      </button>
                      {(isManager() || isAdmin()) && (
                        <button
                          onClick={() => setIsDeleteDialogOpen(true)}
                          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-red-600 px-2 py-2 text-center text-xs font-semibold text-white shadow-md transition-all hover:bg-red-700 hover:shadow-lg sm:px-3 sm:text-sm"
                        >
                          <TrashIcon className="w-4 h-4" />
                          {t("common:delete")}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Progress Bars Section */}
            {(() => {
                // Use backend-calculated totalSpentHours so leave activities (izin) can be excluded consistently.
                const totalSpentHours = Number(
                  workflowInstance?.totalSpentHours ?? 0,
                );

                const plannedHours =
                  workflowInstance?.workflowContainerFormData
                    ?.estimatedDurationHours ||
                  workflowInstance?.workflowContainerFormData
                    ?.estimatedDurationDays ||
                  0;

                const plannedHoursNumber =
                  typeof plannedHours === "string"
                    ? Number.parseFloat(plannedHours)
                    : plannedHours;

                const percentage =
                  plannedHoursNumber > 0
                    ? Math.round((totalSpentHours / plannedHoursNumber) * 100)
                    : 0;

                const isOverBudget =
                  plannedHoursNumber > 0 &&
                  totalSpentHours > plannedHoursNumber;
                const hasActivityProgress = totalActivities > 0;
                const hasPlannedHours = plannedHoursNumber > 0;

                // Determine grid layout based on what we have
                const gridClass = hasActivityProgress
                  ? "grid grid-cols-1 lg:grid-cols-2 gap-5"
                  : "grid grid-cols-1 gap-5";

                return (
                  <div className={`mt-5 ${gridClass}`}>
                    {/* Activity Progress Bar - Only show if there are template activities */}
                    {hasActivityProgress && (
                      <div className="bg-white/50 rounded-xl p-3.5 border border-slate-200/50">
                        <div className="flex justify-between text-sm text-slate-600 mb-2">
                          <span className="font-medium flex items-center gap-2">
                            <CheckCircleIcon className="w-4 h-4 text-blue-500" />
                            {t("common:progress")}
                          </span>
                          <span className="font-semibold">
                            {completedActivities} / {totalActivities}{" "}
                            <span className="text-slate-400">
                              ({progress}%)
                            </span>
                          </span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Hours Progress Bar */}
                    <div
                      className={`rounded-xl p-3.5 border ${
                        isOverBudget
                          ? "bg-red-50/50 border-red-200"
                          : "bg-orange-50/50 border-orange-200/50"
                      }`}
                    >
                      <div className="flex justify-between text-sm mb-2">
                        <span
                          className={`font-medium flex items-center gap-2 ${
                            isOverBudget ? "text-red-700" : "text-orange-700"
                          }`}
                        >
                          <ClockIcon className="w-4 h-4" />
                          {showLeaveActivitiesInDetail
                            ? t("projects:leaveDuration")
                            : hasPlannedHours
                              ? t("projects:hoursProgress")
                              : t("projects:spentHours")}
                        </span>
                        <span className="font-semibold text-sm">
                          <span
                            className={`${
                              isOverBudget ? "text-red-600" : "text-orange-600"
                            }`}
                          >
                            {totalSpentHours.toFixed(2)}
                          </span>
                          {hasPlannedHours && (
                            <>
                              <span className="text-slate-400 mx-1">/</span>
                              <span className="text-slate-600">
                                {plannedHoursNumber} {t("dashboard:hours")}
                              </span>
                              <span
                                className={`ml-2 ${
                                  isOverBudget
                                    ? "text-red-500"
                                    : "text-slate-400"
                                }`}
                              >
                                ({percentage}%)
                              </span>
                            </>
                          )}
                          {!hasPlannedHours && (
                            <span className="text-slate-500 ml-1">
                              {t("dashboard:hours")}
                            </span>
                          )}
                        </span>
                      </div>
                      {hasPlannedHours ? (
                        <>
                          <div className="h-3 bg-white/80 rounded-full overflow-hidden border border-slate-200/50">
                            <div
                              className={`h-full rounded-full transition-all ${
                                isOverBudget
                                  ? "bg-gradient-to-r from-red-500 to-red-600"
                                  : "bg-gradient-to-r from-orange-400 to-amber-500"
                              }`}
                              style={{
                                width: `${Math.min(percentage, 100)}%`,
                              }}
                            />
                          </div>
                          {isOverBudget && (
                            <p className="text-xs text-red-600 mt-2 font-medium">
                              ⚠️{" "}
                              {(totalSpentHours - plannedHoursNumber).toFixed(
                                2,
                              )}{" "}
                              {t("projects:workflowInstanceDetail.hoursBudgetOverrun")}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-slate-500 mt-1">
                          {t(
                            showLeaveActivitiesInDetail
                              ? "projects:workflowInstanceDetail.completedLeavesSpentTime"
                              : "projects:workflowInstanceDetail.completedActivitiesSpentTime",
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}

            {isVisitorUser && canVisitorViewFinancialDetails && (
              <div className="mt-5 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-4">
                  <CurrencyDollarIcon className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-sm font-semibold text-slate-900">
                    {t("projects:workflowInstanceDetail.finance.title")}
                  </h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <tbody className="divide-y divide-slate-100 bg-white">
                      <tr>
                        <td className="px-3 py-2 font-semibold text-teal-700">
                          {t(
                            "projects:workflowInstanceDetail.finance.totalCollection",
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-teal-800">
                          {formatCurrencyByCode(
                            projectFinanceSummary.totalCollected,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-rose-700">
                          {t(
                            "projects:workflowInstanceDetail.finance.remainingCollection",
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-rose-800">
                          {formatCurrencyByCode(
                            projectFinanceSummary.remainingCollection,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!isVisitorUser && canViewFinancials && (
              <div className="mt-5 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-2">
                    <CurrencyDollarIcon className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-sm font-semibold text-slate-900">
                      {t("projects:workflowInstanceDetail.finance.title")}
                    </h3>
                  </div>
                  {isCostRatesLoading && (
                    <span className="text-xs text-slate-500">
                      {t("projects:workflowInstanceDetail.finance.costRatesLoading")}
                    </span>
                  )}
                  {!isCostRatesLoading && isExchangeRatesLoading && (
                    <span className="text-xs text-slate-500">
                      {t(
                        "projects:workflowInstanceDetail.finance.exchangeRatesLoading",
                      )}
                    </span>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">
                          {t("projects:workflowInstanceDetail.finance.lineItem")}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-700">
                          {t("projects:workflowInstanceDetail.finance.amount")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      <tr>
                        <td className="px-3 py-2 font-semibold text-sky-700">
                          {t(
                            "projects:workflowInstanceDetail.finance.projectAmount",
                            "İş Tutarı",
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-sky-800">
                          {formatCurrencyByCode(
                            projectFinanceSummary.projectAmount,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-emerald-700">
                          {t("projects:workflowInstanceDetail.finance.realizedCost")}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-800">
                          {formatCurrencyByCode(
                            projectCostSummary.realizedCost,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-amber-700">
                          {t(
                            "projects:workflowInstanceDetail.finance.estimatedRemaining",
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-amber-800">
                          {formatCurrencyByCode(
                            projectCostSummary.remainingEstimatedCost,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-purple-700">
                          {t("projects:workflowInstanceDetail.finance.estimatedTotal")}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-purple-800">
                          {formatCurrencyByCode(
                            projectCostSummary.totalEstimatedCost,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-sky-700">
                          {t("projects:workflowInstanceDetail.finance.grossProfit")}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-sky-800">
                          {formatCurrencyByCode(
                            projectFinanceSummary.grossProfit,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-cyan-700">
                          {t("projects:workflowInstanceDetail.finance.netProfit")}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-cyan-800">
                          {formatCurrencyByCode(
                            projectFinanceSummary.netProfit,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-fuchsia-700">
                          {t(
                            "projects:workflowInstanceDetail.finance.estimatedNetProfit",
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-fuchsia-800">
                          {formatCurrencyByCode(
                            projectFinanceSummary.estimatedNetProfit,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-slate-700">
                          {t("projects:workflowInstanceDetail.finance.invoiceTotal")}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-slate-800">
                          {formatCurrencyByCode(
                            projectFinanceSummary.invoiceTotal,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-teal-700">
                          {t(
                            "projects:workflowInstanceDetail.finance.totalCollection",
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-teal-800">
                          {formatCurrencyByCode(
                            projectFinanceSummary.totalCollected,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold text-rose-700">
                          {t(
                            "projects:workflowInstanceDetail.finance.remainingCollection",
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-rose-800">
                          {formatCurrencyByCode(
                            projectFinanceSummary.remainingCollection,
                            projectFinanceSummary.projectCurrency,
                            { display: "suffix" },
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </div>
            )}

            {canViewCollectionDetails && (
              <div className="mt-5 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      {t("projects:workflowInstanceDetail.collections.title")}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1">
                      {t("projects:workflowInstanceDetail.collections.subtitle")}
                    </p>
                  </div>
                  {canManageCollections && !isArchivedProject && (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => openFinanceShortcutModal("invoice")}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-sm font-semibold transition-colors"
                      >
                        <DocumentTextIcon className="w-4 h-4" />
                        {t(
                          "projects:workflowInstanceDetail.financeShortcut.invoiceAction",
                          "Fatura Ekle",
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => openFinanceShortcutModal("offer")}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg text-sm font-semibold transition-colors"
                      >
                        <CurrencyDollarIcon className="w-4 h-4" />
                        {t(
                          "projects:workflowInstanceDetail.financeShortcut.offerAction",
                          "İş Teklifi Tutar Ekle",
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={openCollectionModal}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        {t("projects:workflowInstanceDetail.collections.add")}
                      </button>
                    </div>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">
                          {t("common:date")}
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">
                          {t("projects:workflowInstanceDetail.finance.amount")}
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">
                          {t(
                            "projects:workflowInstanceDetail.collections.paymentDescription",
                          )}
                        </th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">
                          {t("projects:workflowInstanceDetail.collections.files")}
                        </th>
                        {canManageCollections && (
                          <th className="px-3 py-2 text-right font-semibold text-slate-700">
                            {t("common:actions")}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {isCollectionsLoading ? (
                        <tr>
                          <td
                            colSpan={canManageCollections ? 5 : 4}
                            className="px-3 py-4 text-center text-slate-500"
                          >
                            {t("projects:workflowInstanceDetail.collections.loading")}
                          </td>
                        </tr>
                      ) : projectCollections.length === 0 ? (
                        <tr>
                          <td
                            colSpan={canManageCollections ? 5 : 4}
                            className="px-3 py-4 text-center text-slate-500"
                          >
                            {t("projects:workflowInstanceDetail.collections.empty")}
                          </td>
                        </tr>
                      ) : (
                        projectCollections.map((collection) => (
                          <tr key={collection.id}>
                            <td className="px-3 py-2 text-slate-700">
                              {formatDateOnly(collection.collectionDate)}
                            </td>
                            <td className="px-3 py-2 font-semibold text-slate-900">
                              {formatCurrencyByCode(
                                collection.amount,
                                collection.currency,
                                { display: "suffix" },
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              {collection.description?.trim() || "-"}
                            </td>
                            <td className="px-3 py-2">
                              {Array.isArray(collection.attachments) &&
                              collection.attachments.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {collection.attachments.map((attachment) => (
                                    <button
                                      key={`${collection.id}-${attachment.id}`}
                                      type="button"
                                      onClick={() => void handlePreviewWorkflowFile(attachment)}
                                      disabled={previewingDriveFileId === attachment.id}
                                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                                    >
                                      {previewingDriveFileId === attachment.id ? (
                                        <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                                      ) : (
                                        <PaperClipIcon className="w-3.5 h-3.5" />
                                      )}
                                      <span className="max-w-[180px] truncate">
                                        {attachment.name}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            {canManageCollections && (
                              <td className="px-3 py-2 text-right">
                                {!isArchivedProject && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      void handleDeleteCollection(collection.id)
                                    }
                                    disabled={deletingCollectionId === collection.id}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-60"
                                  >
                                    <TrashIcon className="w-3.5 h-3.5" />
                                    {t("common:delete")}
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto">
            {/* Stats Cards - Workflow Instance Start/End, Budget, Members */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Workflow Started Date */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-green-50">
                    <CalendarIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      {t("common:startDate")}
                    </p>
                    <p className="text-sm font-semibold text-slate-900">
                      {formatDateOnly(
                        workflowInstance.workflowContainerFormData?.startDate,
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Workflow Completed Date */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-emerald-50">
                    <CheckCircleIcon className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      {t("common:endDate")}
                    </p>
                    <p className="text-sm font-semibold text-slate-900">
                      {formatDateOnly(
                        workflowInstance.workflowContainerFormData?.endDate,
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {canViewFinancials && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-blue-50">
                      <CurrencyDollarIcon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                        {t("projects:budget")}
                      </p>
                      <p className="text-lg font-semibold text-slate-900">
                        {(() => {
                          const budget =
                            workflowInstance.workflowContainerFormData?.budget;
                          const budgetNumber =
                            typeof budget === "string"
                              ? Number.parseFloat(budget)
                              : budget;
                          return formatCurrency(
                            budgetNumber,
                            projectFinanceSummary.projectCurrency || "TRY",
                          );
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Team Members */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-purple-50">
                    <UsersIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      {t("projects:projectMembers")}
                    </p>
                    <p className="text-lg font-semibold text-slate-900">
                      {projectMembers.length} {t("projects:people")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Details Row */}
            {(workflowInstance.workflowContainerFormData?.startDate ||
              workflowInstance.workflowContainerFormData?.endDate ||
              workflowInstance.workflowContainerFormData?.clientName) && (
              <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4 mb-6">
                {/* Project Duration */}
                {(workflowInstance.workflowContainerFormData?.startDate ||
                  workflowInstance.workflowContainerFormData?.endDate) && (
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-amber-50">
                        <CalendarIcon className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex flex-col gap-1 flex-1">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                          {t("projects:projectDuration")}
                        </p>
                        <p className="text-sm font-semibold text-slate-900">
                          {formatDateOnly(
                            workflowInstance.workflowContainerFormData
                              ?.startDate,
                          )}{" "}
                          -{" "}
                          {formatDateOnly(
                            workflowInstance.workflowContainerFormData?.endDate,
                          )}
                          {(() => {
                            const businessDays = calculateBusinessDays(
                              workflowInstance.workflowContainerFormData
                                ?.startDate,
                              workflowInstance.workflowContainerFormData
                                ?.endDate,
                              companySettings?.workCalendar,
                            );
                            return businessDays > 0 ? (
                              <span className="text-xs text-slate-500 ml-1">
                                ({formatBusinessDays(businessDays)}{" "}
                                {t("projects:workDays")})
                              </span>
                            ) : null;
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Client Name */}
                {workflowInstance?.workflowContainerFormData?.clientName && (
                  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-indigo-50">
                        <InformationCircleIcon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div className="flex flex-col gap-1 flex-1">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                          {t("projects:client")}
                        </p>
                        <p className="text-sm font-semibold text-slate-900">
                          {
                            workflowInstance.workflowContainerFormData
                              .clientName
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Project Members Section */}
            {projectMembers.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <UsersIcon className="w-5 h-5 text-blue-600" />
                    {t("projects:projectMembers")}
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {projectMembers.map((member) => (
                    <div
                      key={member.id}
                      className="border border-slate-200 rounded-xl p-2.5 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold text-base">
                          {getUserInitials(
                            member.user?.firstName,
                            member.user?.lastName,
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xs font-semibold text-slate-900 truncate">
                            {member.user?.firstName} {member.user?.lastName}
                          </h3>
                          <p className="text-[11px] text-slate-500 truncate">
                            @{member.user?.username}
                          </p>
                          <p className="text-[11px] text-blue-600 font-medium mt-0.5">
                            {getProjectMemberDisplayRole(member)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Project Form Details - Matching WorkflowOverview design */}
            {workflowInstance.workflowContainerFormData &&
              Object.keys(workflowInstance.workflowContainerFormData).length >
                0 &&
              (() => {
                const formData =
                  workflowInstance.workflowContainerFormData || {};
                const normalizeFinancialToken = (value: unknown): string =>
                  String(value ?? "")
                    .toLocaleLowerCase("tr-TR")
                    .replace(/ı/g, "i")
                    .replace(/ö/g, "o")
                    .replace(/ü/g, "u")
                    .replace(/ş/g, "s")
                    .replace(/ğ/g, "g")
                    .replace(/ç/g, "c")
                    .replace(/[^a-z0-9_ ]/g, "");
                const sensitiveFinancialIds = new Set([
                  "budget",
                  "projectbudget",
                  "projectcurrency",
                  "currency",
                  "currencycode",
                  "para_birimi",
                  "parabirimi",
                  "project_currency",
                ]);
                const isSensitiveFinancialKey = (key: string): boolean => {
                  const normalized = normalizeFinancialToken(key);
                  if (sensitiveFinancialIds.has(normalized)) {
                    return true;
                  }
                  return (
                    normalized.includes("para birimi") ||
                    normalized.includes("doviz") ||
                    normalized.includes("currency")
                  );
                };
                const isSensitiveFinancialElement = (element: any): boolean => {
                  if (!element) {
                    return false;
                  }
                  if (
                    element.type === "currency_input" ||
                    element.type === "budget_input"
                  ) {
                    return true;
                  }
                  if (isSensitiveFinancialKey(String(element.id || ""))) {
                    return true;
                  }
                  return isSensitiveFinancialKey(String(element.label || ""));
                };
                const shouldIncludeKey = (key: string) =>
                  key !== "name" &&
                  key !== "description" &&
                  key !== "clientName" &&
                  key !== "warningThreshold" &&
                  key !== "progressCalculationMethod" &&
                  key !== "startDate" &&
                  key !== "endDate" &&
                  key !== "estimatedDurationDays" &&
                  key !== "elementCurrencies" &&
                  key !== "element_currencies" &&
                  (canViewFinancials || !isSensitiveFinancialKey(key)) &&
                  !key.startsWith("element_");

                const filteredEntries = projectContainer?.formElements?.length
                  ? projectContainer.formElements
                      .filter((element) => shouldIncludeKey(element.id))
                      .filter(
                        (element) =>
                          canViewFinancials ||
                          !isSensitiveFinancialElement(element),
                      )
                      .filter((element) =>
                        Object.prototype.hasOwnProperty.call(
                          formData,
                          element.id,
                        ),
                      )
                      .map((element) => ({
                        key: element.id,
                        value: formData[element.id],
                        element,
                      }))
                  : Object.entries(formData)
                      .filter(([key]) => shouldIncludeKey(key))
                      .map(([key, value]) => ({
                        key,
                        value,
                        element: projectContainer?.formElements?.find(
                          (el) => el.id === key,
                        ),
                      }));

                return (
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <DocumentTextIcon className="w-5 h-5 text-indigo-600" />
                      <h2 className="text-sm font-semibold text-slate-700">
                        {t("projects:projectDetails")}
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 items-start auto-rows-min">
                      {filteredEntries.length === 0 ? (
                        <div className="col-span-full text-center py-8">
                          <p className="text-sm text-slate-500">
                            {t("projects:noProjectDetails")}
                          </p>
                        </div>
                      ) : (
                        filteredEntries.map(({ key, value, element }) => {
                          const labelText = getFieldLabel(key, element);
                          const displayValue = (() => {
                            if (value === null || value === undefined)
                              return t("common:notProvided");

                            const elementType = (element as any)?.type as
                              | string
                              | undefined;

                            if (
                              elementType === "employee_select" ||
                              elementType === "project_manager_select" ||
                              elementType === "employee_list_select"
                            ) {
                              const parseUserIds = (raw: any): number[] => {
                                if (
                                  raw === null ||
                                  raw === undefined ||
                                  raw === ""
                                ) {
                                  return [];
                                }

                                const toNumberList = (items: any[]): number[] =>
                                  items
                                    .map((item) => Number(String(item).trim()))
                                    .filter((id) => Number.isFinite(id));

                                if (Array.isArray(raw)) {
                                  return toNumberList(raw);
                                }

                                if (typeof raw === "number") {
                                  return Number.isFinite(raw) ? [raw] : [];
                                }

                                if (typeof raw === "string") {
                                  const trimmed = raw.trim();
                                  if (!trimmed) return [];
                                  try {
                                    const parsed = JSON.parse(trimmed);
                                    if (Array.isArray(parsed)) {
                                      return toNumberList(parsed);
                                    }
                                    if (
                                      parsed !== null &&
                                      parsed !== undefined &&
                                      parsed !== ""
                                    ) {
                                      const parsedAsNumber = Number(
                                        String(parsed).trim(),
                                      );
                                      return Number.isFinite(parsedAsNumber)
                                        ? [parsedAsNumber]
                                        : [];
                                    }
                                  } catch {
                                    // Not JSON, continue with CSV fallback.
                                  }

                                  const csvItems = trimmed
                                    .split(",")
                                    .map((item) => item.trim())
                                    .filter(Boolean);
                                  return toNumberList(csvItems);
                                }

                                return [];
                              };

                              const userIds = parseUserIds(value);
                              if (userIds.length > 0) {
                                const names = userIds.map((id) => {
                                  const user = allUsers.find(
                                    (u) => u.id === id,
                                  );
                                  if (!user) return `User ${id}`;
                                  const full =
                                    `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
                                  return full || user.username || `User ${id}`;
                                });
                                return names.join(", ");
                              }

                              if (Array.isArray(value)) {
                                const fallback = value
                                  .map((v) => String(v).trim())
                                  .filter(Boolean)
                                  .join(", ");
                                return fallback || t("common:notProvided");
                              }

                              return formatProjectDetailValue(value);
                            }

                            if (
                              elementType === "currency_input" ||
                              elementType === "offer_amount"
                            ) {
                              const currencyCode =
                                (element as any).currency || "TRY";
                              const numValue = Number(value);
                              if (Number.isFinite(numValue)) {
                                return formatCurrencyByCode(
                                  numValue,
                                  currencyCode,
                                  { display: "suffix" },
                                );
                              }
                              return `${String(value)} ${getCurrencySuffixLabel(
                                currencyCode,
                              )}`;
                            }

                            if (elementType === "invoice") {
                              const normalized = String(value)
                                .trim()
                                .toLowerCase();
                              if (
                                normalized === "yes" ||
                                normalized === "true" ||
                                normalized === "1"
                              ) {
                                return t("common:yes");
                              }
                              if (
                                normalized === "no" ||
                                normalized === "false" ||
                                normalized === "0"
                              ) {
                                return t("common:no");
                              }
                            }

                            if (typeof value === "boolean") {
                              return value ? t("common:yes") : t("common:no");
                            }

                            return formatProjectDetailValue(value);
                          })();
                          const displayText = String(displayValue);
                          const showTooltip =
                            labelText.length > 24 || displayText.length > 24;
                          return (
                            <div
                              key={key}
                              className="p-3 bg-slate-50 rounded-lg self-start min-w-0 group relative"
                            >
                              <div className="flex flex-col gap-1 min-w-0">
                                <span className="text-xs font-semibold text-slate-900 truncate">
                                  {labelText}
                                </span>
                                <div className="text-sm text-slate-600 truncate">
                                  {displayText}
                                </div>
                              </div>
                              {showTooltip && (
                                <div className="absolute left-0 top-full mt-2 z-20 w-max max-w-xs rounded-md bg-slate-900 text-white text-xs p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="font-semibold">
                                    {labelText}
                                  </div>
                                  <div className="mt-1 whitespace-pre-wrap break-words">
                                    {displayText}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })()}
          </div>

        {!isVisitorUser && (
          <div className="-mx-4 lg:-mx-6 px-4 lg:px-6">
            {/* Completed Activities Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 sm:p-6">
              <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-bold text-slate-900 sm:text-xl">
                  {showLeaveActivitiesInDetail
                    ? "İzinler"
                    : hasPendingApprovalDetailActivities
                      ? t("activities:activities")
                      : t("activities:completedActivities")}
                </h2>
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end sm:gap-3">
                  <span className="text-sm text-slate-500">
                    {showLeaveActivitiesInDetail
                      ? leaveDetailItems.length
                      : filteredAndSortedCompletedActivityItems.length}{" "}
                    {t("activities:activityCount")}
                  </span>
                  {!showLeaveActivitiesInDetail && canViewActivityInvoiceStatus && (
                    <div className="flex w-full items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-2 sm:w-auto">
                      <label
                        htmlFor="completed-activity-invoice-filter"
                        className="text-xs font-semibold text-emerald-800"
                      >
                        {t(
                          "projects:workflowInstanceDetail.invoiceTracking.filterLabel",
                        )}
                      </label>
                      <select
                        id="completed-activity-invoice-filter"
                        value={completedActivityInvoiceFilter}
                        onChange={(event) =>
                          setCompletedActivityInvoiceFilter(
                            event.target.value as CompletedActivityInvoiceFilter,
                          )
                        }
                        className="h-8 min-w-0 flex-1 rounded-lg border border-emerald-200 bg-white px-3 text-xs font-medium text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 sm:flex-none"
                      >
                        <option value="all">
                          {t(
                            "projects:workflowInstanceDetail.invoiceTracking.all",
                          )}
                        </option>
                        <option value="invoiced">
                          {t(
                            "projects:workflowInstanceDetail.invoiceTracking.invoiced",
                          )}
                        </option>
                        <option value="not_invoiced">
                          {t(
                            "projects:workflowInstanceDetail.invoiceTracking.notInvoiced",
                          )}
                        </option>
                      </select>
                    </div>
                  )}
                  {!showLeaveActivitiesInDetail && (
                    <button
                      type="button"
                      onClick={() =>
                        setIsCompletedTableConfiguratorOpen((prev) => !prev)
                      }
                      className="inline-flex h-9 flex-1 items-center justify-center px-3 py-2 text-xs font-semibold rounded-lg shadow-sm border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 transition sm:flex-none"
                    >
                      <Cog6ToothIcon className="w-4 h-4 mr-1.5" />
                      {t("dashboard:managerActivities.tableEdit")}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={exportCompletedActivitiesToExcel}
                    disabled={
                      showLeaveActivitiesInDetail
                        ? leaveDetailItems.length === 0
                        : filteredAndSortedCompletedActivityItems.length === 0
                    }
                    className={`inline-flex h-9 flex-1 items-center justify-center px-3 py-2 text-xs font-semibold rounded-lg shadow-sm border border-transparent text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition sm:flex-none ${
                      (showLeaveActivitiesInDetail
                        ? leaveDetailItems.length === 0
                        : filteredAndSortedCompletedActivityItems.length === 0)
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:shadow-md"
                    }`}
                  >
                    <DocumentTextIcon className="w-4 h-4 mr-1.5" />
                    {t("dashboard:managerActivities.excelDownload")}
                  </button>
                </div>
              </div>

              {!showLeaveActivitiesInDetail &&
                canViewActivityInvoiceStatus &&
                completedActivityItems.length > 0 && (
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/80 px-3 py-2 text-xs">
                    <span className="font-semibold text-emerald-700">
                      {t(
                        "projects:workflowInstanceDetail.invoiceTracking.summaryInvoiced",
                      )}
                    </span>
                    <span className="text-slate-500">•</span>
                    <span className="font-medium text-slate-700">
                      {completedActivityInvoiceSummary.invoiced.totalHours.toFixed(
                        2,
                      )}{" "}
                      {t(
                        "projects:workflowInstanceDetail.invoiceTracking.hoursLabel",
                      ).toLocaleLowerCase("tr-TR")}
                    </span>
                    <span className="text-slate-500">•</span>
                    <span className="font-medium text-slate-700">
                      {formatCurrencyByCode(
                        completedActivityInvoiceSummary.invoiced.totalCost,
                        projectFinanceSummary.projectCurrency,
                        { display: "suffix" },
                      )}
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-xl border border-amber-100 bg-amber-50/80 px-3 py-2 text-xs">
                    <span className="font-semibold text-amber-700">
                      {t(
                        "projects:workflowInstanceDetail.invoiceTracking.summaryNotInvoiced",
                      )}
                    </span>
                    <span className="text-slate-500">•</span>
                    <span className="font-medium text-slate-700">
                      {completedActivityInvoiceSummary.notInvoiced.totalHours.toFixed(
                        2,
                      )}{" "}
                      {t(
                        "projects:workflowInstanceDetail.invoiceTracking.hoursLabel",
                      ).toLocaleLowerCase("tr-TR")}
                    </span>
                    <span className="text-slate-500">•</span>
                    <span className="font-medium text-slate-700">
                      {formatCurrencyByCode(
                        completedActivityInvoiceSummary.notInvoiced.totalCost,
                        projectFinanceSummary.projectCurrency,
                        { display: "suffix" },
                      )}
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                    <span className="font-semibold text-slate-600">
                      {t(
                        "projects:workflowInstanceDetail.invoiceTracking.summaryTotal",
                      )}
                    </span>
                    <span className="text-slate-500">•</span>
                    <span className="font-medium text-slate-700">
                      {completedActivityInvoiceSummary.total.totalHours.toFixed(
                        2,
                      )}{" "}
                      {t(
                        "projects:workflowInstanceDetail.invoiceTracking.hoursLabel",
                      ).toLocaleLowerCase("tr-TR")}
                    </span>
                    <span className="text-slate-500">•</span>
                    <span className="font-medium text-slate-700">
                      {formatCurrencyByCode(
                        completedActivityInvoiceSummary.total.totalCost,
                        projectFinanceSummary.projectCurrency,
                        { display: "suffix" },
                      )}
                    </span>
                  </div>
                </div>
              )}

              {(showLeaveActivitiesInDetail
                ? leaveDetailItems.length === 0
                : completedActivityItems.length === 0) ? (
                <div className="text-center py-12">
                  <div className="text-slate-400 text-5xl mb-4">[]</div>
                  <p className="text-slate-500">
                    {showLeaveActivitiesInDetail
                      ? t("dashboard:noActivitiesFound")
                      : t("activities:noCompletedActivities")}
                  </p>
                </div>
              ) : showLeaveActivitiesInDetail ? (
                <div className="overflow-auto max-h-[70vh] rounded-xl border border-red-100">
                  <table className="w-full min-w-[1200px] divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-red-600 to-rose-600 sticky top-0 z-30 border-b border-red-700">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider sticky top-0 left-0 z-40 bg-gradient-to-r from-red-600 to-rose-600 border-r border-red-500">
                          {t("dashboard:managerLeaves.rowNo")}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                          {t("dashboard:managerLeaves.leaveStartDate")}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                          {t("dashboard:managerLeaves.leaveEndDate")}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                          {t("dashboard:managerLeaves.leaveAssignee")}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                          {t("dashboard:managerLeaves.leaveStartTime")}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                          {t("dashboard:managerLeaves.leaveEndTime")}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-bold text-white uppercase tracking-wider">
                          {t("dashboard:managerLeaves.durationHours")}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                          {LEAVE_TYPE_FIELD_LABEL}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                          {t("dashboard:managerLeaves.leaveDescription")}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                          {t("common:status")}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-white uppercase tracking-wider">
                          {t("dashboard:managerLeaves.action")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leaveDetailItems.map((activity, idx) => {
                        const { start, end } = getActivityStartEnd(activity);
                        const duration =
                          getUserTimeInfo(activity.userDurations)?.duration ?? 0;
                        const assignedNames = getAssignedUserNames(activity);
                        const description = getSubmitDescriptionPreview(activity);
                        const leaveType = getActivityLeaveType(activity);
                        const isPendingApproval =
                          activity.status === "PENDING_APPROVAL";

                        return (
                          <tr
                            key={activity.id}
                            className={`group transition-all duration-200 border-b border-gray-100 ${
                              isPendingApproval
                                ? "bg-amber-50/70 hover:bg-amber-100/60"
                                : "hover:bg-gradient-to-r hover:from-red-50/70 hover:to-rose-50/40"
                            }`}
                          >
                            <td className="px-4 py-1 whitespace-nowrap text-xs font-semibold text-gray-900 sticky left-0 bg-white border-r border-gray-200 group-hover:bg-red-50/60">
                              {idx + 1}
                            </td>
                            <td className="px-4 py-1 whitespace-nowrap text-xs text-gray-700 font-medium">
                              {formatDateOnlyTr(start)}
                            </td>
                            <td className="px-4 py-1 whitespace-nowrap text-xs text-gray-700 font-medium">
                              {formatDateOnlyTr(end)}
                            </td>
                            <td className="px-4 py-1 text-xs font-semibold text-gray-900 max-w-[220px]">
                              <OverflowInfoCell
                                value={assignedNames}
                                fullValue={assignedNames === "-" ? "" : assignedNames}
                                title={t("dashboard:managerLeaves.employeeName")}
                                threshold={24}
                              />
                            </td>
                            <td className="px-4 py-1 whitespace-nowrap text-xs text-gray-700">
                              {formatTimeOnlyTr(start)}
                            </td>
                            <td className="px-4 py-1 whitespace-nowrap text-xs text-gray-700">
                              {formatTimeOnlyTr(end)}
                            </td>
                            <td className="px-4 py-1 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <span className="font-semibold text-gray-900 text-xs">
                                  {duration > 0 ? duration.toFixed(2) : "-"}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {t("dashboard:hours")}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-1 text-xs text-gray-800 font-medium max-w-[420px]">
                              <OverflowInfoCell
                                value={leaveType.summary}
                                fullValue={leaveType.tooltip || leaveType.summary}
                                title={LEAVE_TYPE_FIELD_LABEL}
                                threshold={36}
                              />
                            </td>
                            <td className="px-4 py-1 text-xs text-gray-700 max-w-[320px]">
                              <OverflowInfoCell
                                value={description || "-"}
                                fullValue={description || ""}
                                title={t("dashboard:managerLeaves.leaveDescription")}
                                threshold={30}
                              />
                            </td>
                            <td className="px-4 py-1 whitespace-nowrap text-xs">
                              <span
                                className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border bg-gradient-to-r ${getStatusColor(
                                  activity.status || "",
                                )}`}
                              >
                                {getStatusDisplayName(activity.status || "")}
                              </span>
                            </td>
                            <td className="px-4 py-1 whitespace-nowrap text-xs font-medium">
                              <div className="flex items-center gap-2">
                                {isPendingApproval && (isAdmin() || isManager()) && (
                                  <>
                                    <button
                                      onClick={() =>
                                        handleApproveDetailActivity(activity.id)
                                      }
                                      className="flex items-center px-3 py-1 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                    >
                                      <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                                      {t("common:approve")}
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleRejectDetailActivity(activity.id)
                                      }
                                      className="flex items-center px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                    >
                                      <XMarkIcon className="h-4 w-4 mr-1.5" />
                                      {t("common:reject")}
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => handleViewDetails(activity)}
                                  className="flex items-center px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                >
                                  <EyeIcon className="h-4 w-4 mr-1.5" />
                                  {t("common:view")}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="sticky bottom-0 z-20 bg-red-50/95 backdrop-blur border-t-2 border-red-200">
                      <tr>
                        <td colSpan={6} className="px-4 py-2 text-xs font-bold text-red-900">
                          {t("dashboard:managerLeaves.recordCount", {
                            count: leaveDetailItems.length,
                          })}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <span className="font-bold text-red-900 text-xs">
                              {leaveDetailTotalDurationHours.toFixed(2)}
                            </span>
                            <span className="text-xs text-red-700">
                              {t("dashboard:hours")}
                            </span>
                          </div>
                        </td>
                        <td colSpan={4} className="px-4 py-2 text-xs text-red-700 font-semibold" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="relative min-h-[340px] overflow-visible">
                  <ActivityTableConfigurator
                    title={t("dashboard:managerActivities.tableConfiguratorTitle")}
                    description={t(
                      "dashboard:managerActivities.tableConfiguratorDescription",
                    )}
                    isOpen={isCompletedTableConfiguratorOpen}
                    selectedColumns={completedActivityVisibleColumns}
                    defaultColumns={completedActivityDefaultColumns}
                    formColumns={completedActivityAvailableFormColumns}
                    saving={isSavingCompletedTableLayout}
                    onClose={() => setIsCompletedTableConfiguratorOpen(false)}
                    onSave={persistCompletedActivityTableLayout}
                  />
                  <div
                    ref={completedActivitiesScrollContainerRef}
                    onScroll={(event) =>
                      setCompletedActivitiesScrollTop(
                        event.currentTarget.scrollTop,
                      )
                    }
                    className="overflow-auto max-h-[70vh]"
                  >
                    <div className="space-y-2 p-3 md:hidden">
                      {filteredAndSortedCompletedActivityItems.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                          {t("common:noResults")}
                        </div>
                      ) : (
                        <>
                          {completedActivitiesVirtualizationEnabled &&
                            completedActivitiesTopSpacerHeight > 0 && (
                              <div
                                style={{
                                  height: `${completedActivitiesTopSpacerHeight}px`,
                                }}
                              />
                            )}
                          {visibleCompletedActivityItems.map(
                            (activity, idx) => {
                              const rowNo =
                                (completedActivitiesVirtualizationEnabled
                                  ? completedActivitiesVirtualStartIndex
                                  : 0) +
                                idx +
                                1;
                              const assignedNames =
                                getAssignedUserNames(activity);
                              const { start, end } =
                                getActivityStartEnd(activity);
                              const duration =
                                getUserTimeInfo(activity.userDurations)
                                  ?.duration ?? 0;
                              const content = getFormContent(activity);
                              const description =
                                getSubmitDescriptionPreview(activity);
                              const projectName =
                                activity.projectName ||
                                workflowInstance?.workflowContainerName ||
                                workflowInstance?.name ||
                                "-";
                              const latestCommentRow =
                                latestManagerCommentsByActivityId[
                                  String(activity.id)
                                ];
                              const latestComment = (
                                latestCommentRow?.comment ?? ""
                              )
                                .toString()
                                .trim();
                              const isInvoicedActivity =
                                canViewActivityInvoiceStatus &&
                                Boolean(activity.isInvoiced);
                              const isPendingApprovalActivity =
                                activity.status === "PENDING_APPROVAL";

                              return (
                                <div
                                  key={`mobile-completed-${activity.id}`}
                                  className={`rounded-lg border px-3 py-2 shadow-sm ${
                                    isPendingApprovalActivity
                                      ? "border-amber-200 bg-amber-50/70"
                                      : isInvoicedActivity
                                        ? "border-emerald-200 bg-emerald-50/70"
                                        : "border-blue-100 bg-white"
                                  }`}
                                >
                                  <div className="flex items-start gap-2">
                                    <span className="mt-0.5 flex h-6 min-w-6 items-center justify-center rounded-md bg-blue-50 text-[11px] font-bold text-blue-700">
                                      #{rowNo}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleViewDetails(activity)}
                                      className="min-w-0 flex-1 text-left"
                                    >
                                      <div className="flex min-w-0 items-center gap-2">
                                        <p className="truncate text-[13px] font-bold text-gray-900">
                                          {activity.activityTitle ||
                                            activity.name ||
                                            "-"}
                                        </p>
                                        {(isPendingApprovalActivity ||
                                          isInvoicedActivity) && (
                                          <span
                                            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                              isPendingApprovalActivity
                                                ? "bg-amber-100 text-amber-700"
                                                : "bg-emerald-100 text-emerald-700"
                                            }`}
                                          >
                                            {isPendingApprovalActivity
                                              ? getStatusDisplayName(
                                                  activity.status || "",
                                                )
                                              : t(
                                                  "projects:workflowInstanceDetail.invoiceTracking.invoiced",
                                                )}
                                          </span>
                                        )}
                                      </div>
                                      <p className="mt-0.5 truncate text-[11px] font-medium text-gray-500">
                                        {projectName} - {assignedNames}
                                      </p>
                                      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] font-semibold text-slate-600">
                                        <span>{formatDateOnlyTr(end)}</span>
                                        <span>
                                          {formatTimeOnlyTr(start)}-
                                          {formatTimeOnlyTr(end)}
                                        </span>
                                        <span>
                                          {duration > 0
                                            ? duration.toFixed(2)
                                            : "-"}{" "}
                                          {t("dashboard:hours")}
                                        </span>
                                      </div>
                                      {(content.summary !== "-" ||
                                        description !== "-" ||
                                        latestComment) && (
                                        <p className="mt-1 truncate text-[11px] text-gray-500">
                                          {content.summary !== "-"
                                            ? content.summary
                                            : description !== "-"
                                              ? description
                                              : latestComment}
                                        </p>
                                      )}
                                    </button>
                                    <div className="flex shrink-0 items-center gap-1">
                                      {canViewActivityInvoiceStatus && (
                                        <input
                                          type="checkbox"
                                          checked={Boolean(activity.isInvoiced)}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            void handleToggleCompletedActivityInvoiceStatus(
                                              activity,
                                            );
                                          }}
                                          disabled={
                                            isArchivedProject ||
                                            invoiceStatusUpdatingId ===
                                              activity.id
                                          }
                                          aria-label={t(
                                            "projects:workflowInstanceDetail.invoiceTracking.columnLabel",
                                          )}
                                          className={`h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 ${
                                            isArchivedProject ||
                                            invoiceStatusUpdatingId ===
                                              activity.id
                                              ? "cursor-not-allowed opacity-60"
                                              : "cursor-pointer"
                                          }`}
                                        />
                                      )}
                                      {includeManagerCommentColumn && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openManagerCommentModal(activity);
                                          }}
                                          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-700"
                                          title={t(
                                            "dashboard:managerActivities.comment",
                                          )}
                                        >
                                          <DocumentTextIcon className="h-4 w-4" />
                                        </button>
                                      )}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewDetails(activity);
                                        }}
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700"
                                        title={t("common:view")}
                                      >
                                        <EyeIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                  {isPendingApprovalActivity &&
                                    (isAdmin() || isManager()) && (
                                      <div className="mt-2 grid grid-cols-2 gap-2 border-t border-amber-100 pt-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleApproveDetailActivity(
                                              activity.id,
                                            );
                                          }}
                                          className="inline-flex h-8 items-center justify-center rounded-md bg-green-600 text-xs font-semibold text-white"
                                        >
                                          <CheckCircleIcon className="mr-1 h-4 w-4" />
                                          {t("common:approve")}
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRejectDetailActivity(
                                              activity.id,
                                            );
                                          }}
                                          className="inline-flex h-8 items-center justify-center rounded-md bg-red-600 text-xs font-semibold text-white"
                                        >
                                          <XCircleIcon className="mr-1 h-4 w-4" />
                                          {t("common:reject")}
                                        </button>
                                      </div>
                                    )}
                                </div>
                              );
                            },
                          )}
                          {completedActivitiesVirtualizationEnabled &&
                            completedActivitiesBottomSpacerHeight > 0 && (
                              <div
                                style={{
                                  height: `${completedActivitiesBottomSpacerHeight}px`,
                                }}
                              />
                            )}
                          <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-800">
                            <div className="flex items-center justify-between gap-2">
                              <div className="inline-flex items-center rounded-md border border-indigo-200 bg-white p-0.5">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCompletedDurationSummaryMode("total")
                                  }
                                  className={`px-2 py-1 text-[11px] font-semibold rounded-md transition ${
                                    completedDurationSummaryMode === "total"
                                      ? "bg-indigo-600 text-white shadow-sm"
                                      : "text-indigo-700"
                                  }`}
                                >
                                  {t(
                                    "dashboard:managerActivities.summaryTotal",
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCompletedDurationSummaryMode("average")
                                  }
                                  className={`px-2 py-1 text-[11px] font-semibold rounded-md transition ${
                                    completedDurationSummaryMode === "average"
                                      ? "bg-indigo-600 text-white shadow-sm"
                                      : "text-indigo-700"
                                  }`}
                                >
                                  {t(
                                    "dashboard:managerActivities.summaryAverage",
                                  )}
                                </button>
                              </div>
                              <span>
                                {completedDurationSummary.displayHours.toFixed(
                                  2,
                                )}{" "}
                                {t("dashboard:hours")}
                              </span>
                            </div>
                            <p className="mt-1 text-[11px] text-indigo-700">
                              {t(
                                "dashboard:managerActivities.recordCount",
                                {
                                  count:
                                    completedDurationSummary.recordCount,
                                },
                              )}
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    <table className="hidden w-full min-w-[1300px] divide-y divide-gray-200 md:table">
                      <thead className="bg-gradient-to-r from-blue-100 to-indigo-100 sticky top-0 z-30 border-b border-gray-200">
                        <tr>
                          {completedActivityVisibleColumns.map((column) => {
                            const sortField =
                              column.kind === "form"
                                ? column.id
                                : completedActivityColumnSortFieldById[
                                    column.id
                                  ];
                            const isSticky =
                              column.kind === "default" &&
                              column.id === "rowNo";
                            const isNumeric =
                              column.kind === "default" &&
                              (column.id === "duration" ||
                                column.id === "cost");
                            const headerClasses = isSticky
                              ? "w-[88px] min-w-[88px] max-w-[88px] px-3 py-2 text-left text-xs font-bold text-blue-800 uppercase tracking-wider sticky top-0 left-0 z-40 bg-gradient-to-r from-blue-100 to-indigo-100 border-r border-blue-200"
                              : `px-4 py-2 ${isNumeric ? "text-right" : "text-left"} text-xs font-bold text-blue-800 uppercase tracking-wider${
                                  sortField
                                    ? " cursor-pointer hover:bg-blue-200 transition-colors"
                                    : ""
                                }`;

                            return (
                              <th
                                key={`completed-header-${column.kind}-${column.id}`}
                                onClick={
                                  sortField
                                    ? () =>
                                        handleCompletedActivitySort(sortField)
                                    : undefined
                                }
                                className={headerClasses}
                              >
                                <div
                                  className={`flex w-full items-center gap-1 ${
                                    isNumeric ? "justify-end" : ""
                                  }`}
                                >
                                  {column.label}
                                  {sortField && (
                                    <CompletedActivitySortIcon
                                      field={sortField}
                                    />
                                  )}
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                        <tr className="bg-blue-50/80 border-t border-blue-200">
                          {completedActivityVisibleColumns.map(
                            renderCompletedActivityFilterCell,
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredAndSortedCompletedActivityItems.length ===
                          0 && (
                          <tr>
                            <td
                              colSpan={completedActivityVisibleColumns.length}
                              className="px-4 py-8 text-center text-sm text-gray-500"
                            >
                              {t("common:noResults")}
                            </td>
                          </tr>
                        )}
                        {completedActivitiesVirtualizationEnabled &&
                          completedActivitiesTopSpacerHeight > 0 && (
                            <tr>
                              <td
                                colSpan={completedActivityVisibleColumns.length}
                                style={{
                                  height: `${completedActivitiesTopSpacerHeight}px`,
                                }}
                                className="p-0 border-0"
                              />
                            </tr>
                          )}
                        {visibleCompletedActivityItems.map(
                          (activity, idx) => {
                            const rowNo =
                              (completedActivitiesVirtualizationEnabled
                                ? completedActivitiesVirtualStartIndex
                                : 0) +
                              idx +
                              1;
                            const assignedNames =
                              getAssignedUserNames(activity);
                            const { start, end } =
                              getActivityStartEnd(activity);
                            const duration =
                              getUserTimeInfo(activity.userDurations)
                                ?.duration ?? 0;
                            const activityCost =
                              getCompletedActivityCostValue(activity);
                            const content = getFormContent(activity);
                            const description =
                              getSubmitDescriptionPreview(activity);
                            const projectName =
                              activity.projectName ||
                              workflowInstance?.workflowContainerName ||
                              workflowInstance?.name ||
                              "-";
                            const latestCommentRow =
                              latestManagerCommentsByActivityId[
                                String(activity.id)
                              ];
                            const latestComment = (
                              latestCommentRow?.comment ?? ""
                            )
                              .toString()
                              .trim();
                            const latestCommentPreview = latestComment
                              ? latestComment.length > 80
                                ? `${latestComment.slice(0, 77)}...`
                                : latestComment
                              : "-";
                            const latestCommentTitle = latestComment
                              ? [
                                  latestCommentRow?.authorName
                                    ? `${t("projects:workflowInstanceDetail.authorLabel")}: ${latestCommentRow.authorName}`
                                    : null,
                                  latestCommentRow?.createdAt
                                    ? `${t("projects:workflowInstanceDetail.dateLabel")}: ${new Date(
                                        latestCommentRow.createdAt,
                                      ).toLocaleString("tr-TR")}`
                                    : null,
                                  `${t("projects:workflowInstanceDetail.commentLabel")}: ${latestComment}`,
                                ]
                                  .filter(Boolean)
                                  .join("\n")
                              : "";
                            const isInvoicedActivity =
                              canViewActivityInvoiceStatus &&
                              Boolean(activity.isInvoiced);
                            const isPendingApprovalActivity =
                              activity.status === "PENDING_APPROVAL";
                            const rowClassName = isPendingApprovalActivity
                              ? "group bg-amber-50/70 hover:bg-amber-100/70 transition-all duration-200 border-b border-amber-100"
                              : isInvoicedActivity
                                ? "group bg-emerald-50/70 hover:bg-emerald-100/70 transition-all duration-200 border-b border-emerald-100"
                                : "group hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-blue-50/30 transition-all duration-200 border-b border-gray-100";
                            const stickyCellClassName = isPendingApprovalActivity
                              ? "w-[88px] min-w-[88px] max-w-[88px] px-3 py-1 whitespace-nowrap text-xs font-semibold text-gray-900 sticky left-0 bg-amber-50/70 border-r border-amber-100 group-hover:bg-amber-100/70"
                              : isInvoicedActivity
                                ? "w-[88px] min-w-[88px] max-w-[88px] px-3 py-1 whitespace-nowrap text-xs font-semibold text-gray-900 sticky left-0 bg-emerald-50/70 border-r border-emerald-100 group-hover:bg-emerald-100/70"
                                : "w-[88px] min-w-[88px] max-w-[88px] px-3 py-1 whitespace-nowrap text-xs font-semibold text-gray-900 sticky left-0 bg-white border-r border-gray-200 group-hover:bg-purple-50/50";

                            return (
                              <tr
                                key={activity.id}
                                className={rowClassName}
                              >
                                {completedActivityVisibleColumns.map(
                                  (column) => {
                                    if (column.kind === "form") {
                                      const value =
                                        getCompletedActivityFormColumnValue(
                                          content.tooltip,
                                          column,
                                          activity.name ||
                                            activity.activityTitle ||
                                            "",
                                        ) || "-";
                                      return (
                                        <td
                                          key={`${activity.id}-${column.id}`}
                                          className="px-4 py-1 text-xs text-gray-800 max-w-[260px]"
                                        >
                                          <OverflowInfoCell
                                            value={value}
                                            threshold={28}
                                            overflowOnly
                                            cellClassName="max-w-[260px]"
                                            textClassName="text-xs text-gray-800"
                                          />
                                        </td>
                                      );
                                    }

                                    switch (column.id) {
                                      case "rowNo":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className={stickyCellClassName}
                                          >
                                            {rowNo}
                                          </td>
                                        );
                                      case "endDate":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-xs text-gray-700 font-medium"
                                          >
                                            {formatDateOnlyTr(end)}
                                          </td>
                                        );
                                      case "assignee":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-xs font-semibold text-gray-900"
                                          >
                                            <OverflowInfoCell
                                              value={assignedNames}
                                              threshold={24}
                                              overflowOnly
                                              cellClassName="max-w-[220px]"
                                              textClassName="text-xs font-semibold text-gray-900"
                                            />
                                          </td>
                                        );
                                      case "startTime":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-xs text-gray-700"
                                          >
                                            {formatTimeOnlyTr(start)}
                                          </td>
                                        );
                                      case "endTimeOnly":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-xs text-gray-700"
                                          >
                                            {formatTimeOnlyTr(end)}
                                          </td>
                                        );
                                      case "duration":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-right"
                                          >
                                            <div className="flex items-center justify-end space-x-2">
                                              <span className="font-semibold text-gray-900 text-xs">
                                                {duration > 0
                                                  ? `${duration.toFixed(2)}`
                                                  : "-"}
                                              </span>
                                              <span className="text-xs text-gray-500">
                                                {t("dashboard:hours")}
                                              </span>
                                            </div>
                                          </td>
                                        );
                                      case "cost":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-right text-xs font-semibold text-emerald-700"
                                          >
                                            {activityCost === null
                                              ? "-"
                                              : formatCurrencyByCode(
                                                  activityCost,
                                                  projectFinanceInputs.projectCurrency,
                                                  { display: "suffix" },
                                                )}
                                          </td>
                                        );
                                      case "projectName":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-xs text-gray-900 font-medium max-w-[240px]"
                                          >
                                            <OverflowInfoCell
                                              value={projectName}
                                              threshold={24}
                                              overflowOnly
                                              cellClassName="max-w-[240px]"
                                              textClassName="text-xs font-medium text-gray-900"
                                            />
                                          </td>
                                        );
                                      case "activityTitle":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-xs text-gray-900 font-medium max-w-[260px]"
                                          >
                                            <OverflowInfoCell
                                              value={
                                                activity.activityTitle ||
                                                activity.name ||
                                                "-"
                                              }
                                              threshold={28}
                                              overflowOnly
                                              cellClassName="max-w-[260px]"
                                              textClassName="text-xs font-medium text-gray-900"
                                            />
                                          </td>
                                        );
                                      case "formContent":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 text-xs text-gray-800 font-medium max-w-[520px]"
                                          >
                                            <OverflowInfoCell
                                              value={content.summary}
                                              fullValue={
                                                content.tooltip ||
                                                content.summary
                                              }
                                              threshold={42}
                                              overflowOnly
                                              cellClassName="max-w-[520px]"
                                              textClassName="text-xs font-medium text-gray-800"
                                            />
                                          </td>
                                        );
                                      case "submitDescription":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 text-xs text-gray-700 max-w-xs"
                                          >
                                            <OverflowInfoCell
                                              value={description || "-"}
                                              threshold={28}
                                              overflowOnly
                                              cellClassName="max-w-xs"
                                              textClassName="text-xs text-gray-700"
                                            />
                                          </td>
                                        );
                                      case "managerComment":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 text-xs text-gray-700 font-medium max-w-[520px]"
                                          >
                                            <OverflowInfoCell
                                              value={latestCommentPreview}
                                              fullValue={latestCommentTitle}
                                              threshold={32}
                                              overflowOnly
                                              cellClassName="max-w-[520px]"
                                              textClassName="text-xs font-medium text-gray-700"
                                            />
                                          </td>
                                        );
                                      case "invoiceStatus":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-center text-xs"
                                          >
                                            <div className="flex items-center justify-center">
                                              <input
                                                type="checkbox"
                                                checked={Boolean(
                                                  activity.isInvoiced,
                                                )}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  void handleToggleCompletedActivityInvoiceStatus(
                                                    activity,
                                                  );
                                                }}
                                                disabled={
                                                  isArchivedProject ||
                                                  invoiceStatusUpdatingId ===
                                                    activity.id
                                                }
                                                aria-label={t(
                                                  "projects:workflowInstanceDetail.invoiceTracking.columnLabel",
                                                )}
                                                className={`h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 ${
                                                  isArchivedProject ||
                                                  invoiceStatusUpdatingId ===
                                                    activity.id
                                                    ? "cursor-not-allowed opacity-60"
                                                    : "cursor-pointer"
                                                }`}
                                              />
                                            </div>
                                          </td>
                                        );
                                      case "details":
                                        return (
                                          <td
                                            key={`${activity.id}-${column.id}`}
                                            className="px-4 py-1 whitespace-nowrap text-xs font-medium"
                                          >
                                            <div className="flex items-center gap-2">
                                              {isPendingApprovalActivity && (
                                                <span
                                                  className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border bg-gradient-to-r ${getStatusColor(
                                                    activity.status || "",
                                                  )}`}
                                                >
                                                  {getStatusDisplayName(
                                                    activity.status || "",
                                                  )}
                                                </span>
                                              )}
                                              {isPendingApprovalActivity &&
                                                (isAdmin() || isManager()) && (
                                                  <>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleApproveDetailActivity(
                                                          activity.id,
                                                        );
                                                      }}
                                                      className="flex items-center px-3 py-1 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                                    >
                                                      <CheckCircleIcon className="h-4 w-4 mr-1.5" />
                                                      {t("common:approve")}
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRejectDetailActivity(
                                                          activity.id,
                                                        );
                                                      }}
                                                      className="flex items-center px-3 py-1 bg-red-600 text-white hover:bg-red-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                                    >
                                                      <XCircleIcon className="h-4 w-4 mr-1.5" />
                                                      {t("common:reject")}
                                                    </button>
                                                  </>
                                                )}
                                              {includeManagerCommentColumn && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openManagerCommentModal(
                                                      activity,
                                                    );
                                                  }}
                                                  className="flex items-center px-3 py-1 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                                >
                                                  <DocumentTextIcon className="h-4 w-4 mr-1.5" />
                                                  {t("dashboard:managerActivities.comment")}
                                                </button>
                                              )}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleViewDetails(activity);
                                                }}
                                                className="flex items-center px-3 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-all duration-200 font-semibold shadow-sm"
                                              >
                                                <EyeIcon className="h-4 w-4 mr-1.5" />
                                                {t("common:view")}
                                              </button>
                                            </div>
                                          </td>
                                        );
                                      default:
                                        return null;
                                    }
                                  },
                                )}
                              </tr>
                            );
                          },
                        )}
                        {completedActivitiesVirtualizationEnabled &&
                          completedActivitiesBottomSpacerHeight > 0 && (
                            <tr>
                              <td
                                colSpan={completedActivityVisibleColumns.length}
                                style={{
                                  height: `${completedActivitiesBottomSpacerHeight}px`,
                                }}
                                className="p-0 border-0"
                              />
                            </tr>
                          )}
                      </tbody>
                      <tfoot className="sticky bottom-0 z-20 bg-indigo-50/95 backdrop-blur border-t-2 border-indigo-200">
                        <tr>
                          {completedActivityVisibleColumns.map(
                            (column, index) => {
                              if (column.kind === "form") {
                                return (
                                  <td
                                    key={`completed-summary-${column.id}-${index}`}
                                    className="px-4 py-2 text-xs text-indigo-700"
                                  >
                                    {index ===
                                    completedActivityVisibleColumns.length - 1
                                      ? t(
                                          "dashboard:managerActivities.recordCount",
                                          {
                                            count:
                                              completedDurationSummary.recordCount,
                                          },
                                        )
                                      : ""}
                                  </td>
                                );
                              }

                              if (column.id === "rowNo") {
                                return (
                                  <td
                                    key={`completed-summary-${column.id}`}
                                    className="sticky left-0 z-30 px-4 py-2 text-xs font-semibold text-indigo-800 bg-indigo-50/95 border-r border-indigo-200"
                                  >
                                    <div className="inline-flex items-center rounded-md border border-indigo-200 bg-white p-0.5">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setCompletedDurationSummaryMode(
                                            "total",
                                          )
                                        }
                                        className={`px-3 py-1 text-xs font-semibold rounded-md transition ${
                                          completedDurationSummaryMode ===
                                          "total"
                                            ? "bg-indigo-600 text-white shadow-sm"
                                            : "text-indigo-700 hover:bg-indigo-100"
                                        }`}
                                      >
                                        {t(
                                          "dashboard:managerActivities.summaryTotal",
                                        )}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setCompletedDurationSummaryMode(
                                            "average",
                                          )
                                        }
                                        className={`px-3 py-1 text-xs font-semibold rounded-md transition ${
                                          completedDurationSummaryMode ===
                                          "average"
                                            ? "bg-indigo-600 text-white shadow-sm"
                                            : "text-indigo-700 hover:bg-indigo-100"
                                        }`}
                                      >
                                        {t(
                                          "dashboard:managerActivities.summaryAverage",
                                        )}
                                      </button>
                                    </div>
                                  </td>
                                );
                              }

                              if (column.id === "duration") {
                                return (
                                  <td
                                    key={`completed-summary-${column.id}`}
                                    className="px-4 py-2 whitespace-nowrap text-right"
                                  >
                                    <div className="flex items-center justify-end space-x-2">
                                      <span className="font-bold text-indigo-900 text-xs">
                                        {completedDurationSummary.displayHours.toFixed(
                                          2,
                                        )}
                                      </span>
                                      <span className="text-xs text-indigo-700">
                                        {t("dashboard:hours")}
                                      </span>
                                    </div>
                                  </td>
                                );
                              }

                              if (column.id === "cost") {
                                return (
                                  <td
                                    key={`completed-summary-${column.id}`}
                                    className="px-4 py-2 whitespace-nowrap text-right text-xs font-bold text-emerald-700"
                                  >
                                    {formatCurrencyByCode(
                                      completedDurationSummaryMode === "average"
                                        ? filteredCompletedActivitiesAverageCost
                                        : filteredCompletedActivitiesTotalCost,
                                      projectFinanceInputs.projectCurrency,
                                      { display: "suffix" },
                                    )}
                                  </td>
                                );
                              }

                              return (
                                <td
                                  key={`completed-summary-${column.id}`}
                                  className="px-4 py-2 text-xs text-indigo-700 font-semibold"
                                >
                                  {index ===
                                  completedActivityVisibleColumns.length - 1
                                    ? t(
                                        "dashboard:managerActivities.recordCount",
                                        {
                                          count:
                                            completedDurationSummary.recordCount,
                                        },
                                      )
                                    : ""}
                                </td>
                              );
                            },
                          )}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Confirm Complete Dialog */}
      {showCompleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <ArchiveBoxIcon className="w-6 h-6 text-gray-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {t("workflows:archiveProject")}
                  </h2>
                </div>
                <button
                  onClick={() => setShowCompleteDialog(false)}
                  disabled={isCompleting}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 text-base">
                {t("workflows:archiveProjectConfirm")}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {t("workflows:archiveProjectMessage")}
              </p>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowCompleteDialog(false)}
                  disabled={isCompleting}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
                >
                  {t("common:cancel")}
                </button>
                <button
                  onClick={handleCompleteWorkflow}
                  disabled={isCompleting}
                  className="px-4 py-2 bg-gradient-to-r from-gray-600 to-slate-600 hover:from-gray-700 hover:to-slate-700 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isCompleting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {t("workflows:archiving")}
                    </>
                  ) : (
                    <>
                      <ArchiveBoxIcon className="w-5 h-5" />
                      {t("workflows:archive")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Project Type Modal */}
      {showChangeTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <ArrowPathIcon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {t("workflows:changeProjectType")}
                  </h2>
                </div>
                <button
                  onClick={() => setShowChangeTypeModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {isLoadingWorkflows ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
                </div>
              ) : availableWorkflows.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {t("workflows:noWorkflows")}
                </p>
              ) : (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    {workflowText}
                  </label>
                  <select
                    value={selectedWorkflowId ?? ""}
                    onChange={(e) =>
                      setSelectedWorkflowId(Number(e.target.value))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="" disabled>
                      {t("common:select")}
                    </option>
                    {availableWorkflows.map((workflow) => (
                      <option key={workflow.id} value={workflow.id}>
                        {workflow.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowChangeTypeModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  {t("common:cancel")}
                </button>
                <button
                  onClick={handleStartTemplateChange}
                  disabled={!selectedWorkflowId || isLoadingWorkflows}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t("common:next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Project Type Form */}
      {showTemplateChangeModal && workflowInstance && selectedWorkflowId && (
        <WorkflowStartModal
          isOpen={showTemplateChangeModal}
          onClose={handleCloseTemplateChange}
          projectId={workflowInstance.workflowContainerId}
          workflowId={selectedWorkflowId}
          workflowInstanceId={workflowInstance.id}
          isTemplateChange={true}
          onSuccess={handleTemplateChangeSuccess}
        />
      )}

      {/* Edit Details Modal */}
      {workflowInstance && (
        <WorkflowStartModal
          isOpen={showEditDetailsModal}
          onClose={() => setShowEditDetailsModal(false)}
          projectId={workflowInstance.workflowContainerId}
          workflowId={workflowInstance.workflowId || 0}
          workflowInstanceId={workflowInstance.id}
          isEditMode={true}
          initialData={workflowInstance.workflowContainerFormData}
          onSuccess={async () => {
            try {
              if (!instanceId) {
                return;
              }

              const instance = await reloadWorkflowInstanceSummary(instanceId);
              if (!instance) {
                return;
              }

              let instanceData:
                | {
                    formData: Record<string, any>;
                    projectMembers: number[];
                    workflowContainerId: number;
                  }
                | undefined;
              try {
                instanceData = await apiService.getWorkflowInstanceFormData(
                  instance.id,
                );
              } catch (error) {
              }

              setWorkflowInstance({
                ...instance,
                workflowContainerFormData:
                  instanceData?.formData ?? instance.workflowContainerFormData,
              });

              if (instanceData) {
                if (isVisitorUser) {
                  try {
                    const people = await apiService.getWorkflowInstancePeople(
                      instance.id,
                    );
                    const resolvedPeople = Array.isArray(people) ? people : [];
                    setAllUsers(resolvedPeople);
                    setProjectMembers(
                      mapMembersFromIds(
                        instanceData.projectMembers || [],
                        resolvedPeople,
                        instance.workflowContainerId,
                      ),
                    );
                  } catch (error) {
                    setAllUsers([]);
                    setProjectMembers([]);
                  }
                } else {
                  const users = await apiService.getUsers(true);
                  setAllUsers(users);
                  const userMap = new Map<number, User>(
                    users.map((user) => [user.id, user]),
                  );
                  const now = new Date().toISOString();
                  const mappedMembers = (instanceData.projectMembers || [])
                    .map((userId) => {
                      const user = userMap.get(userId);
                      if (!user) {
                        return null;
                      }
                      return {
                        id: userId,
                        workflowContainerId: instance.workflowContainerId,
                        userId,
                        role: "team_member",
                        canStartWorkflow: false,
                        addedAt: now,
                        user: {
                          id: user.id,
                          firstName: user.firstName,
                          lastName: user.lastName,
                          username: user.username,
                          email: user.email,
                          role: user.role,
                        },
                      } as WorkflowContainerMember;
                    })
                    .filter(
                      (member): member is WorkflowContainerMember =>
                        member !== null,
                    );

                  setProjectMembers(mappedMembers);
                }
              }
            } catch (error) {
            }
          }}
        />
      )}

      {/* Task Detail Modal */}
      {selectedActivity && (
        <TaskDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          task={selectedActivity}
        />
      )}

      {/* Manager Comment Modal */}
      {managerCommentActivity && (isManager() || isAdmin()) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 flex items-center justify-between">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-white truncate">
                  {t("dashboard:managerActivities.managerComment")}
                </h2>
                <p className="text-white/80 text-xs mt-1 truncate">
                  {(managerCommentActivity.projectName ||
                    workflowInstance?.workflowContainerName ||
                    "-") +
                    " / " +
                    (managerCommentActivity.activityTitle ||
                      managerCommentActivity.name ||
                      "-")}
                </p>
              </div>
              <button
                onClick={closeManagerCommentModal}
                disabled={savingManagerComment}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors disabled:opacity-60"
                aria-label="Close"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t("dashboard:managerActivities.comment")}
                </label>
                <textarea
                  value={managerCommentText}
                  onChange={(e) => setManagerCommentText(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  placeholder={t("dashboard:managerActivities.commentPlaceholder")}
                  disabled={savingManagerComment}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeManagerCommentModal}
                disabled={savingManagerComment}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold transition-colors disabled:opacity-60"
              >
                {t("common:cancel")}
              </button>
              <button
                type="button"
                onClick={saveManagerComment}
                disabled={savingManagerComment}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60"
              >
                <CheckCircleIcon className="w-5 h-5" />
                {savingManagerComment ? t("common:saving") : t("common:save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Files Modal */}
      {workflowInstance && canViewProjectFiles && (
        <ProjectFilesModal
          isOpen={showFilesModal}
          onClose={() => setShowFilesModal(false)}
          workflowInstanceId={workflowInstance.id}
          templateWorkflowId={workflowInstance.templateWorkflowId ?? null}
          projectName={
            projectDisplayName ||
            workflowInstance.workflowContainerFormData?.name ||
            workflowInstance.workflowContainerName
          }
          projectMemberIds={workflowInstance.projectMembers ?? []}
          projectManagerId={
            workflowInstance.workflowContainerFormData?.projectManager ??
            workflowInstance.workflowContainerFormData?.project_manager ??
            null
          }
        />
      )}

      {workflowInstance && (
        <AssignTaskModal
          isOpen={showAssignTaskModal}
          onClose={() => setShowAssignTaskModal(false)}
          onSuccess={() => setRefreshKey((prev) => prev + 1)}
          projects={[
            {
              id: workflowInstance.id,
              projectName:
                workflowInstance.workflowContainerName || workflowInstance.name,
              workflowName: workflowInstance.workflowName,
              projectCode: workflowInstance.projectCode || null,
              templateWorkflowId: workflowInstance.templateWorkflowId ?? null,
            },
          ]}
          users={allUsers}
          initialWorkflowInstanceId={workflowInstance.id}
        />
      )}

      {financeShortcutMode && (
        <div className="fixed inset-0 z-[120] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {financeShortcutMode === "offer"
                    ? t(
                        "projects:workflowInstanceDetail.financeShortcut.offerTitle",
                        "İş Teklifi Tutarı",
                      )
                    : t(
                        "projects:workflowInstanceDetail.financeShortcut.invoiceTitle",
                        "Fatura",
                      )}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {financeShortcutMode === "offer"
                    ? t(
                        "projects:workflowInstanceDetail.financeShortcut.offerDescription",
                        "İş teklifi tutarını, para birimini ve ilgili dosyaları buradan güncelleyebilirsiniz.",
                      )
                    : t(
                        "projects:workflowInstanceDetail.financeShortcut.invoiceDescription",
                        "Fatura bilgisini ve ilgili dosyaları buradan güncelleyebilirsiniz.",
                      )}
                </p>
              </div>
              <button
                type="button"
                onClick={closeFinanceShortcutModal}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
                disabled={isSavingFinanceShortcut}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">
                      {financeShortcutMode === "offer"
                        ? t(
                            "projects:workflowInstanceDetail.financeShortcut.existingOfferFilesTitle",
                            "Mevcut İş Tutarı/Teklifler",
                          )
                        : t(
                            "projects:workflowInstanceDetail.financeShortcut.existingInvoiceFilesTitle",
                            "Mevcut Faturalar",
                          )}
                    </h4>
                    <p className="mt-1 text-xs text-slate-500">
                      {financeShortcutMode === "offer"
                        ? t(
                            "projects:workflowInstanceDetail.financeShortcut.existingOfferFilesDescription",
                            "Projeye daha once eklenmis teklif dosyalarini burada gorebilirsiniz.",
                          )
                        : t(
                            "projects:workflowInstanceDetail.financeShortcut.existingInvoiceFilesDescription",
                            "Projeye daha once eklenmis fatura dosyalarini burada gorebilirsiniz.",
                          )}
                    </p>
                  </div>
                  {!isExistingFinanceShortcutFilesLoading &&
                    activeExistingFinanceShortcutFiles.length > 0 && (
                      <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600 border border-slate-200">
                        {activeExistingFinanceShortcutFiles.length}
                      </span>
                    )}
                </div>

                {isExistingFinanceShortcutFilesLoading ? (
                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    {t(
                      "projects:workflowInstanceDetail.financeShortcut.existingFilesLoading",
                      "Mevcut dosyalar yukleniyor...",
                    )}
                  </div>
                ) : activeExistingFinanceShortcutFiles.length === 0 ? (
                  <div className="mt-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-4 text-sm text-slate-500">
                    {t(
                      "projects:workflowInstanceDetail.financeShortcut.existingFilesEmpty",
                      "Bu alan icin daha once yuklenmis dosya bulunamadi.",
                    )}
                  </div>
                ) : (
                  <div className="mt-3 max-h-60 space-y-2 overflow-y-auto">
                    {activeExistingFinanceShortcutFiles.map((file) => (
                      <div
                        key={file.id}
                        className="rounded-lg border border-slate-200 bg-white p-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <button
                              type="button"
                              onClick={() => void handlePreviewWorkflowFile(file)}
                              disabled={previewingDriveFileId === file.id}
                              className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
                            >
                              {previewingDriveFileId === file.id ? (
                                <ArrowPathIcon className="h-4 w-4 flex-shrink-0 animate-spin" />
                              ) : (
                                <DocumentTextIcon className="h-4 w-4 flex-shrink-0" />
                              )}
                              <span className="truncate">{file.name}</span>
                            </button>
                            {file.description?.trim() && (
                              <p className="mt-2 text-sm text-slate-600 break-words">
                                {file.description}
                              </p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500">
                              <span>
                                {formatDate(file.uploadedAt || file.addedAt)}
                              </span>
                              <span>{file.uploadedByName || "-"}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => void handlePreviewWorkflowFile(file)}
                            disabled={previewingDriveFileId === file.id}
                            className="inline-flex items-center rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                          >
                            {t(
                              "projects:workflowInstanceDetail.financeShortcut.openFile",
                              "Aç",
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {financeShortcutMode === "offer" ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t(
                        "projects:workflowInstanceDetail.financeShortcut.offerAmount",
                        "İş Teklifi Tutarı",
                      )}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={financeShortcutForm.amount}
                      onChange={(event) =>
                        setFinanceShortcutForm((prev) => ({
                          ...prev,
                          amount: event.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      {t(
                        "projects:workflowInstanceDetail.financeShortcut.currency",
                        "Para Birimi",
                      )}
                    </label>
                    <select
                      value={financeShortcutForm.currency}
                      onChange={(event) =>
                        setFinanceShortcutForm((prev) => ({
                          ...prev,
                          currency: event.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                    >
                      {PROJECT_CURRENCY_OPTIONS.map((option) => (
                        <option key={option.code} value={option.code}>
                          {option.code}
                          {PROJECT_CURRENCY_SYMBOLS[option.code]
                            ? ` (${PROJECT_CURRENCY_SYMBOLS[option.code]})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t(
                      "projects:workflowInstanceDetail.financeShortcut.invoiceLabel",
                      "Fatura Durumu",
                    )}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setFinanceShortcutForm((prev) => ({
                          ...prev,
                          invoiceEnabled: true,
                        }))
                      }
                      className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                        financeShortcutForm.invoiceEnabled
                          ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <div className="text-sm font-semibold">
                        {t(
                          "projects:workflowInstanceDetail.financeShortcut.invoiceEnabled",
                          "Fatura Var",
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {t("projects:invoice")}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        {
                          setFinanceShortcutForm((prev) => ({
                            ...prev,
                            invoiceEnabled: false,
                            files: [],
                          }));
                          setIsFinanceShortcutDragOver(false);
                        }
                      }
                      className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                        !financeShortcutForm.invoiceEnabled
                          ? "border-slate-400 bg-slate-100 text-slate-900"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <div className="text-sm font-semibold">
                        {t(
                          "projects:workflowInstanceDetail.financeShortcut.invoiceDisabled",
                          "Fatura Yok",
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {t(
                          "projects:workflowInstanceDetail.financeShortcut.invoiceDisabledDescription",
                          "Gerekirse daha sonra tekrar açabilirsiniz.",
                        )}
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {(financeShortcutMode === "offer" ||
                financeShortcutForm.invoiceEnabled) && (
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                  {renderQueuedUploadSection({
                    files: financeShortcutForm.files,
                    isDragActive: isFinanceShortcutDragOver,
                    disabled: isSavingFinanceShortcut,
                    label: t(
                      "projects:workflowInstanceDetail.financeShortcut.files",
                      "Dosyalar",
                    ),
                    hint:
                      financeShortcutMode === "offer"
                        ? t(
                            "projects:workflowInstanceDetail.financeShortcut.offerFilesHint",
                            "Teklif, sözleşme veya destekleyici dosyaları ekleyebilirsiniz.",
                          )
                        : t(
                            "projects:workflowInstanceDetail.financeShortcut.invoiceFilesHint",
                            "Fatura dosyalarını hızlıca yükleyebilirsiniz.",
                          ),
                    onFileChange: handleFinanceShortcutFileChange,
                    onDragOver: handleFinanceShortcutDragOver,
                    onDragLeave: handleFinanceShortcutDragLeave,
                    onDrop: handleFinanceShortcutDrop,
                    onRemoveFile: removeFinanceShortcutFile,
                    onUpdateFileDescription:
                      updateFinanceShortcutFileDescription,
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={closeFinanceShortcutModal}
                disabled={isSavingFinanceShortcut}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-100 disabled:opacity-60"
              >
                {t("common:cancel")}
              </button>
              <button
                type="button"
                onClick={() => void handleSaveFinanceShortcut()}
                disabled={isSavingFinanceShortcut}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-950 text-white text-sm font-semibold disabled:opacity-60"
              >
                {isSavingFinanceShortcut && (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                )}
                {t("common:save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCollectionModal && (
        <div className="fixed inset-0 z-[120] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  {t("projects:workflowInstanceDetail.collections.add")}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {t("projects:workflowInstanceDetail.collections.modalDescription")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowCollectionModal(false);
                  setIsCollectionDragOver(false);
                }}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("common:date")}
                  </label>
                  <input
                    type="date"
                    value={collectionForm.collectionDate}
                    onChange={(e) =>
                      setCollectionForm((prev) => ({
                        ...prev,
                        collectionDate: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("projects:workflowInstanceDetail.finance.amount")}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={collectionForm.amount}
                    onChange={(e) =>
                      setCollectionForm((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t("projects:workflowInstanceDetail.collections.currency")}
                  </label>
                  <select
                    value={collectionForm.currency}
                    onChange={(e) =>
                      setCollectionForm((prev) => ({
                        ...prev,
                        currency: e.target.value,
                      }))
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="AZN">AZN</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {t("projects:workflowInstanceDetail.collections.paymentDescription")}
                </label>
                <textarea
                  rows={3}
                  value={collectionForm.description}
                  onChange={(e) =>
                    setCollectionForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </div>

              <div>
                {renderQueuedUploadSection({
                  files: collectionForm.files,
                  isDragActive: isCollectionDragOver,
                  disabled: isSavingCollection,
                  label: t("projects:workflowInstanceDetail.collections.files"),
                  hint: t(
                    "projects:workflowInstanceDetail.collections.filesHint",
                    "Dekont, makbuz veya ilgili tahsilat dosyalarini ekleyebilirsiniz.",
                  ),
                  onFileChange: handleCollectionFileChange,
                  onDragOver: handleCollectionDragOver,
                  onDragLeave: handleCollectionDragLeave,
                  onDrop: handleCollectionDrop,
                  onRemoveFile: removeCollectionFile,
                  onUpdateFileDescription: updateCollectionFileDescription,
                })}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={() => {
                  setShowCollectionModal(false);
                  setIsCollectionDragOver(false);
                }}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-100"
              >
                {t("common:cancel")}
              </button>
              <button
                type="button"
                onClick={() => void handleSaveCollection()}
                disabled={isSavingCollection}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60"
              >
                {isSavingCollection && (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                )}
                {t("common:save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t("common:deleteConfirmTitle", "Delete Confirmation")}
        message={t(
          "common:deleteConfirmMessage",
          "Are you sure you want to delete this item? This action cannot be undone.",
        )}
        confirmText={t("common:delete", "Delete")}
        cancelText={t("common:cancel", "Cancel")}
        type="danger"
      />

      {/* Reactivate Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showReactivateDialog}
        onClose={() => setShowReactivateDialog(false)}
        onConfirm={confirmReactivateProject}
        title={t("workflows:reactivateProject")}
        message={t("workflows:reactivateProjectMessage")}
        confirmText={t("workflows:reactivateProjectConfirm")}
        cancelText={t("common:cancel")}
        type="warning"
      />
    </div>
  );
};

